import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import GroundedSearchBox from '../components/GroundedSearchBox';
import ShareButtons from '../components/ShareButtons';
import CakeSegment from '../components/CakeSegment';
import { Check, X, Plus, Minus, ShoppingCart, Phone, Calendar, Users, CaretDown, CaretUp, CaretLeft, Camera, MusicNotes, MapPin, ForkKnife, Heart, CaretRight, Star, Info, FloppyDisk, Trash, PencilSimple, Baby, House, Briefcase, Gift, UserCircle, HandsPraying, Flower, Image as ImageIcon } from '@phosphor-icons/react';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import Lightbox from '../components/Lightbox';
import BrandLogo from '../components/BrandLogo';
import FloatingSegmentMenu, { SEGMENT_ICONS } from '../components/FloatingSegmentMenu';
import DecoratorPanel from '../components/DecoratorPanel';
import DecorPackagesLightbox from '../components/DecorPackagesLightbox';
import PortfolioLightbox from '../components/PortfolioLightbox';
import CatererMealLeafPopup from '../components/CatererMealLeafPopup';
import { ProviderCardFrame, ProviderShowcaseHeader } from '../components/ProviderShowcaseCard';
import ProviderShowcaseLightbox from '../components/ProviderShowcaseLightbox';
import CateringTeamPicker from '../components/CateringTeamPicker';
import { splitCartByLane } from '../utils/cartLanes';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Calendar as CalendarPicker } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { isMuhurthamDate, getMuhurthamDateObjects } from '../data/muhurthamDates';
import { TIME_SLOTS, SLOT_MORNING, isWeddingEvent, getSlotLabel } from '../data/timeSlots';
import { slugForProName, isDualPricingPro } from '../data/proCategories';
import CityLocationInput from '../components/CityLocationInput';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Data + constants moved to ./instantPricingData
import {
  MARRIAGE_TYPES, EVENT_TYPES, EVENT_SECTIONS, PHOTO_TIERS, CATERING_PLANS,
  DECO_CATEGORY_IMAGES, PHOTO_IMAGES, PHOTOGRAPHER_AVATARS, SAMPLE_PHOTOGRAPHERS,
  DJ_AVATARS, SAMPLE_DJ_PROVIDERS, PRO_AVATARS, SAMPLE_PRO_PROVIDERS,
  VENUE_AVATARS, CATERER_AVATARS, SAMPLE_CATERER_PROVIDERS, SAMPLE_VENUE_PROVIDERS,
  VENUE_DECO_MAP, ALL_DECO_CATEGORIES, BIRTHDAY_DECO_IMAGES, SAMPLE_BIRTHDAY_DECO_PROVIDERS,
  FLOWER_GARLAND_ITEMS, FLOWER_CATEGORIES, EQUIPMENT_RENTAL_ITEMS, EQUIPMENT_CATEGORIES,  // eslint-disable-line no-unused-vars
  DECO_STYLES, MEAL_MENU_MAP, SAMPLE_NADASWARAM_THAVIL, SAMPLE_BIRTHDAY_ARTISTS
} from './instantPricingData';

const InstantPricingPage = () => {
  const { t } = useLanguage();
  const { user } = useAuth();

  // Food name → emoji mapping for the banana-leaf preview modal
  const getFoodEmoji = (name) => {
    const n = (name || '').toLowerCase();
    if (n.includes('idly') || n.includes('idli')) return '🍙';
    if (n.includes('dosa')) return '🥞';
    if (n.includes('vada') || n.includes('bonda')) return '🍩';
    if (n.includes('pongal') || n.includes('upma') || n.includes('kesari')) return '🍚';
    if (n.includes('poori') || n.includes('puri') || n.includes('parotta') || n.includes('paratha') || n.includes('naan') || n.includes('roti') || n.includes('chapati') || n.includes('idiyappam') || n.includes('appam')) return '🫓';
    if (n.includes('biryani') || n.includes('pulav') || n.includes('rice')) return '🍛';
    if (n.includes('sambar') || n.includes('rasam') || n.includes('soup') || n.includes('dal')) return '🍲';
    if (n.includes('chutney') || n.includes('pickle') || n.includes('podi')) return '🥣';
    if (n.includes('curd')) return '🥛';
    if (n.includes('chicken')) return '🍗';
    if (n.includes('mutton') || n.includes('lamb') || n.includes('beef')) return '🍖';
    if (n.includes('fish') || n.includes('prawn') || n.includes('seafood')) return '🐟';
    if (n.includes('egg')) return '🥚';
    if (n.includes('paneer') || n.includes('chenna') || n.includes('palak') || n.includes('kofta') || n.includes('gravy') || n.includes('masala')) return '🍛';
    if (n.includes('jamun') || n.includes('jalebi') || n.includes('mysore pak') || n.includes('halwa') || n.includes('ladoo') || n.includes('rasamalai')) return '🍮';
    if (n.includes('ice cream')) return '🍨';
    if (n.includes('coffee')) return '☕';
    if (n.includes('tea')) return '🍵';
    if (n.includes('buttermilk') || n.includes('badam') || n.includes('milk')) return '🥛';
    if (n.includes('juice') || n.includes('sarbath')) return '🧃';
    if (n.includes('salad')) return '🥗';
    return '🍽️';
  };
  const [step, setStep] = useState(1);
  // Controls the date-picker popover so we can auto-close it the moment the
  // customer picks a day (rather than forcing them to click outside).
  const [datePopoverOpen, setDatePopoverOpen] = useState(false);
  const [phone, setPhone] = useState(user?.phone || '');
  const [eventType, setEventType] = useState('');
  const [marriageType, setMarriageType] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [eventCity, setEventCity] = useState(() => {
    try { return localStorage.getItem('eventbook_event_city') || ''; } catch { return ''; }
  });
  // Exact geolocation captured when the customer taps "Use my location".
  // Persisted alongside eventCity for radius-based filtering + analytics.
  const [eventGeo, setEventGeo] = useState(() => {
    try { return JSON.parse(localStorage.getItem('eventbook_event_geo') || 'null') || null; } catch { return null; }
  });
  useEffect(() => {
    try {
      if (eventGeo) localStorage.setItem('eventbook_event_geo', JSON.stringify(eventGeo));
      else localStorage.removeItem('eventbook_event_geo');
    } catch {}
  }, [eventGeo]);
  // Admin-curated master list of serviceable cities — populates the event_city dropdown.
  const [masterCities, setMasterCities] = useState([]);
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/service-cities`)
      .then(r => setMasterCities(r.data?.cities || []))
      .catch(() => setMasterCities([]));
  }, []);
  const [guests, setGuests] = useState('');
  const [pricingData, setPricingData] = useState(null);
  const [loading, setLoading] = useState(false);
  // Venue time slot for non-wedding events: 'morning' | 'evening'.
  // Wedding-tier events always use the full day (timeSlot stays null).
  const [timeSlot, setTimeSlot] = useState(SLOT_MORNING);

  const [selectedVenueType, setSelectedVenueType] = useState('');

  // Cart
  const [cart, setCart] = useState([]);
  const [expandedSections, setExpandedSections] = useState({
    decorator: true, caterer: true, photographer: true, dj: true, venue: true, professional: true, flowers: true, equipment: true, custom_decor: true,
    nadaswaram_thavil: true, birthday_artists: true, return_gift: true, cake: true,
  });

  const [cateringTier, setCateringTier] = useState('budget');
  const [cateringType, setCateringType] = useState('veg');
  const [photoTier, setPhotoTier] = useState('budget');

  // Side panel state
  const [sidePanel, setSidePanel] = useState({ open: false, type: '', title: '', data: null });
  // Decoration Team — slideshow lightbox for the pro's decor packages.
  const [decorLightbox, setDecorLightbox] = useState({ open: false, providerId: null, packages: [], startIdx: 0 });
  // Universal pro / entertainment-pro portfolio gallery (images + videos).
  const [portfolioLightbox, setPortfolioLightbox] = useState({ open: false, providerId: null, items: [], startIdx: 0, title: '' });
  // Full-screen provider showcase lightbox — aggregates ALL media for a
  // single vendor / pro / venue / entertainment-pro card and surfaces a
  // sticky "Add to estimate" bar at the bottom.
  const [showcaseLightbox, setShowcaseLightbox] = useState({
    open: false,
    provider: null,
    cartId: null,
    addPayload: null, // { id, name, price, category, image, details }
    priceText: '',
    subPriceText: '',
  });
  // Customer can toggle "nearby only" — hides cross-city sample providers + limits real venues to <=25 km.
  const [nearbyOnly, setNearbyOnly] = useState(false);
  // Caterer service style filter — 'all' | 'buffet' | 'traditional'.
  const [catererStyle, setCatererStyle] = useState('all');
  // Caterer "min items per meal" filter — optional. When set (e.g. 8) only
  // caterers with at least one meal whose veg menu has ≥ N items are shown.
  const [catererMinItems, setCatererMinItems] = useState('');
  // Banana-leaf meal popup state. Opened when customer clicks a meal name
  // (Tiffin / Lunch / Snacks / Dinner) on a caterer card.
  const [mealLeafPopup, setMealLeafPopup] = useState({
    open: false, providerId: null, mealKey: null, mealLabel: '', caterer: null, pureVeg: false,
  });

  // Real vendor catalogs aggregated from Return Gift / Flowers & Pooja vendors.
  // Fetched when the customer lands on the results page (step === 2). Shape:
  //   { shops: [{id, business_name, city, area, rating, reviews, verified, bio,
  //               items:[{id, name, price, unit, image, category_tag}], item_count}],
  //     shop_count, items: <flat back-compat list> }
  // Each shop renders as a caterer-style card with the items as an expandable menu.
  const [realReturnGiftData, setRealReturnGiftData] = useState({ shops: [], items: [] });
  const [realFlowerData, setRealFlowerData] = useState({ shops: [], items: [] });
  // Which shop card's menu is currently expanded (per category).
  const [expandedFlowerShop, setExpandedFlowerShop] = useState(null);
  const [expandedReturnGiftShop, setExpandedReturnGiftShop] = useState(null);

  useEffect(() => {
    if (step !== 2) return;
    const ctrl = new AbortController();
    const params = new URLSearchParams();
    if (eventCity) params.append('city', eventCity);
    if (eventType) params.append('event_type', eventType);
    const qs = params.toString() ? `?${params.toString()}` : '';
    axios.get(`${BACKEND_URL}/api/instant-pricing/return-gifts${qs}`, { signal: ctrl.signal })
      .then(r => setRealReturnGiftData({ shops: r.data?.shops || [], items: r.data?.items || [] }))
      .catch(() => {});
    axios.get(`${BACKEND_URL}/api/instant-pricing/flowers-pooja${qs}`, { signal: ctrl.signal })
      .then(r => setRealFlowerData({ shops: r.data?.shops || [], items: r.data?.items || [] }))
      .catch(() => {});
    return () => ctrl.abort();
  }, [step, eventCity, eventType]);

  // Expanded provider in photography panel
  const [expandedProviderId, setExpandedProviderId] = useState(null);

  // Food timing selection for Server/Chef/Asst Chef
  const [selectedFoodTiming, setSelectedFoodTiming] = useState({});

  // Keep phone in sync once the auth context resolves (user may load after mount).
  useEffect(() => {
    if (user?.phone && !phone) setPhone(user.phone);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.phone]);

  // Saved packages
  const [savedPackages, setSavedPackages] = useState([]);
  const [loadingPackages, setLoadingPackages] = useState(false);
  const [editingPackageId, setEditingPackageId] = useState(null);
  const [swapItemId, setSwapItemId] = useState(null);     // cart line being replaced
  const [swapCategory, setSwapCategory] = useState('');   // category of swap target

  // Per-meal guest counts
  const [mealGuests, setMealGuests] = useState({});
  const [realPhotographers, setRealPhotographers] = useState([]);
  const [realBirthdayDecorators, setRealBirthdayDecorators] = useState([]);
  const [realVenues, setRealVenues] = useState([]);
  const [realDecorationTeams, setRealDecorationTeams] = useState([]);
  // Phase 3: real pros for every dual-pricing category, keyed by category slug.
  const [realDualPricedPros, setRealDualPricedPros] = useState({});
  const [customDecorPackages, setCustomDecorPackages] = useState([]);
  const [customDecorPhotoIdx, setCustomDecorPhotoIdx] = useState({}); // { [pkgId]: activeIdx }
  const [lightbox, setLightbox] = useState({ open: false, photos: [], initialIndex: 0, title: '' });
  // Full-screen popup for a single tapped flower / return-gift product.
  const [productPopup, setProductPopup] = useState(null);  // { shop, item, finalPrice }
  const [bananaLeafPreview, setBananaLeafPreview] = useState(null); // { providerName, mealLabel, items, isVeg }
  const [platformCommission, setPlatformCommission] = useState({ professional: 0, vendor: 0, venue: 0 });
  const [segmentToggles, setSegmentToggles] = useState({}); // admin: { [eventType]: { [segKey]: bool } }
  const [segmentImages, setSegmentImages] = useState({}); // admin: { [segKey]: imageUrl }
  const [realNadaswaramThavil, setRealNadaswaramThavil] = useState([]);
  const [realBirthdayArtists, setRealBirthdayArtists] = useState([]);
  // Celebrate With Music — vendor music-service rows filtered by event/tradition.
  const [musicServiceRows, setMusicServiceRows] = useState([]);

  // Helper: is a segment enabled for the current event type? Defaults to true if not configured.
  const isSegmentEnabled = (segKey) => {
    if (!eventType) return true;
    const evCfg = segmentToggles[eventType];
    if (!evCfg) return true;
    return evCfg[segKey] !== false;
  };

  // Helper: resolve admin-managed banner URL for a segment (returns absolute URL)
  const resolveSegImg = (u) => {
    if (!u) return '';
    if (u.startsWith('http://') || u.startsWith('https://')) return u;
    return `${BACKEND_URL}${u}`;
  };

  // Unified segment ordering across all event types. The customer's
  // browsing/decision funnel is: pick venue → fix catering → photography →
  // professionals → flowers/pooja → entertainment → music → gifts → rentals.
  // The few segments that only render for specific events (cake for birthday,
  // birthday_deco for birthday, custom_decor_packages for marriage/engagement)
  // share the same numeric slot as their generic counterpart.
  //
  // SPECIAL CASE — Birthday: Cake is the headline item, so it sits ABOVE
  // birthday decoration.
  const orderOf = (segKey) => {
    const map = {
      cake: 5,                   // birthday only — sits at the very top
      decorator: 10,             // non-birthday events
      birthday_deco: 10,         // birthday only — same slot as decorator
      custom_decor_packages: 15, // marriage / engagement only
      venue: 20,
      caterer: 30,
      photographer: 40,
      professional: 50,
      flowers: 60,
      entertainment_pro: 70,
      dj: 80,
      return_gift: 90,
      equipment: 100,
    };
    return map[segKey] ?? 999;
  };

  // Renders a slim banner strip (~96px tall) above a segment Card.
  const SegmentBanner = ({ segKey, label }) => {
    const url = segmentImages[segKey];
    if (!url) return null;
    return (
      <div className="relative w-full h-24 overflow-hidden border border-[#E8E1D5] border-b-0 bg-stone-200"
        data-testid={`segment-banner-${segKey}`}>
        <img src={resolveSegImg(url)} alt={label || segKey}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={(e) => { e.target.style.display = 'none'; }} />
        <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-black/10 to-transparent" />
        {label && (
          <div className="absolute left-3 bottom-2 text-sand-50 font-heading text-lg drop-shadow-md">
            {label}
          </div>
        )}
      </div>
    );
  };

  // Selected menu items per meal
  const [selectedMenuItems, setSelectedMenuItems] = useState({});

  // Caterer meal configuration per provider: { [providerId]: { [mealKey]: { enabled, guests, isNonVeg, removedItems } } }
  const [catererMealConfig, setCatererMealConfig] = useState({});

  const guestCount = parseInt(guests) || 100;

  // Initialize meal guests when guests changes
  useEffect(() => {
    if (guests) {
      const g = parseInt(guests) || 100;
      setMealGuests(prev => {
        const updated = { ...prev };
        ['morning_tiffin', 'lunch', 'evening_snacks', 'dinner', 'juice'].forEach(k => {
          if (!updated[k]) updated[k] = g;
        });
        return updated;
      });
    }
  }, [guests]);

  // Fetch real photographer vendors (with galleries) whenever event/marriage type changes
  useEffect(() => {
    const fetchRealPhotographers = async () => {
      try {
        const params = new URLSearchParams();
        if (eventType) params.append('event_type', eventType);
        if (eventType === 'marriage' && marriageType) params.append('marriage_type', marriageType);
        if (eventCity) params.append('event_city', eventCity);
        const { data } = await axios.get(`${BACKEND_URL}/api/instant-pricing/photographers?${params.toString()}`);
        setRealPhotographers(Array.isArray(data) ? data : []);
      } catch {
        setRealPhotographers([]);
      }
    };
    if (eventType) fetchRealPhotographers();
  }, [eventType, marriageType, eventCity]);

  // Fetch real birthday decorator vendors (with theme images) — surface them in the
  // birthday decor side panel above sample providers
  useEffect(() => {
    const fetchRealBirthdayDecorators = async () => {
      try {
        const params = new URLSearchParams();
        if (eventCity) params.append('event_city', eventCity);
        const { data } = await axios.get(`${BACKEND_URL}/api/instant-pricing/birthday-decorators?${params.toString()}`);
        setRealBirthdayDecorators(Array.isArray(data) ? data : []);
      } catch {
        setRealBirthdayDecorators([]);
      }
    };
    if (eventType === 'birthday') fetchRealBirthdayDecorators();
  }, [eventType, eventCity]);

  // Fetch real registered venue providers with event-aware pricing.
  // Backend resolves to event-specific price or falls back to marriage price.
  // Engagement/reception always use marriage price.
  useEffect(() => {
    const fetchRealVenues = async () => {
      try {
        const params = new URLSearchParams();
        if (eventType) params.append('event_type', eventType);
        if (eventDate) params.append('event_date', eventDate);
        // Wedding-tier events reserve the full day; only non-wedding events
        // need a slot. Sending the slot ensures slot-aware availability.
        if (!isWeddingEvent(eventType) && timeSlot) params.append('time_slot', timeSlot);
        if (eventCity) params.append('event_city', eventCity);
        // Guest-count gating — venues whose capacity < guests OR whose
        // min_guests > guests are hidden by the backend.
        const guestNum = parseInt(guests, 10);
        if (Number.isFinite(guestNum) && guestNum > 0) params.append('guests', String(guestNum));
        // Exact lat/lng — enables nearest-first sorting on the backend.
        if (eventGeo?.lat && eventGeo?.lng) {
          params.append('event_lat', String(eventGeo.lat));
          params.append('event_lng', String(eventGeo.lng));
        }
        const { data } = await axios.get(`${BACKEND_URL}/api/instant-pricing/venues?${params.toString()}`);
        setRealVenues(Array.isArray(data) ? data : []);
      } catch {
        setRealVenues([]);
      }
    };
    if (eventType) fetchRealVenues();
    else setRealVenues([]);
  }, [eventType, eventDate, timeSlot, eventCity, guests, eventGeo]);

  // Fetch Decoration Team professionals with event-aware pricing (wedding vs
  // other_events). Refetches whenever event type / date / slot changes so the
  // listing always reflects current availability + price.
  useEffect(() => {
    const fetchDecorationTeams = async () => {
      try {
        const params = new URLSearchParams();
        if (eventType) params.append('event_type', eventType);
        if (eventDate) params.append('event_date', eventDate);
        if (!isWeddingEvent(eventType) && timeSlot) params.append('time_slot', timeSlot);
        if (eventCity) params.append('event_city', eventCity);
        const { data } = await axios.get(`${BACKEND_URL}/api/instant-pricing/decoration-team?${params.toString()}`);
        setRealDecorationTeams(Array.isArray(data) ? data : []);
      } catch {
        setRealDecorationTeams([]);
      }
    };
    if (eventType) fetchDecorationTeams();
    else setRealDecorationTeams([]);
  }, [eventType, eventDate, timeSlot, eventCity]);

  // Phase 3: unified fetch for every dual-pricing pro category. Backend
  // groups results by category slug so we can inject into each pro panel
  // without firing one request per category.
  useEffect(() => {
    const fetchDualPricedPros = async () => {
      try {
        const params = new URLSearchParams();
        if (eventType) params.append('event_type', eventType);
        if (eventDate) params.append('event_date', eventDate);
        if (!isWeddingEvent(eventType) && timeSlot) params.append('time_slot', timeSlot);
        if (eventCity) params.append('event_city', eventCity);
        const { data } = await axios.get(`${BACKEND_URL}/api/instant-pricing/pros?${params.toString()}`);
        setRealDualPricedPros(data && typeof data === 'object' ? data : {});
      } catch {
        setRealDualPricedPros({});
      }
    };
    if (eventType) fetchDualPricedPros();
    else setRealDualPricedPros({});
  }, [eventType, eventDate, timeSlot, eventCity]);

  // Fetch custom wedding decoration packages (decorator vendors' custom bundles)
  useEffect(() => {
    const fetchCustomDecorPackages = async () => {
      try {
        const params = marriageType ? `?marriage_type=${encodeURIComponent(marriageType)}` : '';
        const { data } = await axios.get(`${BACKEND_URL}/api/instant-pricing/decoration-packages${params}`);
        setCustomDecorPackages(Array.isArray(data) ? data : []);
      } catch {
        setCustomDecorPackages([]);
      }
    };
    if (eventType === 'marriage' || eventType === 'wedding' || eventType === 'engagement') {
      fetchCustomDecorPackages();
    } else {
      setCustomDecorPackages([]);
    }
  }, [eventType, marriageType]);

  // Celebrate With Music — fetch real vendor music services filtered by event + tradition.
  useEffect(() => {
    const fetchMusic = async () => {
      try {
        const params = new URLSearchParams();
        if (eventType) params.append('event_type', eventType);
        if (marriageType) params.append('marriage_type', marriageType);
        const { data } = await axios.get(`${BACKEND_URL}/api/instant-pricing/celebrate-music?${params.toString()}`);
        setMusicServiceRows(Array.isArray(data) ? data : []);
      } catch {
        setMusicServiceRows([]);
      }
    };
    if (eventType) fetchMusic();
    else setMusicServiceRows([]);
  }, [eventType, marriageType]);

  // Fetch admin segment toggles (one-time)
  useEffect(() => {
    const fetchSegments = async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/instant-pricing/segments`);
        setSegmentToggles(data || {});
      } catch {
        setSegmentToggles({});
      }
    };
    fetchSegments();
  }, []);

  // Fetch admin-managed segment banner images (one-time)
  useEffect(() => {
    const fetchSegmentImages = async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/segment-images`);
        setSegmentImages((data && data.images) || {});
      } catch {
        setSegmentImages({});
      }
    };
    fetchSegmentImages();
  }, []);

  // Fetch real Music Instrument Performers (Nadaswaram, Thavil, generic) — marriage/engagement only
  useEffect(() => {
    const fetchNadaswaramThavil = async () => {
      try {
        const [nada, thavil, generic] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/professionals?category=nadaswaram_artist`).then(r => r.data).catch(() => []),
          axios.get(`${BACKEND_URL}/api/professionals?category=thavil_artist`).then(r => r.data).catch(() => []),
          axios.get(`${BACKEND_URL}/api/professionals?category=music_instrument_performer`).then(r => r.data).catch(() => []),
        ]);
        const all = [
          ...(Array.isArray(nada) ? nada.map(p => ({ ...p, _subType: 'nadaswaram', _categoryLabel: 'Nadaswaram' })) : []),
          ...(Array.isArray(thavil) ? thavil.map(p => ({ ...p, _subType: 'thavil', _categoryLabel: 'Thavil' })) : []),
          ...(Array.isArray(generic) ? generic.map(p => ({ ...p, _subType: 'instrument', _categoryLabel: 'Instrument' })) : []),
        ];
        setRealNadaswaramThavil(all);
      } catch {
        setRealNadaswaramThavil([]);
      }
    };
    if (eventType === 'marriage' || eventType === 'engagement') fetchNadaswaramThavil();
    else setRealNadaswaramThavil([]);
  }, [eventType]);

  // Fetch real Birthday Artists — Buffoon, Mimicry, Singer (birthday only)
  useEffect(() => {
    const fetchBirthdayArtists = async () => {
      try {
        const [buffoon, mimicry, singer] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/professionals?category=buffoon`).then(r => r.data).catch(() => []),
          axios.get(`${BACKEND_URL}/api/professionals?category=mimicry_artist`).then(r => r.data).catch(() => []),
          axios.get(`${BACKEND_URL}/api/professionals?category=singer`).then(r => r.data).catch(() => []),
        ]);
        const all = [
          ...(Array.isArray(buffoon) ? buffoon : []),
          ...(Array.isArray(mimicry) ? mimicry : []),
          ...(Array.isArray(singer) ? singer : []),
        ];
        setRealBirthdayArtists(all);
      } catch {
        setRealBirthdayArtists([]);
      }
    };
    if (eventType === 'birthday') fetchBirthdayArtists();
    else setRealBirthdayArtists([]);
  }, [eventType]);

  // Fetch platform commission settings (admin-controlled markup applied to listing prices)
  useEffect(() => {
    const fetchCommission = async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/referral/settings`);
        if (data?.platform_commission) {
          setPlatformCommission({
            professional: Number(data.platform_commission.professional) || 0,
            vendor: Number(data.platform_commission.vendor) || 0,
            venue: Number(data.platform_commission.venue) || 0,
          });
        }
      } catch {}
    };
    fetchCommission();
  }, []);

  // Group resolver: section/category → professional|vendor|venue
  const groupForCategory = (category) => {
    const c = String(category || '').toLowerCase();
    if (c.includes('venue') || c.includes('mandapam')) return 'venue';
    if (['priest', 'choreographer', 'makeup_artist', 'mehendi', 'professional'].some(k => c.includes(k))) return 'professional';
    return 'vendor';
  };

  // Markup helper — applies platform commission on top of listing price
  const markup = (price, group = 'vendor') => {
    if (!price) return 0;
    const pct = Number(platformCommission[group]) || 0;
    return Math.round(Number(price) * (1 + pct / 100));
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Map cart item.category → platform commission group
  const groupForItemCategory = (cat) => {
    const c = String(cat || '').toLowerCase();
    if (c.includes('venue') || c.includes('mandapam')) return 'venue';
    if (c.includes('professional') || c.includes('priest') || c.includes('mehendi') || c.includes('makeup')) return 'professional';
    return 'vendor';
  };

  // Apply platform commission markup to a list price (used at display + total)
  const itemDisplayPrice = (item) => {
    if (!item) return 0;
    const grp = groupForItemCategory(item.category);
    const pct = Number(platformCommission[grp]) || 0;
    return Math.round(Number(item.price || 0) * (1 + pct / 100));
  };

  const addToCart = (item) => {
    const exists = cart.find(c => c.id === item.id);
    if (exists) return;
    setCart([...cart, item]);
    toast.success(`Added: ${item.name}`);
  };

  const removeFromCart = (itemId) => {
    setCart(cart.filter(c => c.id !== itemId));
  };

  const cartTotal = cart.reduce((sum, item) => sum + itemDisplayPrice(item), 0);

  const closeSidePanel = () => setSidePanel({ open: false, type: '', title: '', data: null });

  // Initialize caterer meal config for a provider when Info is expanded
  const initCatererMealConfig = (providerId) => {
    if (catererMealConfig[providerId]) return; // already initialized
    setCatererMealConfig(prev => ({
      ...prev,
      [providerId]: {
        tiffin: { enabled: false, guests: guestCount, isNonVeg: false, removedItems: [] },
        lunch: { enabled: false, guests: guestCount, isNonVeg: false, removedItems: [] },
        snacks: { enabled: false, guests: guestCount, isNonVeg: false, removedItems: [] },
        dinner: { enabled: false, guests: guestCount, isNonVeg: false, removedItems: [] },
      }
    }));
  };

  // Compute per-meal total for caterer (sum of enabled meals × per-plate price × guests)
  const computeCatererTotal = (provider) => {
    const cfg = catererMealConfig[provider.id] || {};
    const MEAL_KEYS = ['tiffin', 'lunch', 'snacks', 'dinner'];
    let total = 0;
    let hasEnabled = false;
    MEAL_KEYS.forEach(k => {
      const mc = cfg[k];
      const md = provider.meals?.[k];
      if (mc?.enabled && md) {
        const price = mc.isNonVeg && md.priceNV ? md.priceNV : (md.priceVeg || 0);
        total += price * (mc.guests || 0);
        hasEnabled = true;
      }
    });
    return { total, hasEnabled };
  };

  const toggleCatererMeal = (providerId, mealKey) => {
    setCatererMealConfig(prev => ({
      ...prev,
      [providerId]: {
        ...prev[providerId],
        [mealKey]: { ...prev[providerId][mealKey], enabled: !prev[providerId][mealKey].enabled }
      }
    }));
  };

  const updateCatererMealGuests = (providerId, mealKey, val) => {
    const num = parseInt(val) || 0;
    setCatererMealConfig(prev => ({
      ...prev,
      [providerId]: {
        ...prev[providerId],
        [mealKey]: { ...prev[providerId][mealKey], guests: num }
      }
    }));
  };

  const toggleCatererMealType = (providerId, mealKey) => {
    setCatererMealConfig(prev => ({
      ...prev,
      [providerId]: {
        ...prev[providerId],
        [mealKey]: { ...prev[providerId][mealKey], isNonVeg: !prev[providerId][mealKey].isNonVeg, removedItems: [] }
      }
    }));
  };

  const toggleCatererMenuItem = (providerId, mealKey, itemName) => {
    setCatererMealConfig(prev => {
      const removed = prev[providerId][mealKey].removedItems || [];
      const isRemoved = removed.includes(itemName);
      return {
        ...prev,
        [providerId]: {
          ...prev[providerId],
          [mealKey]: {
            ...prev[providerId][mealKey],
            removedItems: isRemoved ? removed.filter(i => i !== itemName) : [...removed, itemName]
          }
        }
      };
    });
  };

  // Load saved packages when step 2 loads
  useEffect(() => {
    if (step === 2 && phone) {
      const fetchPkgs = async () => {
        try {
          const { data } = await axios.get(`${BACKEND_URL}/api/packages/${encodeURIComponent(phone)}`, { withCredentials: true });
          setSavedPackages(data || []);
        } catch { /* not authenticated as this customer, or no packages yet */ }
      };
      fetchPkgs();
    }
  }, [step, phone]);

  // Hydrate from a "Edit in Pricing" deep-link — CustomerDashboard writes the
  // saved package to localStorage, navigates here, and we resume directly at
  // Step 2 with the cart pre-populated. Skips the event-details form entirely.
  // Third source: PublicPackagePage's Edit CTA writes just the package id to
  // `eventbook_edit_package_id` — we fetch the full package from `/api/packages`
  // here, then forward through the same hydration path.
  useEffect(() => {
    let editById = null;
    try {
      editById = localStorage.getItem('eventbook_edit_package_id');
    } catch { /* noop */ }
    if (!editById) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/packages`, { withCredentials: true });
        const found = (data || []).find((p) => p.id === editById);
        if (found && !cancelled) {
          const payload = {
            phone: found.phone,
            eventType: found.event_type,
            marriageType: found.marriage_type || '',
            eventDate: found.event_date || '',
            guestCount: found.guests,
            items: found.items || [],
            editingPackageId: found.id,
          };
          localStorage.setItem('eventbook_edit_package', JSON.stringify(payload));
        }
      } catch {
        toast.error('Could not load package for editing — please log in.');
      } finally {
        try { localStorage.removeItem('eventbook_edit_package_id'); } catch { /* noop */ }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Hydrate from a "Edit in Pricing" deep-link — CustomerDashboard writes the
  // saved package to localStorage, navigates here, and we resume directly at
  // Step 2 with the cart pre-populated. Skips the event-details form entirely.
  useEffect(() => {
    let raw = null;
    let pendingRaw = null;
    try {
      raw = localStorage.getItem('eventbook_edit_package');
      pendingRaw = localStorage.getItem('eventbook_pending_estimate');
    } catch { /* storage blocked */ }
    // Prefer an explicit edit payload over a pending estimate.
    const source = raw || pendingRaw;
    const sourceKey = raw ? 'eventbook_edit_package' : (pendingRaw ? 'eventbook_pending_estimate' : null);
    if (!source) return;
    let payload;
    try { payload = JSON.parse(source); } catch { localStorage.removeItem(sourceKey); return; }
    try { if (sourceKey) localStorage.removeItem(sourceKey); } catch { /* noop */ }
    if (!payload || !payload.eventType) return;

    setPhone(payload.phone || '');
    setEventType(payload.eventType);
    setMarriageType(payload.marriageType || '');
    setEventDate(payload.eventDate || '');
    setGuests(String(payload.guestCount || ''));
    // In swap mode the targeted line is dropped — customer picks a fresh one.
    const baseItems = Array.isArray(payload.items) ? payload.items : [];
    const itemsForCart = payload.swapItemId
      ? baseItems.filter((i) => i.id !== payload.swapItemId)
      : baseItems;
    setCart(itemsForCart);
    if (payload.editingPackageId) setEditingPackageId(payload.editingPackageId);
    if (payload.swapItemId) {
      setSwapItemId(payload.swapItemId);
      setSwapCategory(payload.swapCategory || '');
    }

    const guestsForUrl = parseInt(payload.guestCount, 10) || 100;
    (async () => {
      try {
        const { data } = await axios.get(
          `${BACKEND_URL}/api/instant-pricing?guests=${guestsForUrl}${payload.marriageType ? `&marriage_type=${payload.marriageType}` : ''}`,
        );
        setPricingData(data);
        setMealGuests({
          morning_tiffin: guestsForUrl, lunch: guestsForUrl, evening_snacks: guestsForUrl,
          dinner: guestsForUrl, juice: guestsForUrl,
        });
        setStep(2);
        if (payload.swapItemId) {
          toast.success(`Swap mode — pick a replacement ${(payload.swapCategory || 'item').toLowerCase()}`);
        } else {
          toast.success('Loaded saved package — edit and re-save');
        }
      } catch {
        toast.error('Could not load pricing for editing');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSavedPackages = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/packages/${encodeURIComponent(phone)}`, { withCredentials: true });
      setSavedPackages(data || []);
    } catch { /* no packages yet */ }
  };

  const saveAsPackage = async () => {
    if (cart.length === 0) { toast.error('Add items to your estimate first'); return; }
    const pkgNum = savedPackages.length + 1;
    if (pkgNum > 2 && !editingPackageId) { toast.error('Maximum 2 packages. Delete one first.'); return; }

    setLoadingPackages(true);
    try {
      if (editingPackageId) {
        await axios.put(`${BACKEND_URL}/api/packages/${editingPackageId}`, {
          items: cart, total: cartTotal,
        }, { withCredentials: true });
        toast.success('Package updated!');
        setEditingPackageId(null);
      } else {
        await axios.post(`${BACKEND_URL}/api/packages`, {
          package_name: `Package ${pkgNum}`,
          phone, event_type: eventType, marriage_type: marriageType,
          event_date: eventDate, guests: parseInt(guests),
          items: cart, total: cartTotal,
          time_slot: isWeddingEvent(eventType) ? null : timeSlot,
          event_city: eventCity || null,
        }, { withCredentials: true });
        toast.success(`Saved as Package ${pkgNum}!`);
      }
      await loadSavedPackages();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to save package';
      if (err.response?.status === 401 || err.response?.status === 403) {
        toast.error('Please log in as a customer to save packages');
      } else {
        toast.error(detail);
      }
    }
    setLoadingPackages(false);
  };

  const deletePackage = async (pkgId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/packages/${pkgId}`, { withCredentials: true });
      toast.success('Package deleted');
      await loadSavedPackages();
      if (editingPackageId === pkgId) setEditingPackageId(null);
    } catch { toast.error('Failed to delete'); }
  };

  const loadPackageIntoCart = (pkg) => {
    setCart(pkg.items || []);
    setEditingPackageId(pkg.id);
    toast.success(`Loaded "${pkg.package_name}" — make changes and save`);
  };

  // Get the selected food timing price for a provider type
  const getProviderPriceWithTiming = (providerTypeName, basePrice) => {
    const timingKey = selectedFoodTiming[providerTypeName];
    if (!timingKey || !pricingData) return basePrice;
    let packages = [];
    if (providerTypeName === 'Server / Waiter') packages = pricingData.professional?.server_packages || [];
    const pkg = packages.find(p => p.name === timingKey);
    return pkg ? pkg.defaultPrice : basePrice;
  };

  const openPhotoPanel = (tier) => {
    const items = pricingData?.photographer?.items || [];
    const providers = sortByPrice(SAMPLE_PHOTOGRAPHERS[tier.key] || []);
    setExpandedProviderId(null);
    setSidePanel({ open: true, type: 'photography', title: `${tier.label} Photography`, data: { tier, items, providers } });
  };

  const openDJPanel = (djType) => {
    const providers = sortByPrice(SAMPLE_DJ_PROVIDERS[djType.name] || []);
    setExpandedProviderId(null);
    setSidePanel({ open: true, type: 'dj_provider', title: djType.name, data: { djType, providers } });
  };

  // Food-related professional types that need pure_veg filtering for Brahmin weddings
  const FOOD_RELATED_PROS = ['Chef', 'Assistant Chef', 'Server / Waiter', 'Catering Team'];

  // Helper: sort providers by price ascending (low → high). Stable for equal prices.
  const sortByPrice = (arr) => [...(arr || [])].sort((a, b) => (Number(a?.price) || 0) - (Number(b?.price) || 0));

  const openProPanel = (proType) => {
    const slug = slugForProName(proType.name);
    const dualPriced = isDualPricingPro(proType.name);
    // Priest tradition filter: Hindu/Brahmin marriages → only Hindu-titled priests,
    // Christian → Christian officiants, Muslim → Nikah officiants. Priests without
    // a structured tradition (legacy or unmigrated) are hidden from these flows.
    const priestTradMap = { hindu: 'hindu', brahmin: 'hindu', christian: 'christian', muslim: 'muslim' };
    const expectedTrad = slug === 'priest' ? (priestTradMap[marriageType] || null) : null;
    // Phase 3: inject real registered dual-priced pros at the top of the
    // provider list for ANY eligible category. They display effective
    // (event-aware) price + uploaded portfolio photos and are marked verified.
    const realFromBackend = (dualPriced && slug)
      ? (realDualPricedPros[slug] || [])
        .filter(p => slug !== 'priest' || (expectedTrad && p.priest_tradition === expectedTrad))
        .map((p, i) => ({
          id: p.id || `${slug}-real-${i}`,
          // Prefix the priest's chosen title before their name (e.g. "Iyer Ravi Kumar").
          name: slug === 'priest' && p.priest_title ? `${p.priest_title} ${p.name}` : p.name,
          avatar: i % 4,
          rating: p.rating || 5.0,
          reviews: 0,
          location: p.location || '—',
          experience: p.experience_years ? `${p.experience_years} yrs` : '—',
          price: Number(p.effective_price) || 0,
          speciality: (p.photos || []).length ? `${p.photos.length} portfolio photo${p.photos.length === 1 ? '' : 's'}` : '',
          photos: p.photos || [],
          verified: true,
          // Decoration Team only — simple decor packages for the slideshow lightbox.
          decor_packages: p.decor_packages || [],
          // Pro / entertainment-pro portfolio gallery (images + ≤30s videos).
          portfolio_items: p.portfolio_items || [],
        }))
      : [];
    // Legacy fallback for Decoration Team uses the same merged list — already
    // covered by `realDualPricedPros.decoration_team`. Keep `realDecorationTeams`
    // as a secondary safety net for any lagging components.
    let providers = realFromBackend;
    if (proType.name === 'Decoration Team' && realFromBackend.length === 0) {
      providers = (realDecorationTeams || []).map((dt, i) => ({
        id: dt.id || `dt-real-${i}`,
        name: dt.name,
        avatar: i % 4,
        rating: dt.rating || 5.0,
        reviews: 0,
        location: dt.location || '—',
        experience: dt.experience_years ? `${dt.experience_years} yrs` : '—',
        price: Number(dt.effective_price) || 0,
        speciality: (dt.photos || []).length ? `${dt.photos.length} portfolio photo${dt.photos.length === 1 ? '' : 's'}` : '',
        photos: dt.photos || [],
        verified: true,
      }));
    }
    // Append sample providers below the verified ones — but ONLY when the
    // category isn't dual-priced (otherwise sample data would muddy real
    // event-aware pricing the customer just saw).
    if (!dualPriced) {
      let samples = SAMPLE_PRO_PROVIDERS[proType.name] || [];
      if (marriageType === 'brahmin' && FOOD_RELATED_PROS.includes(proType.name)) {
        samples = samples.filter(p => p.pure_veg === true);
      }
      providers = [...providers, ...samples];
    }
    providers = sortByPrice(providers);
    let packages = [];
    if (proType.name === 'Server / Waiter') {
      packages = pricingData?.professional?.server_packages || [];
    }
    setExpandedProviderId(null);
    setSidePanel({ open: true, type: 'pro_provider', title: proType.name, data: { proType, providers, packages, perGuest: proType.name === 'Muhurtham Pai / Return Gift', pureVegOnly: marriageType === 'brahmin' && FOOD_RELATED_PROS.includes(proType.name) } });
  };

  // Open Entertainment/Music panel — used for Nathaswaram & Thavil (combined), Magician, Buffoon, Singer with Karaoke,
  // Mimicry Artist, Instrument Player, Standup Comedian, Anchor/Emcee, Dancer/Dance Troupe
  const openEntertainmentPanel = (entType) => {
    // entType = { name, defaultPrice }
    let providers = [];
    if (entType.name === 'Nathaswaram & Thavil') {
      const real = (realNadaswaramThavil || []).map((p, i) => ({
        id: p.id || `nt-real-${i}`,
        name: `${p.business_name || p.name}${p._categoryLabel ? ` (${p._categoryLabel})` : ''}`,
        avatar: i % 4,
        rating: 5.0, reviews: 0, location: p.location || '—', experience: p.experience || '—',
        price: Number(p.base_price) || (p._subType === 'thavil' ? 12000 : 16000),
        speciality: p.description || '', verified: true,
      }));
      const samples = SAMPLE_NADASWARAM_THAVIL.map(p => ({ ...p, name: `${p.name} (${p.categoryLabel})`, verified: false }));
      providers = [...real, ...samples];
    } else if (['Buffoon', 'Mimicry Artist', 'Singer with Karaoke'].includes(entType.name)) {
      const subKey = entType.name === 'Buffoon' ? 'buffoon' : entType.name === 'Mimicry Artist' ? 'mimicry' : 'singer';
      const realCat = entType.name === 'Mimicry Artist' ? 'mimicry_artist' : subKey;
      const real = (realBirthdayArtists || []).filter(p => p.category === realCat).map((p, i) => ({
        id: p.id || `${subKey}-real-${i}`, name: p.business_name || p.name, avatar: i % 4,
        rating: 5.0, reviews: 0, location: p.location || '—', experience: p.experience || '—',
        price: Number(p.base_price) || (subKey === 'buffoon' ? 5500 : 7500),
        speciality: p.description || '', verified: true,
      }));
      const samples = SAMPLE_BIRTHDAY_ARTISTS.filter(p => p.subType === subKey).map(p => ({ ...p, verified: false }));
      providers = [...real, ...samples];
    } else if (['Magician', 'Anchor / Emcee', 'Dancer / Dance Troupe', 'DJ'].includes(entType.name)) {
      // Reuse SAMPLE_DJ_PROVIDERS data — these types used to live in the DJ section
      const key = entType.name === 'Magician' ? 'Magician / Comedian' : entType.name;
      providers = (SAMPLE_DJ_PROVIDERS[key] || []).map(p => ({ ...p, verified: false }));
    } else if (entType.name === 'Standup Comedian') {
      providers = [
        { id: 'sc-1', name: 'Laugh Riot Comedy', avatar: 0, rating: 4.6, reviews: 68, location: 'Chennai', experience: '7 yrs', price: 15000, speciality: 'Stand-up, observational comedy' },
        { id: 'sc-2', name: 'Punchline Pros', avatar: 1, rating: 4.4, reviews: 44, location: 'Bangalore', experience: '5 yrs', price: 12000, speciality: 'Clean stand-up for family events' },
        { id: 'sc-3', name: 'Mic Drop Comedians', avatar: 2, rating: 4.5, reviews: 52, location: 'Mumbai', experience: '6 yrs', price: 14000, speciality: 'Corporate & birthday shows' },
      ];
    } else if (entType.name === 'Instrument Player') {
      providers = [
        { id: 'ip-1', name: 'Solo Guitarist Akash', avatar: 3, rating: 4.6, reviews: 58, location: 'Chennai', experience: '8 yrs', price: 10000, speciality: 'Acoustic, fingerstyle, Tamil/Bollywood' },
        { id: 'ip-2', name: 'Keyboard Maestro Priya', avatar: 0, rating: 4.7, reviews: 72, location: 'Bangalore', experience: '10 yrs', price: 12000, speciality: 'Piano, keyboard, live background' },
        { id: 'ip-3', name: 'Violin Strings by Rahul', avatar: 1, rating: 4.8, reviews: 91, location: 'Mumbai', experience: '12 yrs', price: 14000, speciality: 'Classical + contemporary violin' },
        { id: 'ip-4', name: 'Flute by Ravi', avatar: 2, rating: 4.5, reviews: 38, location: 'Coimbatore', experience: '6 yrs', price: 9000, speciality: 'Carnatic flute, bansuri' },
      ];
    } else if (entType.name === 'Costume Performers') {
      providers = [
        { id: 'cp-1', name: 'Mascot Magic', avatar: 0, rating: 4.7, reviews: 64, location: 'Chennai', experience: '6 yrs', price: 15000, speciality: 'Cartoon mascots, kids events, brand activations' },
        { id: 'cp-2', name: 'Character Cast Co.', avatar: 1, rating: 4.5, reviews: 41, location: 'Bangalore', experience: '4 yrs', price: 18000, speciality: 'Princess, superhero, movie character performers' },
        { id: 'cp-3', name: 'Themed Performers Hub', avatar: 2, rating: 4.6, reviews: 53, location: 'Mumbai', experience: '8 yrs', price: 22000, speciality: 'Bollywood characters, regional folk costumes' },
        { id: 'cp-4', name: 'Wedding Mascot Crew', avatar: 3, rating: 4.4, reviews: 29, location: 'Coimbatore', experience: '3 yrs', price: 12000, speciality: 'Welcome mascots, baraat performers' },
      ];
    }
    setExpandedProviderId(null);
    setSidePanel({ open: true, type: 'pro_provider', title: entType.name, data: { proType: entType, providers: sortByPrice(providers), packages: [], perGuest: false, pureVegOnly: false } });
  };

  // Celebrate With Music — open a panel for a specific music service key.
  // The vendors come from `musicServiceRows` (already event-filtered upstream),
  // so we just collect every row matching this key and render them.
  const openMusicServicePanel = (musicKey, musicLabel) => {
    const providers = (musicServiceRows || [])
      .filter(r => r.key === musicKey)
      .map((r, i) => ({
        id: `music-${musicKey}-${r.vendor_id}`,
        name: r.vendor_name,
        avatar: i % 4,
        rating: 5.0,
        reviews: 0,
        location: r.location || '—',
        experience: '—',
        price: Number(r.price) || 0,
        speciality: r.universal ? 'Available for all events' : '',
        verified: true,
      }));
    setExpandedProviderId(null);
    setSidePanel({
      open: true, type: 'pro_provider', title: musicLabel,
      data: { proType: { name: musicLabel, defaultPrice: providers[0]?.price || 0 },
        providers: sortByPrice(providers), packages: [], perGuest: false, pureVegOnly: false },
    });
  };

  const openVenuePanel = (venueType) => {
    const providers = sortByPrice(SAMPLE_VENUE_PROVIDERS[venueType.name] || []);
    // Filter real venues that have this venue type
    const realMatches = (realVenues || [])
      .map(rv => {
        const matched = (rv.venues || []).find(v => (v.name || '').toLowerCase() === venueType.name.toLowerCase());
        return matched ? { ...rv, matched } : null;
      })
      .filter(Boolean);
    setExpandedProviderId(null);
    setSidePanel({ open: true, type: 'venue_provider', title: venueType.name, data: { venueType, providers, realVenues: realMatches } });
  };

  // Convert a backend "real caterer" record into the provider shape used by
  // the existing sample-caterer card UI (with meals.tiffin/lunch/snacks/dinner).
  //
  // Per-meal listing price =  (cheapest menu variant's price_per_plate)
  //                        +  (service-price cell for closest item-tier to that
  //                            variant's dish-count × closest guest-bracket to
  //                            customer's guest count)
  //
  // Falls back gracefully when service-price grid is missing.
  const verifiedCatererToProvider = (rc) => {
    const guestNum = parseInt(guests, 10) || 0;
    const sp = rc.service_price;

    // Pre-compute the nearest guest bracket (same regardless of meal).
    let nearestBrk = null;
    if (sp && Array.isArray(sp.guest_brackets) && sp.guest_brackets.length) {
      let nearestDiff = Infinity;
      sp.guest_brackets.forEach((b) => {
        const bn = parseInt(b, 10) || 0;
        const diff = Math.abs(bn - guestNum);
        if (diff < nearestDiff) {
          nearestDiff = diff;
          nearestBrk = b;
        }
      });
    }

    // For a given dish count, find the closest item-tier in the grid.
    const closestTier = (dishCount) => {
      const tiers = (sp && sp.item_tiers) || [];
      if (!tiers.length) return null;
      let best = tiers[0];
      let bestDiff = Math.abs((parseInt(best, 10) || 0) - dishCount);
      for (let i = 1; i < tiers.length; i += 1) {
        const d = Math.abs((parseInt(tiers[i], 10) || 0) - dishCount);
        if (d < bestDiff) { bestDiff = d; best = tiers[i]; }
      }
      return best;
    };

    // Look up the cheapest cell across styles at (tier, bracket).
    const lookupCell = (tier, bracket) => {
      if (!sp || !sp.matrix || !tier || !bracket) return 0;
      let minCell = Infinity;
      Object.values(sp.matrix).forEach((styleTiers) => {
        const p = styleTiers?.[tier]?.[bracket];
        if (p && Number(p) > 0 && Number(p) < minCell) minCell = Number(p);
      });
      return Number.isFinite(minCell) ? minCell : 0;
    };

    const buildMeal = (mealKey) => {
      const variants = (rc.menu_variants || {})[mealKey] || [];
      // Pick the cheapest priced variant for this meal.
      const priced = variants.filter((v) => v.price_per_plate);
      if (!priced.length) return null;
      priced.sort((a, b) => a.price_per_plate - b.price_per_plate);
      const chosen = priced[0];
      const dishCount = chosen.dish_count || 0;
      const tier = closestTier(dishCount);
      const spCellAdd = lookupCell(tier, nearestBrk);
      const base = chosen.price_per_plate + spCellAdd;
      // Show all priced variants in this meal as picker options for tooltip.
      const dishSnap = variants.flatMap((v) => v.name ? [v.name] : []).slice(0, 4);
      return {
        priceVeg: Math.round(base),
        menuVeg: dishSnap.length ? dishSnap : ['Custom menu'],
        // Debug-friendly metadata; harmless if unused by the UI.
        _variant: chosen.name,
        _dishCount: dishCount,
        _spCell: spCellAdd,
        _tier: tier,
        _bracket: nearestBrk,
      };
    };

    const meals = {
      tiffin: buildMeal('tiffin'),
      lunch: buildMeal('lunch'),
      snacks: buildMeal('snacks'),
      dinner: buildMeal('dinner'),
    };
    // Headline `price` = cheapest meal priceVeg (used by the card's "from" pill).
    const allPrices = Object.values(meals).filter(Boolean).map((m) => m.priceVeg);
    const minPrice = allPrices.length ? Math.min(...allPrices) : (rc.min_price || 0);
    return {
      id: `verified-${rc.id}`,
      _verifiedId: rc.id,
      isVerified: true,
      name: rc.business_name || rc.name,
      rating: 0,
      reviews: 0,
      location: rc.kyc_area || rc.kyc_base_city || '',
      experience: '',
      price: minPrice,
      avatar: 0,
      speciality: (rc.service_styles || []).map((s) => s === 'buffet' ? 'Buffet' : 'Traditional').join(', '),
      service_styles: rc.service_styles || [],
      pure_veg: !!rc.pure_veg,
      menu: [], // unused on verified rows
      meals,
      // Universal media library — surfaces under the caterer card as a thumbnail strip.
      portfolio_items: rc.portfolio_items || [],
    };
  };

  const openCatererPanel = async (tierKey) => {
    let providers = SAMPLE_CATERER_PROVIDERS[tierKey] || [];
    // Filter for pure veg only if Brahmin wedding
    if (marriageType === 'brahmin') {
      providers = providers.filter(p => p.pure_veg === true);
    }
    providers = sortByPrice(providers);
    const tierLabel = tierKey === 'budget' ? 'Budget' : tierKey === 'premium' ? 'Premium' : 'Luxury';
    setExpandedProviderId(null);
    // Open panel immediately with sample data; fetch real caterers in parallel
    // and merge them in once the response arrives.
    setSidePanel({ open: true, type: 'caterer_provider', title: `${tierLabel} Caterers`, data: { tierKey, tierLabel, providers, realCaterers: [], pureVegOnly: marriageType === 'brahmin' } });
    try {
      const params = new URLSearchParams();
      if (eventCity) params.append('event_city', eventCity);
      if (marriageType === 'brahmin') params.append('pure_veg_only', 'true');
      const { data } = await axios.get(`${BACKEND_URL}/api/instant-pricing/caterers?${params.toString()}`);
      const realArr = Array.isArray(data) ? data : [];
      // Convert verified caterers into provider-shape and merge into the panel's
      // providers list so they render with the same Tiffin/Lunch/Snacks/Dinner
      // checkbox card the customer is used to. Verified ones bubble to the top.
      const verifiedProviders = realArr.map(verifiedCatererToProvider);
      setSidePanel((sp) => sp.type === 'caterer_provider'
        ? {
            ...sp,
            data: {
              ...sp.data,
              providers: sortByPrice([...verifiedProviders, ...(sp.data?.providers || [])]),
              realCaterers: realArr,
            },
          }
        : sp);
    } catch {
      // Network blip — sample providers still render so the panel stays useful.
    }
  };

  const openBirthdayDecoPanel = (decoItem) => {
    setExpandedProviderId(null);
    setSidePanel({ open: true, type: 'birthday_deco_provider', title: 'Birthday Decorators', data: { decoItem, providers: sortByPrice(SAMPLE_BIRTHDAY_DECO_PROVIDERS) } });
  };

  const openMealPanel = (meal) => {
    const catItems = pricingData?.caterer?.items || [];
    const relevantCats = MEAL_MENU_MAP[meal.key] || [];
    const menuItems = catItems.filter(i => relevantCats.includes(i.category));
    setSidePanel({ open: true, type: 'catering', title: meal.label, data: { mealKey: meal.key, menuItems } });
  };

  const toggleMealMenuItem = (mealKey, item) => {
    setSelectedMenuItems(prev => {
      const current = prev[mealKey] || [];
      const exists = current.find(i => i.name === item.name);
      if (exists) {
        return { ...prev, [mealKey]: current.filter(i => i.name !== item.name) };
      }
      return { ...prev, [mealKey]: [...current, item] };
    });
  };

  const getMealGuestCount = (mealKey) => mealGuests[mealKey] || guestCount;

  const handleGetPricing = async () => {
    if (!eventType) { toast.error('Please select event type'); return; }
    if (eventType === 'marriage' && !marriageType) { toast.error('Please select marriage type'); return; }
    if (!eventDate) { toast.error('Please select event date'); return; }
    if (!eventCity || !eventCity.trim()) { toast.error('Please enter your event city / location'); return; }
    if (!guests || parseInt(guests) < 1) { toast.error('Please enter number of guests'); return; }

    // Persist the city so the Cart / Estimate panel can surface it and so
    // the customer doesn't have to retype it next visit.
    try { localStorage.setItem('eventbook_event_city', eventCity.trim()); } catch { /* noop */ }

    setLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/instant-pricing?guests=${guests}${marriageType ? `&marriage_type=${marriageType}` : ''}${eventType ? `&event_type=${encodeURIComponent(eventType)}` : ''}`);
      setPricingData(data);
      setStep(2);
      // Init per-meal guests
      const g = parseInt(guests) || 100;
      setMealGuests({ morning_tiffin: g, lunch: g, evening_snacks: g, dinner: g, juice: g });
    } catch {
      toast.error('Failed to fetch pricing');
    }
    setLoading(false);
  };

  const getCateringTotal = () => {
    const planKey = eventType === 'marriage' ? marriageType : eventType;
    const plan = CATERING_PLANS[planKey];
    if (!plan) return 0;
    let total = 0;
    plan.meals.forEach(meal => {
      const tierKey = cateringTier;
      let perPlate = 0;
      if (cateringType === 'nonveg' && meal[`${tierKey}NV`]) {
        perPlate = meal[`${tierKey}NV`];
      } else {
        perPlate = meal[`${tierKey}Veg`] || 0;
      }
      total += perPlate * getMealGuestCount(meal.key);
    });
    return total;
  };

  return (
    <div className="min-h-screen bg-sand-50">
      {/* SEO-friendly <title> + meta tags. When customer has picked an event
          and city, the title surfaces those + the canonical SEO slug so this
          page is also crawl-friendly. */}
      <Helmet>
        <title>{(() => {
          const ev = EVENT_TYPES.find((e) => e.value === eventType);
          const evLabel = ev ? t(ev.labelKey) : '';
          if (evLabel && eventCity) return `${evLabel} vendors in ${eventCity} | Instant Pricing — BeeWeddy`;
          if (evLabel)              return `${evLabel} vendors | Instant Pricing — BeeWeddy`;
          if (eventCity)            return `Event vendors in ${eventCity} | Instant Pricing — BeeWeddy`;
          return 'Instant Pricing — BeeWeddy';
        })()}</title>
        <meta name="description" content={
          `Get instant pricing on wedding, birthday & event vendors${eventCity ? ` in ${eventCity}` : ''}. ` +
          `Compare verified photographers, decorators, caterers, venues, DJs and more on BeeWeddy.`
        } />
      </Helmet>
      {/* Header */}
      <header className="sticky top-0 z-50 bg-sand-50/80 border-b border-[#E8E1D5] backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 sm:py-5 flex justify-between items-center">
          <BrandLogo size="lg" linkTo="/" />
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            {step === 2 && cart.length > 0 && (
              user ? (
                <button
                  onClick={() => {
                    // Save current cart to localStorage for preview, then navigate to dashboard
                    const previewData = {
                      eventType, marriageType, eventDate, guestCount, phone,
                      items: cart, total: cartTotal,
                      createdAt: new Date().toISOString(),
                    };
                    localStorage.setItem('eventbook_preview_package', JSON.stringify(previewData));
                    window.location.href = '/dashboard';
                  }}
                  className="flex items-center gap-2 bg-brand-primary text-sand-50 px-4 py-2 hover:bg-[#A84A38] transition-colors cursor-pointer"
                  data-testid="preview-package-btn">
                  <ShoppingCart size={18} />
                  <span className="font-bold">Preview</span>
                  <span>₹{cartTotal.toLocaleString('en-IN')}</span>
                </button>
              ) : (
                <button
                  onClick={() => {
                    // Stash the in-flight estimate so the user lands back here
                    // with their cart intact after authenticating.
                    const previewData = {
                      eventType, marriageType, eventDate, guestCount, phone,
                      items: cart, total: cartTotal,
                      createdAt: new Date().toISOString(),
                    };
                    try { localStorage.setItem('eventbook_pending_estimate', JSON.stringify(previewData)); } catch { /* noop */ }
                    window.location.href = `/login?next=${encodeURIComponent('/instant-pricing')}`;
                  }}
                  className="flex items-center gap-2 bg-brand-primary text-sand-50 px-4 py-2 hover:bg-[#A84A38] transition-colors cursor-pointer"
                  data-testid="login-to-preview-btn">
                  <ShoppingCart size={18} />
                  <span className="font-bold">Login to Preview</span>
                  <span>₹{cartTotal.toLocaleString('en-IN')}</span>
                </button>
              )
            )}
          </div>
        </div>
      </header>

      {/* Step 1: Event Details Form */}
      {step === 1 && (
        <div className="max-w-2xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-light text-center mb-2">
              Get <span className="text-brand-primary">{t('pricing.title')}</span>
            </h1>
            <p className="text-center text-sm sm:text-base text-text-secondary mb-8 sm:mb-10">Tell us about your event and get estimated costs instantly</p>

            <Card className="bg-white border-[#E8E1D5]">
              <CardContent className="py-6 sm:py-8 space-y-6">
                <div className="space-y-2">
                  <Label className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary">{t('pricing.event_type')} <span className="text-red-500">*</span></Label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                    {EVENT_TYPES.map(ev => (
                      <button key={ev.value} type="button"
                        className={`p-3 border-2 text-left transition-all ${eventType === ev.value ? 'border-brand-primary bg-brand-primary/5' : 'border-[#E8E1D5] hover:border-brand-primary/30'}`}
                        onClick={() => { setEventType(ev.value); if (ev.value !== 'marriage') setMarriageType(''); }}
                        data-testid={`event-type-${ev.value}`}>
                        <ev.icon size={20} className={eventType === ev.value ? 'text-brand-primary' : 'text-text-secondary'} />
                        <div className="font-medium text-sm mt-1 leading-tight">{t(ev.labelKey)}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {eventType === 'marriage' && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="space-y-2">
                    <Label className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary">Type of Marriage <span className="text-red-500">*</span></Label>
                    <div className="grid grid-cols-2 gap-3">
                      {MARRIAGE_TYPES.map(mt => (
                        <button key={mt.value} type="button"
                          className={`p-3 border-2 text-left transition-all text-sm font-medium ${marriageType === mt.value ? 'border-brand-primary bg-brand-primary/5 text-brand-primary' : 'border-[#E8E1D5] text-text-secondary hover:border-brand-primary/30'}`}
                          onClick={() => setMarriageType(mt.value)} data-testid={`marriage-type-${mt.value}`}>{mt.label}</button>
                      ))}
                    </div>
                  </motion.div>
                )}

                <div className="space-y-2">
                  <Label className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary flex items-center gap-1">
                    <Calendar size={14} /> {t('pricing.event_date')} <span className="text-red-500">*</span>
                  </Label>
                  {(() => {
                    // Highlight muhurtham (auspicious) days in yellow only for
                    // wedding-tier events; for everything else fall back to a
                    // plain HTML date input — keeps the UI simple.
                    const isWeddingTier = ['marriage', 'engagement'].includes(eventType);
                    if (!isWeddingTier) {
                      return (
                        <Input type="date" value={eventDate} onChange={(e) => setEventDate(e.target.value)}
                          min={new Date().toISOString().split('T')[0]} className="rounded-none border-[#E8E1D5] h-12 text-lg" data-testid="pricing-date" />
                      );
                    }
                    // Convert stored 'YYYY-MM-DD' string ↔ Date for DayPicker
                    const selectedDate = eventDate
                      ? new Date(`${eventDate}T00:00:00`)
                      : undefined;
                    const today = new Date(); today.setHours(0, 0, 0, 0);
                    return (
                      <>
                        <Popover open={datePopoverOpen} onOpenChange={setDatePopoverOpen}>
                          <PopoverTrigger asChild>
                            <button type="button"
                              className="w-full text-left rounded-none border border-[#E8E1D5] h-12 px-3 text-lg bg-white hover:border-brand-primary/40 transition-colors flex items-center justify-between"
                              data-testid="pricing-date">
                              <span className={eventDate ? 'text-text-primary' : 'text-text-secondary/60'}>
                                {eventDate
                                  ? selectedDate.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
                                  : 'Pick your event date'}
                              </span>
                              {eventDate && isMuhurthamDate(selectedDate) && (
                                <span className="text-[10px] tracking-[0.15em] uppercase font-bold bg-yellow-300 text-yellow-900 px-2 py-0.5 rounded" data-testid="muhurtham-badge">
                                  Muhurtham
                                </span>
                              )}
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="p-0 w-auto bg-white border border-[#E8E1D5] shadow-xl z-[100]" align="start">
                            <CalendarPicker
                              mode="single"
                              selected={selectedDate}
                              onSelect={(d) => {
                                if (!d) return;
                                const y = d.getFullYear();
                                const m = String(d.getMonth() + 1).padStart(2, '0');
                                const day = String(d.getDate()).padStart(2, '0');
                                setEventDate(`${y}-${m}-${day}`);
                                // Auto-close so the customer can immediately
                                // continue with the rest of the form.
                                setDatePopoverOpen(false);
                              }}
                              disabled={{ before: today }}
                              modifiers={{ muhurtham: getMuhurthamDateObjects() }}
                              modifiersClassNames={{
                                muhurtham: 'bg-yellow-300 text-yellow-900 font-bold hover:bg-yellow-400 focus:bg-yellow-400 aria-selected:bg-yellow-500 aria-selected:text-white',
                              }}
                              data-testid="pricing-calendar"
                            />
                            <div className="flex items-center gap-2 px-4 pb-3 pt-1 text-[11px] text-text-secondary border-t border-[#E8E1D5] bg-white">
                              <span className="inline-block w-3 h-3 rounded-sm bg-yellow-300 border border-yellow-500" />
                              Muhurtham (auspicious) day
                            </div>
                          </PopoverContent>
                        </Popover>
                      </>
                    );
                  })()}
                </div>

                <CityLocationInput
                  value={eventCity}
                  onChange={setEventCity}
                  onGeoChange={(g) => setEventGeo(g)}
                  label="Event Location"
                  required
                  testId="pricing-city"
                  cities={masterCities}
                />

                <div className="space-y-2">
                  <Label className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary flex items-center gap-1">
                    <Users size={14} /> {t('pricing.guest_count')} <span className="text-red-500">*</span>
                  </Label>
                  <Input type="number" value={guests} onChange={(e) => setGuests(e.target.value)}
                    placeholder="e.g. 500" className="rounded-none border-[#E8E1D5] h-12 text-lg" data-testid="pricing-guests" />
                </div>

                <Button onClick={handleGetPricing} disabled={loading}
                  className="w-full bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none h-14 text-lg font-heading"
                  data-testid="get-pricing-btn">
                  {loading ? '...' : t('pricing.get_pricing')}
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      )}

      {/* Step 2: Pricing Results */}
      {step === 2 && pricingData && (
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-6 sm:py-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 sm:mb-6 gap-2">
            <div>
              <h1 className="font-heading text-2xl sm:text-3xl tracking-tighter">Estimated Pricing</h1>
              <p className="text-text-secondary text-xs sm:text-sm">
                {eventType === 'marriage' ? MARRIAGE_TYPES.find(m => m.value === marriageType)?.label : (EVENT_TYPES.find(e => e.value === eventType)?.labelKey ? t(EVENT_TYPES.find(e => e.value === eventType).labelKey) : '')} — {guestCount} {t('pricing.guests')} — {eventDate}
              </p>
            </div>
            <Button onClick={() => setStep(1)} variant="outline" className="rounded-none border-[#E8E1D5] text-sm">Change Details</Button>
          </div>

          {/* AI Vendor Concierge — Gemini-grounded RAG search. Sits in its
              own segment ABOVE the existing per-category listings so it
              doesn't disturb the structured flow. Strict no-contact-info,
              no-external-knowledge rules are enforced by the backend. */}
          <div className="mb-6">
            <GroundedSearchBox eventType={eventType} city={eventCity} />
          </div>

          {/* Swap mode banner */}
          {swapItemId && (
            <div className="mb-4 sm:mb-6 bg-amber-50 border-2 border-amber-300 rounded p-3 flex items-center justify-between flex-wrap gap-2" data-testid="swap-mode-banner">
              <div className="text-sm text-amber-900">
                <span className="font-bold uppercase tracking-wider text-xs">Swap Mode</span> — pick a replacement{swapCategory ? <> for your <span className="font-semibold">{swapCategory}</span></> : null} line. The original was already removed; other items stay locked.
              </div>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => { setSwapItemId(null); setSwapCategory(''); }}
                className="rounded-none border-amber-400 text-amber-900 hover:bg-amber-100 text-xs"
                data-testid="swap-mode-cancel"
              >
                Cancel swap
              </Button>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
            <div className="lg:col-span-2 flex flex-col gap-4">

              {/* DECORATOR — replaced static sub-categories with vendor-authored packages */}
              {EVENT_SECTIONS[eventType]?.decorator && eventType !== 'birthday' && isSegmentEnabled('decorator') && (
                <div style={{ order: orderOf('decorator') }}>
                  <SegmentBanner segKey="decorator" label="Decoration" />
                  <DecoratorPanel
                    eventType={eventType}
                    marriageType={marriageType}
                    cart={cart}
                    setCart={setCart}
                    markup={markup}
                    expanded={expandedSections.decorator}
                    onToggle={() => toggleSection('decorator')}
                  />
                </div>
              )}

              {/* CUSTOM WEDDING DECORATION PACKAGES */}
              {(eventType === 'marriage' || eventType === 'wedding' || eventType === 'engagement') &&
                isSegmentEnabled('custom_decor_packages') &&
                customDecorPackages.length > 0 && (
                <div style={{ order: orderOf('custom_decor_packages') }}>
                <SegmentBanner segKey="custom_decor_packages" label="Simple Wedding Decoration" />
                <Card className="bg-white border-purple-200" data-testid="pricing-custom-decor">
                  <CardHeader className="cursor-pointer" onClick={() => toggleSection('custom_decor')}>
                    <div className="flex items-center justify-between">
                      <CardTitle className="font-heading text-xl flex items-center gap-2">
                        <Heart size={20} className="text-purple-600" /> Simple Wedding Decoration
                        <span className="text-[10px] px-1.5 py-0.5 bg-purple-600 text-white rounded font-bold uppercase tracking-wider">Vendor Bundles</span>
                      </CardTitle>
                      {expandedSections.custom_decor ? <CaretUp size={18} /> : <CaretDown size={18} />}
                    </div>
                  </CardHeader>
                  {expandedSections.custom_decor && (
                    <CardContent className="space-y-3">
                      <p className="text-xs text-text-secondary">Curated all-in-one packages from our verified decorators</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {customDecorPackages.map((pkg) => {
                          const cartId = `custom-decor-${pkg.id}`;
                          const inCart = cart.find(c => c.id === cartId);
                          const photos = (pkg.photos && pkg.photos.length) ? pkg.photos : (pkg.photo ? [pkg.photo] : []);
                          const activeIdx = customDecorPhotoIdx[pkg.id] || 0;
                          const cover = photos[activeIdx] || photos[0];
                          return (
                            <div key={pkg.id}
                              className={`group relative border-2 rounded-lg overflow-hidden transition-all ${inCart ? 'border-green-500 ring-2 ring-green-200' : 'border-purple-200 hover:border-purple-400'}`}
                              data-testid={`custom-decor-card-${pkg.id}`}>
                              <div className="relative h-36 bg-gradient-to-br from-purple-100 to-purple-200 overflow-hidden">
                                {cover ? (
                                  <img src={cover.startsWith('http') ? cover : `${BACKEND_URL}${cover}`}
                                    alt={pkg.name}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 cursor-zoom-in"
                                    loading="lazy"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setLightbox({ open: true, photos, initialIndex: activeIdx, title: `${pkg.vendor_name || ''} — ${pkg.name}` });
                                    }}
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                    data-testid={`custom-decor-cover-${pkg.id}`} />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center text-purple-400">
                                    <Heart size={32} weight="duotone" />
                                  </div>
                                )}
                                <span className="absolute top-2 left-2 text-[10px] px-1.5 py-0.5 bg-purple-600 text-white rounded font-bold uppercase tracking-wider shadow-md">Bundle</span>
                                {/* Expand to lightbox */}
                                {photos.length > 0 && (
                                  <button
                                    type="button"
                                    aria-label="View fullscreen"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setLightbox({ open: true, photos, initialIndex: activeIdx, title: `${pkg.vendor_name || ''} — ${pkg.name}` });
                                    }}
                                    className="absolute bottom-2 right-2 bg-white/90 hover:bg-white text-purple-700 rounded-full w-7 h-7 flex items-center justify-center shadow-md opacity-0 group-hover:opacity-100 transition z-10"
                                    data-testid={`custom-decor-expand-${pkg.id}`}
                                    title="View fullscreen"
                                  >
                                    <ImageIcon size={14} weight="bold" />
                                  </button>
                                )}
                                {photos.length > 1 && (
                                  <span className="absolute top-2 right-2 text-[10px] px-1.5 py-0.5 bg-black/60 backdrop-blur-sm text-white rounded font-bold shadow-md">
                                    {activeIdx + 1}/{photos.length}
                                  </span>
                                )}
                                {inCart && <div className="absolute top-2 right-12 bg-green-500 text-white rounded-full p-1"><Check size={12} weight="bold" /></div>}
                                {/* Prev/Next chevrons */}
                                {photos.length > 1 && (
                                  <>
                                    <button type="button" aria-label="Previous"
                                      onClick={(e) => { e.stopPropagation(); setCustomDecorPhotoIdx(prev => ({ ...prev, [pkg.id]: (activeIdx - 1 + photos.length) % photos.length })); }}
                                      className="absolute left-1 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-purple-700 rounded-full w-7 h-7 flex items-center justify-center shadow-md opacity-0 group-hover:opacity-100 transition"
                                      data-testid={`custom-decor-prev-${pkg.id}`}>
                                      <CaretLeft size={14} weight="bold" />
                                    </button>
                                    <button type="button" aria-label="Next"
                                      onClick={(e) => { e.stopPropagation(); setCustomDecorPhotoIdx(prev => ({ ...prev, [pkg.id]: (activeIdx + 1) % photos.length })); }}
                                      className="absolute right-1 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-purple-700 rounded-full w-7 h-7 flex items-center justify-center shadow-md opacity-0 group-hover:opacity-100 transition"
                                      data-testid={`custom-decor-next-${pkg.id}`}>
                                      <CaretRight size={14} weight="bold" />
                                    </button>
                                  </>
                                )}
                                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                                  <span className="text-white font-heading text-lg font-bold">₹{markup(pkg.price || 0, 'vendor').toLocaleString('en-IN')}</span>
                                </div>
                              </div>
                              {photos.length > 1 && (
                                <div className="flex gap-1 px-2 pt-2 overflow-x-auto" data-testid={`custom-decor-thumbs-${pkg.id}`}>
                                  {photos.map((u, idx) => (
                                    <button key={idx} type="button"
                                      onClick={() => {
                                        setCustomDecorPhotoIdx(prev => ({ ...prev, [pkg.id]: idx }));
                                      }}
                                      onDoubleClick={() => {
                                        setLightbox({ open: true, photos, initialIndex: idx, title: `${pkg.vendor_name || ''} — ${pkg.name}` });
                                      }}
                                      className={`flex-shrink-0 w-10 h-10 rounded overflow-hidden border-2 transition ${idx === activeIdx ? 'border-purple-600' : 'border-transparent opacity-70 hover:opacity-100'}`}
                                      data-testid={`custom-decor-thumb-${pkg.id}-${idx}`}>
                                      <img src={u.startsWith('http') ? u : `${BACKEND_URL}${u}`} alt="" className="w-full h-full object-cover" />
                                    </button>
                                  ))}
                                </div>
                              )}
                              <div className="p-3">
                                <div className="font-medium text-sm text-text-primary truncate">{pkg.name}</div>
                                {pkg.vendor_name && (
                                  <div className="text-[11px] text-text-secondary mt-0.5 truncate">
                                    by <span className="font-medium">{pkg.vendor_name}</span>
                                    {pkg.vendor_location && <> · {pkg.vendor_location}</>}
                                  </div>
                                )}
                                {pkg.description && (
                                  <p className="text-[11px] text-text-secondary mt-1 line-clamp-2">{pkg.description}</p>
                                )}
                                <Button type="button" size="sm"
                                  className={`w-full mt-2 h-8 rounded-none ${inCart ? 'bg-red-500 hover:bg-red-600' : 'bg-purple-600 hover:bg-purple-700'} text-white`}
                                  onClick={() => {
                                    if (inCart) { removeFromCart(cartId); } else {
                                      addToCart({
                                        id: cartId,
                                        name: `${pkg.vendor_name} — ${pkg.name}`,
                                        price: pkg.price || 0,
                                        category: 'Custom Decoration',
                                        image: (Array.isArray(pkg.photos) && pkg.photos[0]) || pkg.photo || null,
                                        details: {
                                          specs: [
                                            ...(pkg.vendor_location ? [{ label: 'Vendor location', value: pkg.vendor_location }] : []),
                                            ...(pkg.theme ? [{ label: 'Theme', value: pkg.theme }] : []),
                                            ...(pkg.includes_count ? [{ label: 'Items included', value: String(pkg.includes_count) }] : []),
                                          ],
                                          description: pkg.description || null,
                                        },
                                      });
                                    }
                                  }}
                                  data-testid={`custom-decor-add-${pkg.id}`}>
                                  {inCart ? <><Minus size={12} className="mr-1" /> Remove</> : <><Plus size={12} className="mr-1" /> Add to Cart</>}
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  )}
                </Card>
                </div>
              )}

              {/* BIRTHDAY DECORATION */}
              {EVENT_SECTIONS[eventType]?.birthday_deco && isSegmentEnabled('birthday_deco') && (
                <div style={{ order: orderOf('birthday_deco') }}>
                <SegmentBanner segKey="birthday_deco" label="Birthday Decoration" />
                <Card className="bg-white border-[#E8E1D5]" data-testid="pricing-birthday-deco">
                  <CardHeader className="cursor-pointer" onClick={() => toggleSection('decorator')}>
                    <div className="flex items-center justify-between">
                      <CardTitle className="font-heading text-xl flex items-center gap-2">
                        <Heart size={20} className="text-brand-primary" /> {t('pricing.birthday_deco')}
                      </CardTitle>
                      {expandedSections.decorator ? <CaretUp size={18} /> : <CaretDown size={18} />}
                    </div>
                  </CardHeader>
                  {expandedSections.decorator && (
                    <CardContent className="space-y-5">
                      <div>
                        <div className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary mb-2">Popular Themes</div>
                        <p className="text-xs text-text-secondary mb-2">Pick a theme to see decorators</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                          {(pricingData.birthday_decorator?.themes || []).map((theme, idx) => {
                            const inCart = cart.find(c => c.id.startsWith(`bd-prov-${theme.name}-`) || c.id.startsWith(`bd-real-${theme.name}-`));
                            const imgUrl = BIRTHDAY_DECO_IMAGES[idx % BIRTHDAY_DECO_IMAGES.length];
                            return (
                              <div key={idx}
                                className={`group relative border-2 rounded-lg overflow-hidden transition-all cursor-pointer ${inCart ? 'border-green-500 ring-2 ring-green-200' : 'border-[#E8E1D5] hover:border-brand-primary/40'}`}
                                onClick={() => openBirthdayDecoPanel(theme)}
                                data-testid={`bd-card-${idx}`}>
                                <div className="relative h-32 bg-gray-100 overflow-hidden">
                                  <img src={imgUrl} alt={theme.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" onError={(e) => { e.target.style.display = 'none'; }} />
                                  {inCart && <div className="absolute top-2 right-2 bg-green-500 text-white rounded-full p-1"><Check size={12} weight="bold" /></div>}
                                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                                    <span className="text-white font-heading text-lg font-bold">from ₹{Number(theme.defaultPrice).toLocaleString('en-IN')}</span>
                                  </div>
                                </div>
                                <div className="p-3 flex items-center justify-between">
                                  <span className="font-medium text-sm text-text-primary truncate flex-1">{theme.name}</span>
                                  <CaretRight size={16} className="text-brand-primary flex-shrink-0 ml-1" />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Custom themes from vendors — separate segment */}
                      {(() => {
                        // Aggregate all unique custom themes across verified decorators
                        const customMap = new Map();
                        realBirthdayDecorators.forEach(rv => {
                          (rv.themes || []).forEach(t => {
                            if (!t.custom) return;
                            const key = (t.name || '').toLowerCase();
                            if (!key) return;
                            const existing = customMap.get(key);
                            if (existing) {
                              existing.vendors.push(rv);
                              if ((t.price || 0) < existing.minPrice) existing.minPrice = t.price || 0;
                              if (t.photo && !existing.photo) existing.photo = t.photo;
                            } else {
                              customMap.set(key, {
                                name: t.name,
                                photo: t.photo,
                                minPrice: t.price || 0,
                                vendors: [rv],
                              });
                            }
                          });
                        });
                        const customThemes = Array.from(customMap.values());
                        if (customThemes.length === 0) return null;
                        return (
                          <div data-testid="bd-custom-themes-segment">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-xs tracking-[0.2em] uppercase font-bold text-pink-700">Custom Themes</span>
                              <span className="text-[10px] px-1.5 py-0.5 bg-pink-600 text-white rounded font-bold uppercase tracking-wider">Vendor Originals</span>
                              <span className="text-xs text-text-secondary">{customThemes.length} unique theme{customThemes.length !== 1 ? 's' : ''}</span>
                            </div>
                            <p className="text-xs text-text-secondary mb-2">One-of-a-kind themes designed by our verified decorators</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                              {customThemes.map((ct, idx) => {
                                const inCart = cart.find(c => c.id.startsWith(`bd-prov-${ct.name}-`) || c.id.startsWith(`bd-real-${ct.name}-`) || c.id.startsWith(`bd-real-theme-`));
                                return (
                                  <div key={idx}
                                    className={`group relative border-2 rounded-lg overflow-hidden transition-all cursor-pointer ${inCart ? 'border-green-500 ring-2 ring-green-200' : 'border-pink-300 hover:border-pink-500'}`}
                                    onClick={() => openBirthdayDecoPanel({ name: ct.name, defaultPrice: ct.minPrice })}
                                    data-testid={`bd-custom-card-${idx}`}>
                                    <div className="relative h-32 bg-gradient-to-br from-pink-100 to-pink-200 overflow-hidden">
                                      {ct.photo ? (
                                        <img src={ct.photo.startsWith('http') ? ct.photo : `${BACKEND_URL}${ct.photo}`}
                                          alt={ct.name}
                                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                          loading="lazy"
                                          onError={(e) => { e.target.style.display = 'none'; }} />
                                      ) : (
                                        <div className="w-full h-full flex items-center justify-center text-pink-400">
                                          <Heart size={32} weight="duotone" />
                                        </div>
                                      )}
                                      <span className="absolute top-2 left-2 text-[10px] px-1.5 py-0.5 bg-pink-600 text-white rounded font-bold uppercase tracking-wider shadow-md">Custom</span>
                                      {inCart && <div className="absolute top-2 right-2 bg-green-500 text-white rounded-full p-1"><Check size={12} weight="bold" /></div>}
                                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                                        <span className="text-white font-heading text-lg font-bold">from ₹{markup(ct.minPrice || 0, 'vendor').toLocaleString('en-IN')}</span>
                                      </div>
                                    </div>
                                    <div className="p-3 flex items-center justify-between">
                                      <div className="flex-1 min-w-0">
                                        <span className="block font-medium text-sm text-text-primary truncate">{ct.name}</span>
                                        <span className="block text-[10px] text-text-secondary mt-0.5">{ct.vendors.length} vendor{ct.vendors.length !== 1 ? 's' : ''}</span>
                                      </div>
                                      <CaretRight size={16} className="text-pink-600 flex-shrink-0 ml-1" />
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })()}
                    </CardContent>
                  )}
                </Card>
                </div>
              )}

              {/* BIRTHDAY CAKES — birthday events only */}
              {EVENT_SECTIONS[eventType]?.cake && isSegmentEnabled('cake') && (
                <div style={{ order: orderOf('cake') }}>
                  <SegmentBanner segKey="cake" label="Birthday Cakes" />
                  <CakeSegment
                    eventCity={eventCity}
                    expanded={expandedSections.cake}
                    onToggle={() => toggleSection('cake')}
                    cart={cart}
                    setCart={setCart}
                  />
                </div>
              )}

              {/* CATERER */}
              {EVENT_SECTIONS[eventType]?.caterer && isSegmentEnabled('caterer') && (
              <div style={{ order: orderOf('caterer') }}>
              <SegmentBanner segKey="caterer" label="Catering" />
              <Card className="bg-white border-[#E8E1D5]" data-testid="pricing-caterer">
                <CardHeader className="cursor-pointer" onClick={() => toggleSection('caterer')}>
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-heading text-xl flex items-center gap-2">
                      <ForkKnife size={20} className="text-brand-primary" /> {t('pricing.catering')}
                    </CardTitle>
                    {expandedSections.caterer ? <CaretUp size={18} /> : <CaretDown size={18} />}
                  </div>
                </CardHeader>
                {expandedSections.caterer && (
                  <CardContent className="space-y-3">
                    <p className="text-xs text-text-secondary mb-2">Click a tier to see catering service providers. Price shown is per plate.</p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {[
                        { key: 'budget', label: 'Budget', color: 'bg-green-600', desc: 'Simple & traditional', fromPrice: 250 },
                        { key: 'premium', label: 'Premium', color: 'bg-purple-600', desc: 'Multi-cuisine & live counters', fromPrice: 500 },
                        { key: 'luxury', label: 'Luxury', color: 'bg-amber-600', desc: '5-Star & international', fromPrice: 900 },
                      ].map((tier) => {
                        const inCart = cart.find(c => c.id.startsWith(`caterer-${tier.key}-`));
                        const provCount = (SAMPLE_CATERER_PROVIDERS[tier.key] || []).length;
                        return (
                          <div key={tier.key}
                            className={`border-2 p-4 transition-all cursor-pointer hover:border-brand-primary/40 ${inCart ? 'border-green-500 bg-green-50/30' : 'border-[#E8E1D5]'}`}
                            onClick={() => openCatererPanel(tier.key)}
                            data-testid={`catering-tier-${tier.key}`}>
                            <span className={`text-xs px-2 py-0.5 text-white font-bold uppercase tracking-wider ${tier.color}`}>{tier.label}</span>
                            <div className="font-heading text-2xl font-bold text-brand-primary mt-2">from ₹{tier.fromPrice}/plate</div>
                            <div className="text-xs text-text-secondary mt-1">{tier.desc}</div>
                            <div className="flex items-center justify-between mt-2">
                              <span className="text-[10px] text-text-secondary">{provCount} providers</span>
                              <div className="flex items-center gap-1">
                                {inCart && <Check size={12} className="text-green-500" weight="bold" />}
                                <CaretRight size={14} className="text-brand-primary" />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                )}
              </Card>
              </div>
              )}

              {/* PHOTOGRAPHER - clickable tiers open side panel */}
              {EVENT_SECTIONS[eventType]?.photographer && isSegmentEnabled('photographer') && (
              <div style={{ order: orderOf('photographer') }}>
              <SegmentBanner segKey="photographer" label="Photography" />
              <Card className="bg-white border-[#E8E1D5]" data-testid="pricing-photographer">
                <CardHeader className="cursor-pointer" onClick={() => toggleSection('photographer')}>
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-heading text-xl flex items-center gap-2">
                      <Camera size={20} className="text-brand-primary" /> {t('pricing.photography')}
                    </CardTitle>
                    {expandedSections.photographer ? <CaretUp size={18} /> : <CaretDown size={18} />}
                  </div>
                </CardHeader>
                {expandedSections.photographer && (
                  <CardContent className="space-y-3">
                    <p className="text-xs text-text-secondary mb-2">Click a tier to see available services & equipment</p>
                    <div className="grid grid-cols-2 gap-3">
                      {PHOTO_TIERS.map((tier, tIdx) => {
                        const inCart = cart.find(c => c.id === `photo-${tier.key}`);
                        return (
                          <div key={tier.key}
                            className={`border-2 p-4 transition-all cursor-pointer ${inCart ? 'border-green-500 bg-green-50/30' : photoTier === tier.key ? 'border-brand-primary bg-brand-primary/5' : 'border-[#E8E1D5] hover:border-brand-primary/30'}`}
                            onClick={() => { setPhotoTier(tier.key); openPhotoPanel(tier); }}
                            data-testid={`photo-tier-${tier.key}`}>
                            <div className="relative h-24 -mx-4 -mt-4 mb-3 overflow-hidden">
                              <img src={PHOTO_IMAGES[tIdx % PHOTO_IMAGES.length]} alt={tier.label} className="w-full h-full object-cover" loading="lazy" />
                              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                              <span className={`absolute top-2 left-2 text-xs px-2 py-0.5 text-white font-bold uppercase tracking-wider ${tier.color}`}>{tier.label}</span>
                            </div>
                            <div className="font-heading text-2xl font-bold text-brand-primary">₹{markup(tier.basePrice, 'vendor').toLocaleString('en-IN')}</div>
                            <div className="text-xs text-text-secondary mt-1 flex items-center gap-1">Starting price <CaretRight size={12} className="text-brand-primary" /></div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                )}
              </Card>
              </div>
              )}

              {/* ENTERTAINMENT PROFESSIONALS — Magician, Standup Comedian, Instrument Player, Anchor, Dancer
                  + (Birthday only) Buffoon, Singer with Karaoke, Mimicry Artist */}
              {(() => {
                const entTypes = [];
                // Always available (all events)
                entTypes.push({ name: 'Magician', defaultPrice: 20000, subtitle: 'Stage magic, close-up tricks' });
                entTypes.push({ name: 'Standup Comedian', defaultPrice: 12000, subtitle: 'Clean comedy for family & corporate' });
                entTypes.push({ name: 'Instrument Player', defaultPrice: 9000, subtitle: 'Solo guitarist, keyboard, violin, flute' });
                entTypes.push({ name: 'Anchor / Emcee', defaultPrice: 15000, subtitle: 'Bilingual hosts for weddings & shows' });
                entTypes.push({ name: 'Dancer / Dance Troupe', defaultPrice: 25000, subtitle: 'Bharatanatyam, Bollywood, folk, flash mob' });
                entTypes.push({ name: 'Costume Performers', defaultPrice: 18000, subtitle: 'Mascots, character actors, themed costumed performers' });
                entTypes.push({ name: 'DJ', defaultPrice: 15000, subtitle: 'Dance floor, lights, MC mic, sound rig' });
                // Birthday-only additions
                if (eventType === 'birthday') {
                  entTypes.push({ name: 'Buffoon', defaultPrice: 5500, subtitle: 'Clowns, magicians & prop comedy for kids' });
                  entTypes.push({ name: 'Singer with Karaoke', defaultPrice: 7500, subtitle: 'Live singer + karaoke setup' });
                  entTypes.push({ name: 'Mimicry Artist', defaultPrice: 7500, subtitle: 'Celebrity, cartoon & political voices' });
                }
                if (entTypes.length === 0) return null;
                return (
                  <div style={{ order: orderOf('entertainment_pro') }}>
                  <SegmentBanner segKey="entertainment_pro" label="Entertainment" />
                  <Card className="bg-white border-[#E8E1D5]" data-testid="pricing-entertainment-pro">
                    <CardHeader className="cursor-pointer" onClick={() => toggleSection('entertainment_pro')}>
                      <div className="flex items-center justify-between">
                        <CardTitle className="font-heading text-xl flex items-center gap-2">
                          <Star size={20} className="text-amber-700" /> Entertainment Professionals
                        </CardTitle>
                        {expandedSections.entertainment_pro !== false ? <CaretUp size={18} /> : <CaretDown size={18} />}
                      </div>
                    </CardHeader>
                    {expandedSections.entertainment_pro !== false && (
                      <CardContent className="space-y-3">
                        <p className="text-xs text-text-secondary mb-2">Click an entertainment professional type to see available providers</p>
                        {[...entTypes].sort((a, b) => (Number(a.defaultPrice) || 0) - (Number(b.defaultPrice) || 0)).map((entType, idx) => {
                          const inCart = cart.find(c => c.id.startsWith(`pro-prov-${entType.name}-`));
                          // Compute provider count
                          let providerCount = 0;
                          if (entType.name === 'Magician') providerCount = (SAMPLE_DJ_PROVIDERS['Magician / Comedian'] || []).length;
                          else if (entType.name === 'Anchor / Emcee') providerCount = (SAMPLE_DJ_PROVIDERS['Anchor / Emcee'] || []).length;
                          else if (entType.name === 'Dancer / Dance Troupe') providerCount = (SAMPLE_DJ_PROVIDERS['Dancer / Dance Troupe'] || []).length;
                          else if (entType.name === 'Standup Comedian') providerCount = 3;
                          else if (entType.name === 'Instrument Player') providerCount = 4;
                          else if (entType.name === 'Costume Performers') providerCount = 4;
                          else if (entType.name === 'DJ') providerCount = (SAMPLE_DJ_PROVIDERS['DJ'] || []).length;
                          else if (['Buffoon', 'Mimicry Artist', 'Singer with Karaoke'].includes(entType.name)) {
                            const subKey = entType.name === 'Buffoon' ? 'buffoon' : entType.name === 'Mimicry Artist' ? 'mimicry' : 'singer';
                            const realCat = entType.name === 'Mimicry Artist' ? 'mimicry_artist' : subKey;
                            providerCount = ((realBirthdayArtists || []).filter(p => p.category === realCat).length) + SAMPLE_BIRTHDAY_ARTISTS.filter(p => p.subType === subKey).length;
                          }
                          return (
                            <div key={idx}
                              className={`flex items-center justify-between p-3 border-2 rounded transition-all cursor-pointer hover:border-amber-700/40 ${inCart ? 'border-green-500 bg-green-50/30' : 'border-[#E8E1D5]'}`}
                              onClick={() => openEntertainmentPanel(entType)}
                              data-testid={`entertainment-type-${idx}`}>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-medium text-sm">{entType.name}</span>
                                  <span className="text-brand-primary font-bold">from ₹{markup(Number(entType.defaultPrice), 'professional').toLocaleString('en-IN')}</span>
                                  {providerCount > 0 && (
                                    <span className="text-[10px] text-text-secondary">{providerCount} providers</span>
                                  )}
                                </div>
                                <div className="text-xs text-text-secondary mt-0.5 truncate">{entType.subtitle}</div>
                              </div>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                {inCart && <Check size={14} className="text-green-500" weight="bold" />}
                                <CaretRight size={16} className="text-amber-700" />
                              </div>
                            </div>
                          );
                        })}
                      </CardContent>
                    )}
                  </Card>
                  </div>
                );
              })()}

              {/* NATHASWARAM & MUSIC (formerly DJ & Entertainment) */}
              {/* Celebrate With Music — shown for ALL events (sound system needed everywhere) */}
              {eventType && isSegmentEnabled('dj') && (
              <div style={{ order: orderOf('dj') }}>
              <SegmentBanner segKey="dj" label={isWeddingEvent(eventType) ? 'Celebrate With Music' : 'DJ & Music'} />
              <Card className="bg-white border-[#E8E1D5]" data-testid="pricing-dj">
                <CardHeader className="cursor-pointer" onClick={() => toggleSection('dj')}>
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-heading text-xl flex items-center gap-2">
                      <MusicNotes size={20} className="text-brand-primary" /> {t('pricing.dj_entertainment')}
                    </CardTitle>
                    {expandedSections.dj ? <CaretUp size={18} /> : <CaretDown size={18} />}
                  </div>
                </CardHeader>
                {expandedSections.dj && (
                  <CardContent className="space-y-3">
                    <p className="text-xs text-text-secondary mb-2">Click a service to see available providers</p>

                    {/* Vendor Music Services — tradition-filtered grid of real vendor offerings.
                        Groups `musicServiceRows` by music key so each unique service renders one
                        clickable row; tapping it opens a side panel with the offering vendors. */}
                    {(() => {
                      const byKey = new Map();
                      for (const r of (musicServiceRows || [])) {
                        const entry = byKey.get(r.key) || { key: r.key, label: r.label, tradition: r.tradition, universal: r.universal, minPrice: r.price, count: 0 };
                        entry.count += 1;
                        entry.minPrice = Math.min(entry.minPrice, r.price);
                        byKey.set(r.key, entry);
                      }
                      const rows = [...byKey.values()].sort((a, b) => a.minPrice - b.minPrice);
                      if (rows.length === 0) return null;
                      return (
                        <div className="border-2 border-brand-primary/20 rounded p-3 bg-brand-accent/5 space-y-2"
                          data-testid="celebrate-music-vendor-block">
                          <div className="text-xs font-bold uppercase tracking-wider text-brand-primary mb-1">
                            Verified Music Vendors ({rows.length} service{rows.length === 1 ? '' : 's'})
                          </div>
                          {rows.map((r) => {
                            const inCart = cart.find(c => c.id.startsWith(`music-${r.key}-`));
                            return (
                              <div key={r.key}
                                className={`flex items-center justify-between p-3 border-2 rounded transition-all cursor-pointer hover:border-brand-primary/40 bg-white ${inCart ? 'border-green-500 bg-green-50/30' : 'border-[#E8E1D5]'}`}
                                onClick={() => openMusicServicePanel(r.key, r.label)}
                                data-testid={`music-row-customer-${r.key}`}>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-medium text-sm">{r.label}</span>
                                    <span className="text-brand-primary font-bold">from ₹{markup(r.minPrice, 'vendor').toLocaleString('en-IN')}</span>
                                    <span className="text-[10px] text-text-secondary">{r.count} vendor{r.count === 1 ? '' : 's'}</span>
                                    {r.universal && (
                                      <span className="text-[10px] px-1.5 py-0.5 bg-brand-primary text-sand-50 font-bold uppercase tracking-wider rounded">All Events</span>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  {inCart && <Check size={14} className="text-green-500" weight="bold" />}
                                  <CaretRight size={16} className="text-brand-primary" />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}

                    {/* Nathaswaram & Thavil — legacy hardcoded row removed Feb 2026.
                        Now handled dynamically by the Vendor Music Services block above,
                        which surfaces it (with vendor count + min price) only when a real
                        music vendor has ticked it under "Celebrate With Music". */}

                    {/* Music-only types from DJ data — filter out everything
                        already represented under "Celebrate With Music" above
                        (Live Band / Orchestra, Music Sound System) AND the
                        entertainment types that moved to Entertainment Pros. */}
                    {(pricingData.dj?.types || [])
                      .filter(t => !['Magician / Comedian', 'Anchor / Emcee', 'Dancer / Dance Troupe', 'DJ', 'Costume Performers', 'Live Band / Orchestra', 'Music Sound System Provider'].includes(t.name))
                      .slice()
                      .sort((a, b) => (Number(a.defaultPrice) || 0) - (Number(b.defaultPrice) || 0))
                      .map((t, idx) => {
                      const inCart = cart.find(c => c.id.startsWith(`dj-prov-${t.name}-`));
                      const providerCount = (SAMPLE_DJ_PROVIDERS[t.name] || []).length;
                      return (
                        <div key={idx}
                          className={`flex items-center justify-between p-3 border-2 rounded transition-all cursor-pointer hover:border-brand-primary/40 ${inCart ? 'border-green-500 bg-green-50/30' : 'border-[#E8E1D5]'}`}
                          onClick={() => openDJPanel(t)}
                          data-testid={`dj-type-${idx}`}>
                          <div className="flex-1">
                            <span className="font-medium text-sm">{t.name}</span>
                            <span className="text-brand-primary font-bold ml-2">from ₹{Number(t.defaultPrice).toLocaleString('en-IN')}</span>
                            {providerCount > 0 && (
                              <span className="text-[10px] text-text-secondary ml-2">{providerCount} providers</span>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            {inCart && <Check size={14} className="text-green-500" weight="bold" />}
                            <CaretRight size={16} className="text-brand-primary" />
                          </div>
                        </div>
                      );
                    })}
                  </CardContent>
                )}
              </Card>
              </div>
              )}

              {/* VENUE */}
              {EVENT_SECTIONS[eventType]?.venue && isSegmentEnabled('venue') && (
              <div style={{ order: orderOf('venue') }}>
              <SegmentBanner segKey="venue" label="Venue" />
              <Card className="bg-white border-[#E8E1D5]" data-testid="pricing-venue">
                <CardHeader className="cursor-pointer" onClick={() => toggleSection('venue')} data-testid="venue-section-toggle">
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-heading text-xl flex items-center gap-2">
                      <MapPin size={20} className="text-brand-primary" /> {t('pricing.venue')}
                    </CardTitle>
                    {expandedSections.venue ? <CaretUp size={18} /> : <CaretDown size={18} />}
                  </div>
                </CardHeader>
                {expandedSections.venue && (
                  <CardContent className="space-y-3">
                    {/* Time-slot picker for non-wedding events. Wedding-tier
                        events always reserve the whole day, so no picker. */}
                    {!isWeddingEvent(eventType) && (
                      <div className="border-2 border-brand-primary/30 rounded p-3 bg-brand-accent/5" data-testid="venue-slot-picker">
                        <div className="text-[11px] tracking-[0.18em] uppercase font-bold text-brand-primary mb-2">
                          Choose Time Slot
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          {TIME_SLOTS.map((s) => (
                            <button key={s.key} type="button"
                              onClick={() => setTimeSlot(s.key)}
                              className={`p-2.5 border-2 rounded text-left transition-all ${timeSlot === s.key ? 'border-brand-primary bg-brand-primary/5' : 'border-[#E8E1D5] hover:border-brand-primary/40'}`}
                              data-testid={`venue-slot-${s.key}`}>
                              <div className={`text-sm font-bold ${timeSlot === s.key ? 'text-brand-primary' : 'text-text-primary'}`}>{s.label}</div>
                              <div className="text-[11px] text-text-secondary">{s.time}</div>
                            </button>
                          ))}
                        </div>
                        <p className="text-[10px] text-text-secondary mt-2 italic">
                          Venues can host two events per day — pick a slot. Availability updates live as you switch.
                        </p>
                      </div>
                    )}
                    <p className="text-xs text-text-secondary mb-2">Click a venue type to see available venues with pricing</p>
                    {[...(pricingData.venue?.pricing || [])].sort((a, b) => (Number(a.defaultPrice) || 0) - (Number(b.defaultPrice) || 0)).map((v, idx) => {
                      const inCart = cart.find(c => c.id.startsWith(`ven-prov-${v.name}-`));
                      const providerCount = (SAMPLE_VENUE_PROVIDERS[v.name] || []).length;
                      return (
                        <div key={idx}
                          className={`flex items-center justify-between p-3 border-2 rounded transition-all cursor-pointer hover:border-brand-primary/40 ${inCart ? 'border-green-500 bg-green-50/30' : 'border-[#E8E1D5]'}`}
                          onClick={() => openVenuePanel(v)}
                          data-testid={`venue-type-${idx}`}>
                          <div className="flex-1">
                            <span className="font-medium text-sm">{v.name}</span>
                            <span className="text-brand-primary font-bold ml-2">from ₹{Number(v.defaultPrice).toLocaleString('en-IN')}</span>
                            {providerCount > 0 && (
                              <span className="text-[10px] text-text-secondary ml-2">{providerCount} venues</span>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            {inCart && <Check size={14} className="text-green-500" weight="bold" />}
                            <CaretRight size={16} className="text-brand-primary" />
                          </div>
                        </div>
                      );
                    })}
                  </CardContent>
                )}
              </Card>
              </div>
              )}

              {/* PROFESSIONALS */}
              {EVENT_SECTIONS[eventType]?.professional && isSegmentEnabled('professional') && (
              <div style={{ order: orderOf('professional') }}>
              <SegmentBanner segKey="professional" label="Professionals" />
              <Card className="bg-white border-[#E8E1D5]" data-testid="pricing-professional">
                <CardHeader className="cursor-pointer" onClick={() => toggleSection('professional')}>
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-heading text-xl flex items-center gap-2">
                      <Users size={20} className="text-brand-primary" /> {t('pricing.professionals')}
                    </CardTitle>
                    {expandedSections.professional ? <CaretUp size={18} /> : <CaretDown size={18} />}
                  </div>
                </CardHeader>
                {expandedSections.professional && (
                  <CardContent className="space-y-3">
                    <p className="text-xs text-text-secondary mb-2">Click a professional type to see available providers</p>
                    {(() => {
                      // Priest/Purohit only appears for wedding events (marriage/engagement).
                      // Filter further happens per-tradition when the side panel opens.
                      const PRIEST_EVENTS = ['marriage', 'engagement'];
                      // Biryani Specialist is non-veg — hide on Brahmin Wedding (pure-veg)
                      const isBrahmin = eventType === 'marriage' && marriageType === 'brahmin';
                      const allTypes = pricingData.professional?.types || [];
                      const filtered = allTypes.filter(p => {
                        const nm = (p.name || '').toLowerCase();
                        const isPriest = nm.includes('priest') || nm.includes('purohit');
                        if (isPriest && !PRIEST_EVENTS.includes(eventType)) return false;
                        // Return Gift moved to its own dedicated section above Flowers
                        if (nm.includes('return gift') || nm.includes('muhurtham')) return false;
                        // Biryani Specialist: skip on Brahmin Wedding (pure-veg tradition)
                        if (nm.includes('biryani') && isBrahmin) return false;
                        return true;
                      });
                      return [...filtered].sort((a, b) => (Number(a.defaultPrice) || 0) - (Number(b.defaultPrice) || 0));
                    })().map((p, idx) => {
                      const isStaffType = ['Server / Waiter', 'Catering Team'].includes(p.name);
                      const staffCartId = `staff-${p.name.replace(/ /g, '_')}`;
                      const staffInCart = isStaffType ? cart.find(c => c.id === staffCartId) : null;
                      const inCart = isStaffType ? staffInCart : cart.find(c => c.id.startsWith(`pro-prov-${p.name}-`));
                      const providerCount = (() => {
                        const slug = slugForProName(p.name);
                        if (isDualPricingPro(p.name)) {
                          let list = (realDualPricedPros || {})[slug] || [];
                          if (slug === 'priest') {
                            // Match openProPanel's tradition filter so the count reflects
                            // what the customer will actually see when they open the panel.
                            const tmap = { hindu: 'hindu', brahmin: 'hindu', christian: 'christian', muslim: 'muslim' };
                            const want = tmap[marriageType];
                            list = want ? list.filter(x => x.priest_tradition === want) : [];
                          }
                          return list.length
                            || (p.name === 'Decoration Team' ? (realDecorationTeams || []).length : 0);
                        }
                        return (SAMPLE_PRO_PROVIDERS[p.name] || []).length;
                      })();

                      if (isStaffType) {
                        const minPersons = 5;
                        const isServer = p.name === 'Server / Waiter';
                        const isCateringTeam = p.name === 'Catering Team';

                        // Catering Team: 2-axis (guests × menu items) pricing
                        // matrix flow. Live quotes from /api/catering-teams/quote
                        // — replaces the legacy per-person staff selector.
                        if (isCateringTeam) {
                          return (
                            <div key={idx}
                              className="p-3 border-2 rounded transition-all border-[#E8E1D5]"
                              data-testid={`pro-staff-${idx}`}>
                              <div className="flex items-center justify-between mb-3">
                                <span className="font-medium text-sm">{p.name}</span>
                                <span className="text-[11px] text-text-secondary italic">
                                  Pick a team — prices are all-in
                                </span>
                              </div>
                              <CateringTeamPicker
                                initialGuests={guests}
                                city={eventCity}
                                cart={cart}
                                addToCart={addToCart}
                                removeFromCart={removeFromCart}
                              />
                            </div>
                          );
                        }

                        const serverPackages = isServer ? (pricingData?.professional?.server_packages || []) : [];
                        const staffTimingKey = `staffTiming_${p.name.replace(/ /g, '_')}`;
                        const selectedTiming = selectedFoodTiming[staffTimingKey] || (serverPackages.length > 0 ? serverPackages[0].name : '');
                        const selectedPkg = serverPackages.find(sp => sp.name === selectedTiming);
                        const pricePerPerson = isServer && selectedPkg ? selectedPkg.defaultPrice : (p.defaultPrice || 800);
                        const currentQty = staffInCart?.qty || 0;
                        return (
                          <div key={idx}
                            className={`p-3 border-2 rounded transition-all ${inCart ? 'border-green-500 bg-green-50/30' : 'border-[#E8E1D5]'}`}
                            data-testid={`pro-staff-${idx}`}>
                            <div className="flex items-center justify-between mb-2">
                              <div>
                                <span className="font-medium text-sm">{p.name}</span>
                                <span className="text-brand-primary font-bold ml-2">₹{Number(pricePerPerson).toLocaleString('en-IN')}/person</span>
                              </div>
                              {inCart && <Check size={14} className="text-green-500" weight="bold" />}
                            </div>

                            {/* Food Timing Selection for Servers */}
                            {isServer && serverPackages.length > 0 && (
                              <div className="mb-3">
                                <span className="text-xs font-bold text-text-secondary uppercase tracking-wider block mb-1.5">Select Food Timing</span>
                                <div className="flex flex-wrap gap-2">
                                  {serverPackages.map((sp, spIdx) => (
                                    <button key={spIdx} type="button"
                                      className={`px-3 py-1.5 text-xs font-bold border-2 transition-all ${selectedTiming === sp.name ? 'border-brand-primary bg-brand-primary/10 text-brand-primary' : 'border-[#E8E1D5] text-text-secondary hover:border-brand-primary/30'}`}
                                      onClick={() => {
                                        setSelectedFoodTiming(prev => ({ ...prev, [staffTimingKey]: sp.name }));
                                        if (staffInCart) {
                                          setCart(cart.map(c => c.id === staffCartId ? { ...c, name: `${p.name} - ${sp.name} (${currentQty} persons)`, price: sp.defaultPrice * currentQty } : c));
                                        }
                                      }}
                                      data-testid={`staff-timing-${idx}-${spIdx}`}>
                                      {sp.name} — ₹{Number(sp.defaultPrice).toLocaleString('en-IN')}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}

                            <div className="flex items-center gap-3">
                              <div className="flex items-center gap-1">
                                <Button type="button" size="sm" variant="outline" className="w-7 h-7 p-0 rounded-none border-[#E8E1D5]"
                                  disabled={currentQty <= 0}
                                  onClick={() => {
                                    if (currentQty <= minPersons) {
                                      removeFromCart(staffCartId);
                                    } else {
                                      setCart(cart.map(c => c.id === staffCartId ? { ...c, qty: c.qty - 1, price: pricePerPerson * (c.qty - 1) } : c));
                                    }
                                  }}>
                                  <Minus size={12} />
                                </Button>
                                <input type="number" value={currentQty || ''}
                                  min={minPersons}
                                  placeholder={`Min ${minPersons}`}
                                  onChange={(e) => {
                                    const val = parseInt(e.target.value) || 0;
                                    if (val === 0) { removeFromCart(staffCartId); return; }
                                    if (val < minPersons) { toast.error(`Minimum ${minPersons} persons required for ${p.name}`); return; }
                                    if (staffInCart) {
                                      setCart(cart.map(c => c.id === staffCartId ? { ...c, qty: val, name: `${p.name}${isServer && selectedTiming ? ' - ' + selectedTiming : ''} (${val} persons)`, price: pricePerPerson * val } : c));
                                    } else {
                                      addToCart({ id: staffCartId, name: `${p.name}${isServer && selectedTiming ? ' - ' + selectedTiming : ''} (${val} persons)`, price: pricePerPerson * val, category: 'Professional Staff', qty: val });
                                    }
                                  }}
                                  className="w-20 h-7 text-center text-sm border border-[#E8E1D5] rounded-none"
                                  data-testid={`staff-qty-${idx}`} />
                                <Button type="button" size="sm" variant="outline" className="w-7 h-7 p-0 rounded-none border-[#E8E1D5]"
                                  onClick={() => {
                                    const newQty = Math.max(minPersons, currentQty + 1);
                                    if (staffInCart) {
                                      setCart(cart.map(c => c.id === staffCartId ? { ...c, qty: newQty, name: `${p.name}${isServer && selectedTiming ? ' - ' + selectedTiming : ''} (${newQty} persons)`, price: pricePerPerson * newQty } : c));
                                    } else {
                                      addToCart({ id: staffCartId, name: `${p.name}${isServer && selectedTiming ? ' - ' + selectedTiming : ''} (${newQty} persons)`, price: pricePerPerson * newQty, category: 'Professional Staff', qty: newQty });
                                    }
                                  }}>
                                  <Plus size={12} />
                                </Button>
                              </div>
                              <span className="text-xs text-text-secondary">Min {minPersons} persons</span>
                              {staffInCart && (
                                <span className="text-sm font-bold text-brand-primary ml-auto">
                                  ₹{(pricePerPerson * currentQty).toLocaleString('en-IN')}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div key={idx}
                          className={`flex items-center justify-between p-3 border-2 rounded transition-all cursor-pointer hover:border-brand-primary/40 ${inCart ? 'border-green-500 bg-green-50/30' : 'border-[#E8E1D5]'}`}
                          onClick={() => openProPanel(p)}
                          data-testid={`pro-type-${idx}`}>
                          <div className="flex-1">
                            <span className="font-medium text-sm">{p.name}</span>
                            {(() => {
                              if (isDualPricingPro(p.name)) {
                                const slug = slugForProName(p.name);
                                const teams = (realDualPricedPros || {})[slug]
                                  || (p.name === 'Decoration Team' ? realDecorationTeams : [])
                                  || [];
                                if (teams.length === 0) {
                                  return <span className="text-text-secondary text-[11px] ml-2 italic">No pros listed yet for this event</span>;
                                }
                                const minP = Math.min(...teams.map(t => Number(t.effective_price) || 0));
                                return <span className="text-brand-primary font-bold ml-2">from ₹{minP.toLocaleString('en-IN')}</span>;
                              }
                              return <span className="text-brand-primary font-bold ml-2">from ₹{Number(p.defaultPrice).toLocaleString('en-IN')}</span>;
                            })()}
                            {providerCount > 0 && (
                              <span className="text-[10px] text-text-secondary ml-2">{providerCount} providers</span>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            {inCart && <Check size={14} className="text-green-500" weight="bold" />}
                            <CaretRight size={16} className="text-brand-primary" />
                          </div>
                        </div>
                      );
                    })}
                  </CardContent>
                )}
              </Card>
              </div>
              )}

              {/* RETURN GIFT / MUHURTHAM PAI — listed above Flowers & Pooja Items */}
              {EVENT_SECTIONS[eventType]?.flowers && isSegmentEnabled('flowers') && (
              <div style={{ order: orderOf('return_gift') }}>
              <SegmentBanner segKey="flowers" label="Flowers & Pooja" />
              <Card className="bg-white border-[#E8E1D5]" data-testid="pricing-return-gift">
                <CardHeader className="cursor-pointer" onClick={() => toggleSection('return_gift')}>
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-heading text-xl flex items-center gap-2">
                      <Gift size={20} className="text-brand-primary" /> Return Gift / Muhurtham Pai
                    </CardTitle>
                    {expandedSections.return_gift ? <CaretUp size={18} /> : <CaretDown size={18} />}
                  </div>
                </CardHeader>
                {expandedSections.return_gift && (
                  <CardContent className="space-y-2">
                    <p className="text-xs text-text-secondary mb-2">Muhurtham gifts, sweets, return packs for guests.</p>
                    {realReturnGiftData.shops.length > 0 && (
                      <div className="mb-3 space-y-2" data-testid="real-return-gift-shops">
                        <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-emerald-700 mb-1 flex items-center gap-1.5">
                          <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" />
                          {realReturnGiftData.shops.length} verified shop{realReturnGiftData.shops.length === 1 ? '' : 's'} in {eventCity || 'your city'}
                        </div>
                        {realReturnGiftData.shops.map((shop) => {
                          const isOpen = expandedReturnGiftShop === shop.id;
                          const inCartFromShop = cart.filter(c => (c.shop_id === shop.id) && c.id?.startsWith('rg-real-'));
                          const shopTotal = inCartFromShop.reduce((s, c) => s + (c.price || 0) * (c.qty || 1), 0);
                          return (
                            <div key={shop.id}
                              className={`border-2 rounded-lg overflow-hidden transition-all ${inCartFromShop.length > 0 ? 'border-green-500 ring-1 ring-green-200' : 'border-emerald-200'}`}
                              data-testid={`real-return-gift-shop-${shop.id}`}>
                              <div className="p-3">
                                <div className="flex items-start gap-3">
                                  <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center flex-shrink-0">
                                    <Gift size={22} className="text-emerald-600" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="font-medium text-sm truncate flex items-center gap-2 flex-wrap">
                                      {shop.verified && (
                                        <span className="text-[9px] tracking-wider uppercase font-bold bg-emerald-600 text-white px-1.5 py-0.5 rounded">VERIFIED</span>
                                      )}
                                      <span className="truncate">{shop.business_name}</span>
                                    </div>
                                    {shop.rating > 0 && (
                                      <div className="flex items-center gap-1 mt-0.5">
                                        {Array.from({ length: 5 }).map((_, i) => (
                                          <Star key={i} size={10} weight={i < Math.floor(shop.rating) ? 'fill' : 'regular'}
                                            className={i < shop.rating ? 'text-amber-500' : 'text-gray-300'} />
                                        ))}
                                        <span className="text-[10px] text-text-secondary ml-1">{shop.rating} ({shop.reviews || 0})</span>
                                      </div>
                                    )}
                                    <div className="flex items-center gap-2 mt-0.5 text-[11px] text-text-secondary">
                                      <span className="flex items-center gap-0.5"><MapPin size={9} />{shop.area ? `${shop.area}, ${shop.city}` : shop.city}</span>
                                      <span>•</span>
                                      <span>{shop.item_count} item{shop.item_count === 1 ? '' : 's'}</span>
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center justify-between mt-2.5 pt-2.5 border-t border-[#E8E1D5]">
                                  <div>
                                    {inCartFromShop.length > 0 ? (
                                      <div className="text-xs text-text-secondary">In your estimate <span className="font-bold text-brand-primary">₹{shopTotal.toLocaleString('en-IN')}</span></div>
                                    ) : (
                                      <div className="text-xs text-text-secondary">Tap <span className="font-bold">Menu</span> to view items & prices</div>
                                    )}
                                  </div>
                                  <Button type="button" size="sm" variant="outline"
                                    className={`rounded-none border-[#E8E1D5] text-text-secondary hover:text-emerald-700 hover:border-emerald-500 ${isOpen ? 'bg-emerald-50 border-emerald-500 text-emerald-700' : ''}`}
                                    onClick={() => setExpandedReturnGiftShop(isOpen ? null : shop.id)}
                                    data-testid={`real-return-gift-shop-toggle-${shop.id}`}>
                                    <Gift size={14} className="mr-1" /> Menu ({shop.item_count})
                                  </Button>
                                </div>
                              </div>

                              {/* Expandable menu — the shop's items as a list with photo + price + add controls. */}
                              {isOpen && (
                                <div className="border-t-2 border-[#E8E1D5] bg-sand-50/30 p-3 space-y-1.5" data-testid={`real-return-gift-shop-menu-${shop.id}`}>
                                  {shop.items.map(rg => {
                                    const cartId = `rg-real-${rg.id}`;
                                    const inCart = cart.find(c => c.id === cartId);
                                    const finalPrice = markup(rg.price, 'vendor');
                                    return (
                                      <div key={rg.id}
                                        className={`flex items-center gap-3 p-2 border rounded transition-all bg-white ${inCart ? 'border-green-500' : 'border-[#E8E1D5]'}`}
                                        data-testid={`real-return-gift-item-${rg.id}`}>
                                        {rg.image ? (
                                          <img src={rg.image.startsWith('http') ? rg.image : `${BACKEND_URL}${rg.image}`}
                                            alt={rg.name}
                                            onClick={() => setProductPopup({ shop, item: rg, finalPrice, cartId, cartPrefix: 'rg-real-', category: 'Return Gift / Muhurtham Pai', accent: 'emerald' })}
                                            className="w-14 h-14 object-cover rounded flex-shrink-0 cursor-zoom-in" />
                                        ) : (
                                          <div className="w-14 h-14 bg-emerald-50 rounded flex items-center justify-center flex-shrink-0"><Gift size={20} className="text-emerald-600" /></div>
                                        )}
                                        <div className="flex-1 min-w-0">
                                          <div className="text-sm font-medium leading-tight">{rg.name}</div>
                                          {rg.category_tag && <div className="text-[10px] uppercase tracking-wider text-emerald-700/70 font-bold">{rg.category_tag}</div>}
                                          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                            <span className="text-brand-primary font-bold text-sm">₹{finalPrice.toLocaleString('en-IN')}</span>
                                            {rg.unit && <span className="text-[10px] text-text-secondary">({rg.unit})</span>}
                                          </div>
                                        </div>
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                          {inCart ? (
                                            <div className="flex items-center gap-1">
                                              <Button type="button" size="sm" variant="outline" className="w-7 h-7 p-0 rounded-none border-[#E8E1D5]"
                                                onClick={() => {
                                                  if (inCart.qty > 1) {
                                                    setCart(cart.map(c => c.id === cartId ? { ...c, qty: c.qty - 1, price: finalPrice * (c.qty - 1) } : c));
                                                  } else { removeFromCart(cartId); }
                                                }}>
                                                <Minus size={12} />
                                              </Button>
                                              <span className="w-6 text-center text-sm font-bold">{inCart.qty || 1}</span>
                                              <Button type="button" size="sm" variant="outline" className="w-7 h-7 p-0 rounded-none border-[#E8E1D5]"
                                                onClick={() => {
                                                  setCart(cart.map(c => c.id === cartId ? { ...c, qty: (c.qty || 1) + 1, price: finalPrice * ((c.qty || 1) + 1) } : c));
                                                }}>
                                                <Plus size={12} />
                                              </Button>
                                              <button type="button" onClick={() => removeFromCart(cartId)} className="text-red-400 hover:text-red-600 ml-1"><X size={14} /></button>
                                            </div>
                                          ) : (
                                            <Button type="button" size="sm" className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-sand-50 text-xs h-7"
                                              onClick={() => { addToCart({ id: cartId, name: `${rg.name} (${shop.business_name})`, price: finalPrice, category: 'Return Gift / Muhurtham Pai', qty: 1, vendor_id: shop.id, shop_id: shop.id }); }}
                                              data-testid={`real-return-gift-add-${rg.id}`}>
                                              <Plus size={12} className="mr-1" /> Add
                                            </Button>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {realReturnGiftData.shops.length === 0 && (
                      <div className="text-center py-6 border border-dashed border-[#E8E1D5] rounded bg-stone-50"
                        data-testid="return-gift-empty-state">
                        <p className="text-sm text-text-secondary">
                          No verified Return Gift / Muhurtham Pai vendors in your city yet.
                        </p>
                        <p className="text-xs text-text-secondary/70 mt-1">
                          We&apos;re onboarding shops — check back soon!
                        </p>
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>
              </div>
              )}

              {/* FLOWERS & POOJA ITEMS */}
              {EVENT_SECTIONS[eventType]?.flowers && isSegmentEnabled('flowers') && (
              <div style={{ order: orderOf('flowers') }}>
              <Card className="bg-white border-[#E8E1D5]" data-testid="pricing-flowers">
                <CardHeader className="cursor-pointer" onClick={() => toggleSection('flowers')}>
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-heading text-xl flex items-center gap-2">
                      <Flower size={20} className="text-brand-primary" /> {t('pricing.flowers')}
                    </CardTitle>
                    {expandedSections.flowers ? <CaretUp size={18} /> : <CaretDown size={18} />}
                  </div>
                </CardHeader>
                {expandedSections.flowers && (
                  <CardContent className="space-y-4">
                    <p className="text-xs text-text-secondary mb-2">Select flower items and set quantity.</p>
                    {realFlowerData.shops.length > 0 && (
                      <div className="space-y-2" data-testid="real-flower-shops">
                        <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-emerald-700 mb-1 flex items-center gap-1.5">
                          <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" />
                          {realFlowerData.shops.length} verified flower shop{realFlowerData.shops.length === 1 ? '' : 's'} in {eventCity || 'your city'}
                        </div>
                        {realFlowerData.shops.map((shop) => {
                          const isOpen = expandedFlowerShop === shop.id;
                          const inCartFromShop = cart.filter(c => (c.shop_id === shop.id) && c.id?.startsWith('flower-real-'));
                          const shopTotal = inCartFromShop.reduce((s, c) => s + (c.price || 0) * (c.qty || 1), 0);
                          // Group items by category_tag for a tidy menu render.
                          const itemsByTag = {};
                          shop.items.forEach((it) => {
                            const t = it.category_tag || 'Other';
                            itemsByTag[t] = itemsByTag[t] || [];
                            itemsByTag[t].push(it);
                          });
                          return (
                            <div key={shop.id}
                              className={`border-2 rounded-lg overflow-hidden transition-all ${inCartFromShop.length > 0 ? 'border-green-500 ring-1 ring-green-200' : 'border-emerald-200'}`}
                              data-testid={`real-flower-shop-${shop.id}`}>
                              <div className="p-3">
                                <div className="flex items-start gap-3">
                                  <div className="w-12 h-12 rounded-full bg-pink-50 flex items-center justify-center flex-shrink-0">
                                    <Flower size={22} className="text-pink-600" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="font-medium text-sm truncate flex items-center gap-2 flex-wrap">
                                      {shop.verified && (
                                        <span className="text-[9px] tracking-wider uppercase font-bold bg-emerald-600 text-white px-1.5 py-0.5 rounded">VERIFIED</span>
                                      )}
                                      <span className="truncate">{shop.business_name}</span>
                                    </div>
                                    {shop.rating > 0 && (
                                      <div className="flex items-center gap-1 mt-0.5">
                                        {Array.from({ length: 5 }).map((_, i) => (
                                          <Star key={i} size={10} weight={i < Math.floor(shop.rating) ? 'fill' : 'regular'}
                                            className={i < shop.rating ? 'text-amber-500' : 'text-gray-300'} />
                                        ))}
                                        <span className="text-[10px] text-text-secondary ml-1">{shop.rating} ({shop.reviews || 0})</span>
                                      </div>
                                    )}
                                    <div className="flex items-center gap-2 mt-0.5 text-[11px] text-text-secondary">
                                      <span className="flex items-center gap-0.5"><MapPin size={9} />{shop.area ? `${shop.area}, ${shop.city}` : shop.city}</span>
                                      <span>•</span>
                                      <span>{shop.item_count} item{shop.item_count === 1 ? '' : 's'}</span>
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center justify-between mt-2.5 pt-2.5 border-t border-[#E8E1D5]">
                                  <div>
                                    {inCartFromShop.length > 0 ? (
                                      <div className="text-xs text-text-secondary">In your estimate <span className="font-bold text-brand-primary">₹{shopTotal.toLocaleString('en-IN')}</span></div>
                                    ) : (
                                      <div className="text-xs text-text-secondary">Tap <span className="font-bold">Menu</span> to view items & prices</div>
                                    )}
                                  </div>
                                  <Button type="button" size="sm" variant="outline"
                                    className={`rounded-none border-[#E8E1D5] text-text-secondary hover:text-pink-700 hover:border-pink-500 ${isOpen ? 'bg-pink-50 border-pink-500 text-pink-700' : ''}`}
                                    onClick={() => setExpandedFlowerShop(isOpen ? null : shop.id)}
                                    data-testid={`real-flower-shop-toggle-${shop.id}`}>
                                    <Flower size={14} className="mr-1" /> Menu ({shop.item_count})
                                  </Button>
                                </div>
                              </div>

                              {isOpen && (
                                <div className="border-t-2 border-[#E8E1D5] bg-sand-50/30 p-3 space-y-3" data-testid={`real-flower-shop-menu-${shop.id}`}>
                                  {Object.entries(itemsByTag).map(([tag, tagItems]) => (
                                    <div key={tag}>
                                      <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-pink-700 mb-1.5">{tag}</div>
                                      <div className="space-y-1.5">
                                        {tagItems.map(fl => {
                                          const cartId = `flower-real-${fl.id}`;
                                          const inCart = cart.find(c => c.id === cartId);
                                          const finalPrice = markup(fl.price, 'vendor');
                                          return (
                                            <div key={fl.id}
                                              className={`flex items-center gap-3 p-2 border rounded transition-all bg-white ${inCart ? 'border-green-500' : 'border-[#E8E1D5]'}`}
                                              data-testid={`real-flower-item-${fl.id}`}>
                                              {fl.image ? (
                                                <img src={fl.image.startsWith('http') ? fl.image : `${BACKEND_URL}${fl.image}`}
                                                  alt={fl.name}
                                                  onClick={() => setProductPopup({ shop, item: fl, finalPrice, cartId, cartPrefix: 'flower-real-', category: 'Flowers & Pooja Items', accent: 'pink' })}
                                                  className="w-14 h-14 object-cover rounded flex-shrink-0 cursor-zoom-in" />
                                              ) : (
                                                <div className="w-14 h-14 bg-pink-50 rounded flex items-center justify-center flex-shrink-0"><Flower size={20} className="text-pink-600" /></div>
                                              )}
                                              <div className="flex-1 min-w-0">
                                                <div className="text-sm font-medium leading-tight">{fl.name}</div>
                                                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                                  <span className="text-brand-primary font-bold text-sm">₹{finalPrice.toLocaleString('en-IN')}</span>
                                                  {fl.unit && <span className="text-[10px] text-text-secondary">({fl.unit})</span>}
                                                </div>
                                              </div>
                                              <div className="flex items-center gap-2 flex-shrink-0">
                                                {inCart ? (
                                                  <div className="flex items-center gap-1">
                                                    <Button type="button" size="sm" variant="outline" className="w-7 h-7 p-0 rounded-none border-[#E8E1D5]"
                                                      onClick={() => {
                                                        if (inCart.qty > 1) {
                                                          setCart(cart.map(c => c.id === cartId ? { ...c, qty: c.qty - 1, price: finalPrice * (c.qty - 1) } : c));
                                                        } else { removeFromCart(cartId); }
                                                      }}>
                                                      <Minus size={12} />
                                                    </Button>
                                                    <span className="w-6 text-center text-sm font-bold">{inCart.qty || 1}</span>
                                                    <Button type="button" size="sm" variant="outline" className="w-7 h-7 p-0 rounded-none border-[#E8E1D5]"
                                                      onClick={() => {
                                                        setCart(cart.map(c => c.id === cartId ? { ...c, qty: (c.qty || 1) + 1, price: finalPrice * ((c.qty || 1) + 1) } : c));
                                                      }}>
                                                      <Plus size={12} />
                                                    </Button>
                                                    <button type="button" onClick={() => removeFromCart(cartId)} className="text-red-400 hover:text-red-600 ml-1"><X size={14} /></button>
                                                  </div>
                                                ) : (
                                                  <Button type="button" size="sm" className="rounded-none bg-pink-600 hover:bg-pink-700 text-sand-50 text-xs h-7"
                                                    onClick={() => { addToCart({ id: cartId, name: `${fl.name} (${shop.business_name})`, price: finalPrice, category: 'Flowers & Pooja Items', qty: 1, vendor_id: shop.id, shop_id: shop.id }); }}
                                                    data-testid={`real-flower-add-${fl.id}`}>
                                                    <Plus size={12} className="mr-1" /> Add
                                                  </Button>
                                                )}
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                        <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-text-secondary/70 mt-3 mb-1.5"></div>
                      </div>
                    )}
                    {realFlowerData.shops.length === 0 && (
                      <div className="text-center py-6 border border-dashed border-[#E8E1D5] rounded bg-stone-50"
                        data-testid="flowers-empty-state">
                        <p className="text-sm text-text-secondary">
                          No verified Flowers &amp; Pooja vendors in your city yet.
                        </p>
                        <p className="text-xs text-text-secondary/70 mt-1">
                          We&apos;re onboarding shops — check back soon!
                        </p>
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>
              </div>
              )}

              {/* EQUIPMENT RENTALS */}
              {EVENT_SECTIONS[eventType]?.equipment && isSegmentEnabled('equipment') && (
              <div style={{ order: orderOf('equipment') }}>
              <SegmentBanner segKey="equipment" label="Equipment Rental" />
              <Card className="bg-white border-[#E8E1D5]" data-testid="pricing-equipment">
                <CardHeader className="cursor-pointer" onClick={() => toggleSection('equipment')}>
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-heading text-xl flex items-center gap-2">
                      <Briefcase size={20} className="text-brand-primary" /> {t('pricing.equipment')}
                    </CardTitle>
                    {expandedSections.equipment ? <CaretUp size={18} /> : <CaretDown size={18} />}
                  </div>
                </CardHeader>
                {expandedSections.equipment && (
                  <CardContent className="space-y-4">
                    <p className="text-xs text-text-secondary mb-2">Select equipment items and set quantity. Prices are per unit/day.</p>
                    {EQUIPMENT_CATEGORIES.map(cat => {
                      const catItems = EQUIPMENT_RENTAL_ITEMS.filter(e => e.category === cat);
                      const catColors = {
                        'Seating': 'bg-blue-600', 'Tent & Shelter': 'bg-green-600', 'Power & Electrical': 'bg-amber-500',
                        'Sound & AV': 'bg-purple-600', 'Dining': 'bg-pink-500', 'Comfort': 'bg-teal-600', 'Misc': 'bg-gray-600',
                      };
                      return (
                        <div key={cat}>
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`text-xs px-2 py-0.5 text-white font-bold uppercase tracking-wider ${catColors[cat] || 'bg-gray-600'}`}>{cat}</span>
                            <span className="text-xs text-text-secondary">{catItems.length} items</span>
                          </div>
                          <div className="space-y-1.5">
                            {catItems.map(eq => {
                              const inCart = cart.find(c => c.id === `equip-${eq.id}`);
                              return (
                                <div key={eq.id}
                                  className={`flex items-center gap-3 p-2.5 border rounded transition-all ${inCart ? 'border-green-500 bg-green-50/30' : 'border-[#E8E1D5] hover:border-brand-primary/30'}`}
                                  data-testid={`equip-item-${eq.id}`}>
                                  <div className="flex-1 min-w-0">
                                    <span className="text-sm font-medium">{eq.name}</span>
                                    <span className="text-brand-primary font-bold text-sm ml-2">₹{markup(eq.price, 'vendor').toLocaleString('en-IN')}</span>
                                    <span className="text-[10px] text-text-secondary ml-1">({eq.unit})</span>
                                  </div>
                                  <div className="flex items-center gap-2 flex-shrink-0">
                                    {inCart ? (
                                      <div className="flex items-center gap-1">
                                        <Button type="button" size="sm" variant="outline" className="w-7 h-7 p-0 rounded-none border-[#E8E1D5]"
                                          onClick={() => {
                                            const item = cart.find(c => c.id === `equip-${eq.id}`);
                                            if (item && item.qty > 1) {
                                              setCart(cart.map(c => c.id === `equip-${eq.id}` ? { ...c, qty: c.qty - 1, price: eq.price * (c.qty - 1) } : c));
                                            } else { removeFromCart(`equip-${eq.id}`); }
                                          }}>
                                          <Minus size={12} />
                                        </Button>
                                        <span className="w-6 text-center text-sm font-bold">{inCart.qty || 1}</span>
                                        <Button type="button" size="sm" variant="outline" className="w-7 h-7 p-0 rounded-none border-[#E8E1D5]"
                                          onClick={() => {
                                            setCart(cart.map(c => c.id === `equip-${eq.id}` ? { ...c, qty: (c.qty || 1) + 1, price: eq.price * ((c.qty || 1) + 1) } : c));
                                          }}>
                                          <Plus size={12} />
                                        </Button>
                                        <button type="button" onClick={() => removeFromCart(`equip-${eq.id}`)} className="text-red-400 hover:text-red-600 ml-1"><X size={14} /></button>
                                      </div>
                                    ) : (
                                      <Button type="button" size="sm" className="rounded-none bg-brand-primary hover:bg-[#A84A38] text-sand-50 text-xs h-7"
                                        onClick={() => { addToCart({ id: `equip-${eq.id}`, name: eq.name, price: eq.price, category: 'Equipment Rental', qty: 1 }); }}
                                        data-testid={`equip-add-${eq.id}`}>
                                        <Plus size={12} className="mr-1" /> Add
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </CardContent>
                )}
              </Card>
              </div>
              )}
            </div>

            {/* Right: Cart / Estimate Summary */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-4">
                <Card className="bg-white border-[#E8E1D5]" data-testid="pricing-cart">
                  <CardHeader>
                    <CardTitle className="font-heading text-xl flex items-center gap-2">
                      <ShoppingCart size={20} /> {t('pricing.your_estimate')}
                      {editingPackageId && <span className="text-xs text-brand-primary font-normal">(Editing)</span>}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {cart.length === 0 ? (
                      <p className="text-sm text-text-secondary text-center py-4">Add items from the left to build your estimate</p>
                    ) : (
                      <div className="space-y-3">
                        {(() => {
                          const { vendors: vItems, professionals: pItems } = splitCartByLane(cart);
                          const renderRow = (item) => (
                            <div key={item.id} className="flex items-center justify-between text-sm border-b border-[#E8E1D5] pb-2">
                              <div className="flex-1 min-w-0">
                                <div className="font-medium truncate">{item.name}</div>
                                <div className="text-xs text-text-secondary">{item.category}</div>
                              </div>
                              <div className="flex items-center gap-2 ml-2">
                                <span className="text-brand-primary font-bold whitespace-nowrap">₹{Number(itemDisplayPrice(item)).toLocaleString('en-IN')}</span>
                                <button type="button" onClick={() => removeFromCart(item.id)} className="text-red-400 hover:text-red-600"><X size={14} /></button>
                              </div>
                            </div>
                          );
                          const subtotal = (arr) => arr.reduce((s, i) => s + Number(itemDisplayPrice(i) || 0), 0);
                          return (
                            <>
                              {vItems.length > 0 && (
                                <div data-testid="cart-lane-vendors" className="space-y-2">
                                  <div className="flex items-center justify-between bg-sand-100 border-l-2 border-brand-primary px-2 py-1">
                                    <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-text-primary">
                                      Vendors <span className="text-text-secondary font-mono ml-1">({vItems.length})</span>
                                    </span>
                                    <span className="text-xs font-bold text-text-secondary" data-testid="cart-lane-vendors-subtotal">
                                      ₹{subtotal(vItems).toLocaleString('en-IN')}
                                    </span>
                                  </div>
                                  {vItems.map(renderRow)}
                                </div>
                              )}

                              {pItems.length > 0 && (
                                <div data-testid="cart-lane-professionals" className="space-y-2">
                                  <div className="flex items-center justify-between bg-sand-100 border-l-2 border-brand-secondary px-2 py-1">
                                    <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-text-primary">
                                      Professionals <span className="text-text-secondary font-mono ml-1">({pItems.length})</span>
                                    </span>
                                    <span className="text-xs font-bold text-text-secondary" data-testid="cart-lane-professionals-subtotal">
                                      ₹{subtotal(pItems).toLocaleString('en-IN')}
                                    </span>
                                  </div>
                                  {pItems.map(renderRow)}
                                </div>
                              )}
                            </>
                          );
                        })()}

                        <div className="flex items-center justify-between pt-3 border-t-2 border-brand-primary">
                          <span className="font-heading text-lg font-bold">{t('pricing.total')}</span>
                          <span className="font-heading text-2xl font-bold text-brand-primary">₹{cartTotal.toLocaleString('en-IN')}</span>
                        </div>
                        <p className="text-[10px] text-text-secondary text-right -mt-1" data-testid="incl-platform-fees">
                          {t('pricing.incl_platform_fees')}
                        </p>

                        {/* Save as Package button */}
                        <Button onClick={saveAsPackage} disabled={loadingPackages}
                          className="w-full bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none mt-2"
                          data-testid="save-package-btn">
                          <FloppyDisk size={16} className="mr-2" />
                          {loadingPackages ? '...' : editingPackageId ? t('common.save') : savedPackages.length === 0 ? t('pricing.save_package') + ' 1' : savedPackages.length === 1 ? t('pricing.save_package') + ' 2' : 'Max 2'}
                        </Button>
                        {!editingPackageId && savedPackages.length >= 2 && (
                          <p className="text-[10px] text-red-500 text-center">Delete a package first to create a new one</p>
                        )}
                        {editingPackageId && (
                          <button type="button" onClick={() => { setEditingPackageId(null); setCart([]); }}
                            className="w-full text-xs text-text-secondary text-center hover:text-brand-primary mt-1" data-testid="cancel-edit-btn">
                            Cancel editing — start fresh
                          </button>
                        )}
                        <p className="text-[10px] text-text-secondary text-center mt-2">* Prices are estimates. Actual costs may vary.</p>

                        {/* Share my estimate */}
                        <div className="pt-3 mt-2 border-t border-[#E8E1D5]">
                          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-text-secondary mb-2">
                            Share with family / partner
                          </div>
                          <ShareButtons
                            text={`I built a ${eventType ? eventType + ' ' : ''}estimate of ₹${cartTotal.toLocaleString('en-IN')} on BeeWeddy. What do you think?`}
                            hashtags="BeeWeddy"
                            compact
                          />
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Saved Packages */}
                {savedPackages.length > 0 && (
                  <Card className="bg-white border-[#E8E1D5]" data-testid="saved-packages">
                    <CardHeader className="pb-2">
                      <CardTitle className="font-heading text-lg flex items-center gap-2">
                        <FloppyDisk size={18} /> {t('pricing.saved_packages')}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {savedPackages.map((pkg) => (
                        <div key={pkg.id} className={`border-2 rounded p-3 transition-all ${editingPackageId === pkg.id ? 'border-brand-primary bg-brand-primary/5' : 'border-[#E8E1D5]'}`}
                          data-testid={`saved-pkg-${pkg.id}`}>
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-heading font-bold text-sm">{pkg.package_name}</span>
                            <span className="text-brand-primary font-bold text-sm">₹{Number(pkg.total).toLocaleString('en-IN')}</span>
                          </div>
                          <div className="text-xs text-text-secondary mb-2">{(pkg.items || []).length} items</div>
                          <div className="flex gap-2">
                            <Button type="button" size="sm" variant="outline"
                              className="rounded-none border-[#E8E1D5] flex-1 text-xs"
                              onClick={() => loadPackageIntoCart(pkg)}
                              data-testid={`edit-pkg-${pkg.id}`}>
                              <PencilSimple size={12} className="mr-1" /> Edit
                            </Button>
                            <Button type="button" size="sm" variant="outline"
                              className="rounded-none border-red-200 text-red-500 hover:bg-red-50 text-xs"
                              onClick={() => deletePackage(pkg.id)}
                              data-testid={`delete-pkg-${pkg.id}`}>
                              <Trash size={12} className="mr-1" /> Delete
                            </Button>
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Side Panel Overlay */}
      <AnimatePresence>
        {sidePanel.open && (
          <div className="fixed inset-0 z-[60] flex" data-testid="side-panel-overlay">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="flex-1 bg-black/30" onClick={closeSidePanel} />
            <motion.div initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="w-full max-w-md bg-white h-full overflow-y-auto shadow-2xl flex flex-col" data-testid="side-panel">
              {/* Panel Header */}
              <div className="sticky top-0 bg-white border-b border-[#E8E1D5] px-5 py-4 flex items-center justify-between z-10">
                <h3 className="font-heading text-lg font-medium truncate pr-2">{sidePanel.title}</h3>
                <button type="button" onClick={closeSidePanel} className="text-text-secondary hover:text-text-primary p-1" data-testid="close-side-panel"><X size={20} /></button>
              </div>

              {/* Panel Content */}
              <div className="flex-1 overflow-y-auto p-5 space-y-3">

                {/* Photography Providers */}
                {sidePanel.type === 'photography' && (() => {
                  const providers = sidePanel.data?.providers || [];
                  const items = sidePanel.data?.items || [];
                  const tierData = sidePanel.data?.tier;
                  // Group equipment items for the info section
                  const equipGroups = {};
                  items.forEach(i => {
                    if (!equipGroups[i.category]) equipGroups[i.category] = [];
                    equipGroups[i.category].push(i);
                  });
                  return (
                    <>
                      <div className="mb-3">
                        <span className={`text-xs px-2 py-0.5 text-white font-bold uppercase tracking-wider ${tierData?.color}`}>{tierData?.label}</span>
                        <span className="text-xs text-text-secondary ml-2">{providers.length} service providers</span>
                      </div>

                      {/* Real registered photographers with galleries */}
                      {realPhotographers.length > 0 && (
                        <div className="mb-4" data-testid="real-photographers-block">
                          <div className="text-xs tracking-[0.15em] uppercase font-bold text-text-secondary mb-2 flex items-center gap-2">
                            <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                            Verified Photographers ({realPhotographers.length})
                          </div>
                          <div className="space-y-3">
                            {realPhotographers.map((rp) => {
                              const rpCartId = `photographer-real-${rp.id}`;
                              const rpInCart = cart.find(c => c.id === rpCartId);
                              // Find vendor's own scoped package matching this tier + event + sub-type
                              const ownPackages = Array.isArray(rp.photo_packages) ? rp.photo_packages : [];
                              const tierKey = tierData?.key;
                              const ownMatch = ownPackages.find(p => {
                                if (p.tier !== tierKey) return false;
                                const pevent = p.event_type || 'marriage';
                                if (eventType && pevent !== eventType) return false;
                                if (eventType === 'marriage' && marriageType && p.event_subtype && p.event_subtype !== marriageType) return false;
                                return true;
                              });
                              const rpPrice = ownMatch?.price || tierData?.basePrice || 50000;
                              const hasOwnPkg = !!ownMatch;
                              // Prefer the package's own work_images (per-package gallery)
                              // and fall back to the vendor-level images[] when none.
                              const pkgWorks = Array.isArray(ownMatch?.work_images) ? ownMatch.work_images : [];
                              const rpImages = pkgWorks.length > 0
                                ? pkgWorks
                                : (Array.isArray(rp.images) ? rp.images : []);
                              const openWorksLightbox = (initialIdx = 0) => {
                                if (rpImages.length === 0) return;
                                setLightbox({
                                  open: true,
                                  photos: rpImages,
                                  initialIndex: initialIdx,
                                  title: `${rp.business_name || rp.name}${ownMatch?.name ? ` — ${ownMatch.name}` : ''}`,
                                });
                              };
                              return (
                                <div key={rp.id}
                                  className={`border-2 rounded-lg overflow-hidden transition-all ${rpInCart ? 'border-green-500 ring-2 ring-green-200' : 'border-emerald-200'}`}
                                  data-testid={`real-photo-${rp.id}`}>
                                  {/* Gallery strip — clickable, opens fullscreen swipe lightbox */}
                                  {rpImages.length > 0 ? (
                                    <div className="flex gap-1 overflow-x-auto p-1 bg-sand-100 cursor-zoom-in" data-testid={`real-photo-gallery-${rp.id}`}>
                                      {rpImages.slice(0, 6).map((src, i) => (
                                        <img key={i} src={src} alt={`${rp.business_name || rp.name} ${i + 1}`} loading="lazy"
                                          onClick={() => openWorksLightbox(i)}
                                          className="h-20 w-20 flex-shrink-0 object-cover rounded border border-[#E8E1D5] cursor-zoom-in" />
                                      ))}
                                      {rpImages.length > 6 && (
                                        <button type="button"
                                          onClick={() => openWorksLightbox(6)}
                                          className="h-20 w-20 flex-shrink-0 flex items-center justify-center bg-text-primary text-sand-50 text-xs font-bold rounded cursor-pointer hover:bg-brand-primary"
                                          data-testid={`real-photo-gallery-more-${rp.id}`}>
                                          +{rpImages.length - 6}
                                        </button>
                                      )}
                                    </div>
                                  ) : (
                                    <div className="px-3 py-2 bg-sand-100 text-xs text-text-secondary italic" data-testid={`real-photo-no-gallery-${rp.id}`}>
                                      No portfolio uploaded yet
                                    </div>
                                  )}
                                  <div className="p-3">
                                    <div className="flex items-start justify-between gap-2 mb-2">
                                      <div className="flex-1 min-w-0">
                                        <div className="font-medium text-sm truncate">{rp.business_name || rp.name}</div>
                                        {rp.location && (
                                          <div className="flex items-center gap-1 text-xs text-text-secondary mt-0.5">
                                            <MapPin size={10} /> {rp.location}
                                          </div>
                                        )}
                                      </div>
                                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-bold uppercase tracking-wider flex-shrink-0">Verified</span>
                                    </div>
                                    <div className="flex items-center justify-between pt-2 border-t border-[#E8E1D5]">
                                      <div className="flex flex-col">
                                        <div className="font-heading text-lg font-bold text-brand-primary">
                                          From ₹{markup(rpPrice, 'vendor').toLocaleString('en-IN')}
                                        </div>
                                        {hasOwnPkg && (
                                          <span className="text-[10px] uppercase tracking-wider font-bold text-emerald-700" data-testid={`real-photo-own-pkg-${rp.id}`}>
                                            Vendor's own package
                                          </span>
                                        )}
                                      </div>
                                      <Button type="button" size="sm"
                                        className={`rounded-none ${rpInCart ? 'bg-red-500 hover:bg-red-600' : 'bg-brand-primary hover:bg-[#A84A38]'} text-sand-50`}
                                        onClick={() => {
                                          if (rpInCart) { removeFromCart(rpCartId); } else {
                                            setCart(prev => [...prev.filter(c => !c.id.startsWith('photographer-')), {
                                              id: rpCartId,
                                              name: `${rp.business_name || rp.name} (${tierData?.label})`,
                                              price: rpPrice,
                                              category: 'Photography',
                                              image: (rp.gallery && rp.gallery[0]) || (rp.photos && rp.photos[0]) || null,
                                            }]);
                                            toast.success(`Added: ${rp.business_name || rp.name}`);
                                          }
                                        }}
                                        data-testid={`real-photo-add-${rp.id}`}>
                                        {rpInCart ? <><Minus size={14} className="mr-1" /> Remove</> : <><Plus size={14} className="mr-1" /> Add</>}
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          <div className="text-xs tracking-[0.15em] uppercase font-bold text-text-secondary mt-4 mb-2">Sample Providers</div>
                        </div>
                      )}

                      {providers.map((provider) => {
                        const inCart = cart.find(c => c.id === `photographer-${provider.id}`);
                        const isExpanded = expandedProviderId === provider.id;
                        const openPhotoShowcase = () => {
                          const photoSpecs = [];
                          if (provider.location) photoSpecs.push({ label: 'Location', value: provider.location });
                          if (provider.experience) photoSpecs.push({ label: 'Experience', value: provider.experience });
                          if (provider.speciality) photoSpecs.push({ label: 'Style', value: provider.speciality });
                          const inclusions = Object.entries(equipGroups || {}).map(([g, items]) =>
                            `${g}: ${items.map(it => it.name).join(', ')}`
                          );
                          setShowcaseLightbox({
                            open: true,
                            provider,
                            cartId: `photographer-${provider.id}`,
                            addPayload: null,
                            priceText: `₹${markup(provider.price, 'vendor').toLocaleString('en-IN')}`,
                            subPriceText: tierData?.label || '',
                            // Photography is tier-exclusive: replace any prior photographer pick.
                            onAdd: () => {
                              setCart(prev => [...prev.filter(c => !c.id.startsWith('photographer-')), {
                                id: `photographer-${provider.id}`,
                                name: `${provider.name} (${tierData?.label})`,
                                price: provider.price,
                                category: 'Photography',
                                image: provider.image || provider.avatar || null,
                                details: {
                                  specs: photoSpecs,
                                  description: inclusions.length ? `Includes — ${inclusions.join(' · ')}` : null,
                                },
                              }]);
                              toast.success(`Added: ${provider.name}`);
                            },
                          });
                        };
                        return (
                          <ProviderCardFrame key={provider.id}
                            inCart={!!inCart}
                            testId={`photo-provider-${provider.id}`}>
                            {/* Provider Card */}
                            <div className="p-4">
                              <ProviderShowcaseHeader
                                onOpen={openPhotoShowcase}
                                headerTestId={`provider-card-header-${provider.id}`}
                              >
                                <img src={PHOTOGRAPHER_AVATARS[provider.avatar]} alt={provider.name}
                                  className="w-14 h-14 rounded-full object-cover border-2 border-[#E8E1D5] flex-shrink-0"
                                  onError={(e) => { e.target.style.display = 'none'; }} />
                                <div className="flex-1 min-w-0">
                                  <div className="font-medium text-sm truncate">{provider.name}</div>
                                  <div className="flex items-center gap-1 mt-0.5">
                                    {Array.from({ length: 5 }).map((_, i) => (
                                      <Star key={i} size={12} weight={i < Math.floor(provider.rating) ? 'fill' : (i < provider.rating ? 'duotone' : 'regular')}
                                        className={i < provider.rating ? 'text-amber-500' : 'text-gray-300'} />
                                    ))}
                                    <span className="text-xs text-text-secondary ml-1">{provider.rating} ({provider.reviews})</span>
                                  </div>
                                  <div className="flex items-center gap-2 mt-1 text-xs text-text-secondary">
                                    <span className="flex items-center gap-0.5"><MapPin size={10} />{provider.location}</span>
                                    <span>{provider.experience}</span>
                                  </div>
                                </div>
                                {inCart && (
                                  <div className="bg-green-500 text-white rounded-full p-1 flex-shrink-0">
                                    <Check size={12} weight="bold" />
                                  </div>
                                )}
                              </ProviderShowcaseHeader>

                              {/* Price + Action Buttons */}
                              <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#E8E1D5]">
                                <div className="font-heading text-xl font-bold text-brand-primary">
                                  ₹{markup(provider.price, 'vendor').toLocaleString('en-IN')}
                                </div>
                                <div className="flex items-center gap-2">
                                  <Button type="button" size="sm" variant="outline"
                                    className={`rounded-none border-[#E8E1D5] text-text-secondary hover:text-brand-primary hover:border-brand-primary ${isExpanded ? 'bg-brand-primary/5 border-brand-primary text-brand-primary' : ''}`}
                                    onClick={() => setExpandedProviderId(isExpanded ? null : provider.id)}
                                    data-testid={`photo-info-${provider.id}`}>
                                    <Info size={14} className="mr-1" /> Info
                                  </Button>
                                  <Button type="button" size="sm"
                                    className={`rounded-none ${inCart ? 'bg-red-500 hover:bg-red-600' : 'bg-brand-primary hover:bg-[#A84A38]'} text-sand-50`}
                                    onClick={() => {
                                      if (inCart) { removeFromCart(`photographer-${provider.id}`); } else {
                                        // Remove other photographer selections for this tier
                                        // Build a per-tier inclusions list for the slideshow detail panel.
                                        // The equipment groups (Photos, Videos, Add-ons, etc.) are already
                                        // computed above for the tier — we flatten them into a single
                                        // description string + a specs row each.
                                        const photoSpecs = [];
                                        if (provider.location) photoSpecs.push({ label: 'Location', value: provider.location });
                                        if (provider.experience) photoSpecs.push({ label: 'Experience', value: provider.experience });
                                        if (provider.speciality) photoSpecs.push({ label: 'Style', value: provider.speciality });
                                        const inclusions = Object.entries(equipGroups || {}).map(([g, items]) =>
                                          `${g}: ${items.map(it => it.name).join(', ')}`
                                        );
                                        setCart(prev => [...prev.filter(c => !c.id.startsWith('photographer-')), {
                                          id: `photographer-${provider.id}`,
                                          name: `${provider.name} (${tierData?.label})`,
                                          price: provider.price,
                                          category: 'Photography',
                                          image: provider.image || provider.avatar || null,
                                          details: {
                                            specs: photoSpecs,
                                            description: inclusions.length ? `Includes — ${inclusions.join(' · ')}` : null,
                                          },
                                        }]);
                                        toast.success(`Added: ${provider.name}`);
                                      }
                                    }}
                                    data-testid={`photo-add-${provider.id}`}>
                                    {inCart ? <><Minus size={14} className="mr-1" /> Remove</> : <><Plus size={14} className="mr-1" /> Add</>}
                                  </Button>
                                </div>
                              </div>
                            </div>

                            {/* Expanded Info - Equipment/Services */}
                            {isExpanded && (
                              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
                                className="bg-gray-50 border-t border-[#E8E1D5] px-4 py-3">
                                <p className="text-xs text-text-secondary mb-2 font-bold uppercase tracking-wider">Available Equipment & Services</p>
                                {Object.entries(equipGroups).map(([groupName, groupItems]) => (
                                  <div key={groupName} className="mb-2">
                                    <div className="text-[10px] tracking-[0.15em] uppercase font-bold text-text-secondary mt-2 mb-1">{groupName}</div>
                                    <div className="flex flex-wrap gap-1.5">
                                      {groupItems.map((item, iIdx) => (
                                        <span key={iIdx} className="text-xs bg-white border border-[#E8E1D5] px-2 py-1 rounded">
                                          {item.name} <span className="text-brand-primary font-bold">₹{Number(item.defaultPrice).toLocaleString('en-IN')}</span>
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              </motion.div>
                            )}
                          </ProviderCardFrame>
                        );
                      })}
                    </>
                  );
                })()}

                {/* Birthday Decoration Providers */}
                {sidePanel.type === 'birthday_deco_provider' && (() => {
                  const providers = sidePanel.data?.providers || [];
                  const decoItem = sidePanel.data?.decoItem;
                  // Filter real vendors to only those who offer the selected theme
                  const themeName = decoItem?.name;
                  const matchingRealVendors = themeName
                    ? realBirthdayDecorators.filter(rv => (rv.themes || []).some(t => t.name === themeName))
                    : realBirthdayDecorators;
                  return (
                    <>
                      <div className="mb-3">
                        <div className="font-medium text-sm text-text-primary">{decoItem?.name}</div>
                        <p className="text-xs text-text-secondary mt-1">
                          {matchingRealVendors.length + providers.length} decorator{(matchingRealVendors.length + providers.length) !== 1 ? 's' : ''} available
                        </p>
                      </div>

                      {/* Real registered birthday decorators offering this theme */}
                      {matchingRealVendors.length > 0 && (
                        <div className="mb-4" data-testid="real-bd-decorators-block">
                          <div className="text-xs tracking-[0.15em] uppercase font-bold text-text-secondary mb-2 flex items-center gap-2">
                            <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                            Verified Decorators ({matchingRealVendors.length})
                          </div>
                          {matchingRealVendors.map((rv) => {
                            const matchedTheme = (rv.themes || []).find(t => t.name === themeName);
                            const rvCartId = `bd-real-${decoItem?.name}-${rv.id}`;
                            const rvInCart = cart.find(c => c.id === rvCartId);
                            const isExpanded = expandedProviderId === `real-${rv.id}`;
                            const themesWithPhoto = (rv.themes || []).filter(t => t.photo);
                            const galleryCount = themesWithPhoto.length;
                            const openBdRealShowcase = () => {
                              const usePrice = matchedTheme?.price || rv.min_price || 0;
                              const finalPrice = markup(usePrice, 'vendor');
                              setShowcaseLightbox({
                                open: true,
                                provider: {
                                  ...rv,
                                  photos: [
                                    ...(themesWithPhoto.map(t => t.photo)),
                                    ...(rv.portfolio_items || []).map(p => p.url),
                                  ],
                                  verified: true,
                                },
                                cartId: rvCartId,
                                addPayload: { id: rvCartId, name: `${rv.business_name || rv.name} (${decoItem?.name})`, price: usePrice, category: 'Birthday Decoration' },
                                priceText: matchedTheme ? `₹${finalPrice.toLocaleString('en-IN')}` : `From ₹${markup(rv.min_price || 0, 'vendor').toLocaleString('en-IN')}`,
                                subPriceText: decoItem?.name || '',
                              });
                            };
                            return (
                              <ProviderCardFrame key={rv.id}
                                inCart={!!rvInCart}
                                notInCartBorderClass="border-emerald-200"
                                testId={`bd-real-provider-${rv.id}`}>
                                <div className="p-4">
                                  <ProviderShowcaseHeader
                                    onOpen={openBdRealShowcase}
                                    headerTestId={`provider-card-header-${rv.id}`}
                                  >
                                    <div className="w-14 h-14 rounded-full bg-emerald-100 border-2 border-emerald-300 flex items-center justify-center flex-shrink-0">
                                      <Heart size={20} className="text-emerald-600" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="font-medium text-sm truncate flex items-center gap-2">
                                        {rv.business_name || rv.name}
                                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-bold uppercase tracking-wider flex-shrink-0">Verified</span>
                                      </div>
                                      {rv.location && (
                                        <div className="flex items-center gap-1 text-xs text-text-secondary mt-0.5">
                                          <MapPin size={10} /> {rv.location}
                                        </div>
                                      )}
                                      <div className="text-xs text-text-secondary mt-0.5">
                                        {rv.themes.length} theme{rv.themes.length !== 1 ? 's' : ''}
                                        {galleryCount > 0 && <> &middot; {galleryCount} with image{galleryCount !== 1 ? 's' : ''}</>}
                                      </div>
                                    </div>
                                    {rvInCart && <div className="bg-green-500 text-white rounded-full p-1 flex-shrink-0"><Check size={12} weight="bold" /></div>}
                                  </ProviderShowcaseHeader>
                                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#E8E1D5]">
                                    <div className="font-heading text-xl font-bold text-brand-primary">
                                      {matchedTheme ? <>₹{markup(matchedTheme.price || 0, 'vendor').toLocaleString('en-IN')}</> : <>From ₹{markup(rv.min_price || 0, 'vendor').toLocaleString('en-IN')}</>}
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Button type="button" size="sm" variant="outline"
                                        className={`rounded-none border-[#E8E1D5] text-text-secondary hover:text-brand-primary hover:border-brand-primary ${isExpanded ? 'bg-brand-primary/5 border-brand-primary text-brand-primary' : ''}`}
                                        onClick={() => setExpandedProviderId(isExpanded ? null : `real-${rv.id}`)}
                                        data-testid={`bd-real-gallery-${rv.id}`}>
                                        <ImageIcon size={14} className="mr-1" /> Gallery
                                      </Button>
                                      <Button type="button" size="sm"
                                        className={`rounded-none ${rvInCart ? 'bg-red-500 hover:bg-red-600' : 'bg-brand-primary hover:bg-[#A84A38]'} text-sand-50`}
                                        onClick={() => {
                                          if (rvInCart) { removeFromCart(rvCartId); } else {
                                            const usePrice = matchedTheme?.price || rv.min_price || 0;
                                            addToCart({ id: rvCartId, name: `${rv.business_name || rv.name} (${decoItem?.name})`, price: usePrice, category: 'Birthday Decoration' });
                                          }
                                        }}
                                        data-testid={`bd-real-add-${rv.id}`}>
                                        {rvInCart ? <><Minus size={14} className="mr-1" /> Remove</> : <><Plus size={14} className="mr-1" /> Add</>}
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                                {isExpanded && (
                                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
                                    className="bg-gray-50 border-t border-[#E8E1D5] px-4 py-3" data-testid={`bd-real-gallery-panel-${rv.id}`}>
                                    <p className="text-xs font-bold text-text-secondary uppercase tracking-wider mb-2">
                                      Themes Offered ({rv.themes.length})
                                      {rv.themes.length > 2 && <span className="ml-2 text-text-secondary normal-case font-normal">— scroll for more</span>}
                                    </p>
                                    <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 snap-x snap-mandatory">
                                      {rv.themes.map((th, tIdx) => {
                                        const themeCartId = `bd-real-theme-${rv.id}-${th.name}`;
                                        const themeInCart = cart.find(c => c.id === themeCartId);
                                        return (
                                          <div key={tIdx}
                                            className={`border-2 rounded overflow-hidden flex-shrink-0 w-44 snap-start transition-all ${themeInCart ? 'border-green-500 ring-2 ring-green-200 bg-white' : 'border-[#E8E1D5] bg-white hover:border-brand-primary/40'}`}
                                            data-testid={`bd-real-theme-${rv.id}-${tIdx}`}>
                                            <div className="relative h-32 bg-gray-100 overflow-hidden">
                                              {th.photo ? (
                                                <img src={th.photo.startsWith('http') ? th.photo : `${BACKEND_URL}${th.photo}`} alt={th.name}
                                                  className="w-full h-full object-cover"
                                                  onError={(e) => {
                                                    e.target.onerror = null;
                                                    e.target.style.display = 'none';
                                                    e.target.parentElement.classList.add('flex','items-center','justify-center','text-text-secondary');
                                                    if (!e.target.parentElement.querySelector('[data-fallback]')) {
                                                      const span = document.createElement('span');
                                                      span.setAttribute('data-fallback','1');
                                                      span.className = 'text-xs';
                                                      span.textContent = 'No image';
                                                      e.target.parentElement.appendChild(span);
                                                    }
                                                  }} />
                                              ) : (
                                                <div className="w-full h-full flex items-center justify-center text-text-secondary">
                                                  <Camera size={20} />
                                                </div>
                                              )}
                                              {th.custom && (
                                                <span className="absolute top-1 left-1 text-[10px] px-1.5 py-0.5 bg-pink-600 text-white rounded font-bold uppercase tracking-wider">Custom</span>
                                              )}
                                              {themeInCart && (
                                                <div className="absolute top-1 right-1 bg-green-500 text-white rounded-full p-1"><Check size={10} weight="bold" /></div>
                                              )}
                                            </div>
                                            <div className="p-2">
                                              <div className="text-xs font-medium truncate">{th.name}</div>
                                              <div className="font-heading text-sm font-bold text-brand-primary mt-0.5">₹{markup(th.price || 0, 'vendor').toLocaleString('en-IN')}</div>
                                              <Button type="button" size="sm"
                                                className={`w-full mt-1.5 h-7 text-xs rounded-none ${themeInCart ? 'bg-red-500 hover:bg-red-600' : 'bg-brand-primary hover:bg-[#A84A38]'} text-sand-50`}
                                                onClick={() => {
                                                  if (themeInCart) { removeFromCart(themeCartId); } else {
                                                    addToCart({ id: themeCartId, name: `${rv.business_name || rv.name} — ${th.name}`, price: th.price || 0, category: 'Birthday Decoration' });
                                                  }
                                                }}
                                                data-testid={`bd-real-theme-add-${rv.id}-${tIdx}`}>
                                                {themeInCart ? <><Minus size={12} className="mr-1" /> Remove</> : <><Plus size={12} className="mr-1" /> Add</>}
                                              </Button>
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </motion.div>
                                )}
                              </ProviderCardFrame>
                            );
                          })}
                        </div>
                      )}

                      {providers.map((provider) => {
                        const cartId = `bd-prov-${decoItem?.name}-${provider.id}`;
                        const inCart = cart.find(c => c.id === cartId);
                        const isExpanded = expandedProviderId === provider.id;
                        const openBdShowcase = () => {
                          setShowcaseLightbox({
                            open: true,
                            provider,
                            cartId,
                            addPayload: { id: cartId, name: `${provider.name} (${decoItem?.name})`, price: provider.price, category: 'Birthday Decoration' },
                            priceText: `₹${markup(provider.price, 'vendor').toLocaleString('en-IN')}`,
                            subPriceText: decoItem?.name || '',
                          });
                        };
                        return (
                          <ProviderCardFrame key={provider.id}
                            inCart={!!inCart}
                            testId={`bd-provider-${provider.id}`}>
                            <div className="p-4">
                              <ProviderShowcaseHeader
                                onOpen={openBdShowcase}
                                headerTestId={`provider-card-header-${provider.id}`}
                              >
                                <img src={BIRTHDAY_DECO_IMAGES[provider.avatar]} alt={provider.name}
                                  className="w-14 h-14 rounded-full object-cover border-2 border-[#E8E1D5] flex-shrink-0"
                                  onError={(e) => { e.target.style.display = 'none'; }} />
                                <div className="flex-1 min-w-0">
                                  <div className="font-medium text-sm truncate">{provider.name}</div>
                                  <div className="flex items-center gap-1 mt-0.5">
                                    {Array.from({ length: 5 }).map((_, i) => (
                                      <Star key={i} size={12} weight={i < Math.floor(provider.rating) ? 'fill' : (i < provider.rating ? 'duotone' : 'regular')}
                                        className={i < provider.rating ? 'text-amber-500' : 'text-gray-300'} />
                                    ))}
                                    <span className="text-xs text-text-secondary ml-1">{provider.rating} ({provider.reviews})</span>
                                  </div>
                                  <div className="flex items-center gap-2 mt-1 text-xs text-text-secondary">
                                    <span className="flex items-center gap-0.5"><MapPin size={10} />{provider.location}</span>
                                    <span>{provider.experience}</span>
                                  </div>
                                </div>
                                {inCart && <div className="bg-green-500 text-white rounded-full p-1 flex-shrink-0"><Check size={12} weight="bold" /></div>}
                              </ProviderShowcaseHeader>
                              <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#E8E1D5]">
                                <div className="font-heading text-xl font-bold text-brand-primary">₹{markup(provider.price, 'vendor').toLocaleString('en-IN')}</div>
                                <div className="flex items-center gap-2">
                                  <Button type="button" size="sm" variant="outline"
                                    className={`rounded-none border-[#E8E1D5] text-text-secondary hover:text-brand-primary hover:border-brand-primary ${isExpanded ? 'bg-brand-primary/5 border-brand-primary text-brand-primary' : ''}`}
                                    onClick={() => setExpandedProviderId(isExpanded ? null : provider.id)}
                                    data-testid={`bd-gallery-${provider.id}`}>
                                    <ImageIcon size={14} className="mr-1" /> Gallery
                                  </Button>
                                  <Button type="button" size="sm"
                                    className={`rounded-none ${inCart ? 'bg-red-500 hover:bg-red-600' : 'bg-brand-primary hover:bg-[#A84A38]'} text-sand-50`}
                                    onClick={() => {
                                      if (inCart) { removeFromCart(cartId); } else {
                                        addToCart({ id: cartId, name: `${provider.name} (${decoItem?.name})`, price: provider.price, category: 'Birthday Decoration' });
                                      }
                                    }}
                                    data-testid={`bd-add-${provider.id}`}>
                                    {inCart ? <><Minus size={14} className="mr-1" /> Remove</> : <><Plus size={14} className="mr-1" /> Add</>}
                                  </Button>
                                </div>
                              </div>
                            </div>
                            {isExpanded && (
                              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
                                className="bg-gray-50 border-t border-[#E8E1D5] px-4 py-3">
                                <p className="text-xs font-bold text-text-secondary uppercase tracking-wider mb-2">
                                  Sample Themes ({(provider.services || []).length})
                                  {(provider.services || []).length > 2 && <span className="ml-2 text-text-secondary normal-case font-normal">— scroll for more</span>}
                                </p>
                                <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 snap-x snap-mandatory">
                                  {(provider.services || []).map((s, sIdx) => {
                                    const sampleCartId = `bd-sample-theme-${provider.id}-${s}`;
                                    const sampleInCart = cart.find(c => c.id === sampleCartId);
                                    return (
                                      <div key={sIdx}
                                        className={`border-2 rounded overflow-hidden flex-shrink-0 w-44 snap-start transition-all ${sampleInCart ? 'border-green-500 ring-2 ring-green-200 bg-white' : 'border-[#E8E1D5] bg-white hover:border-brand-primary/40'}`}>
                                        <div className="relative h-32 bg-gray-100 overflow-hidden">
                                          <img src={BIRTHDAY_DECO_IMAGES[(provider.avatar + sIdx) % BIRTHDAY_DECO_IMAGES.length]}
                                            alt={s} className="w-full h-full object-cover" loading="lazy"
                                            onError={(e) => { e.target.style.display = 'none'; }} />
                                          {sampleInCart && (
                                            <div className="absolute top-1 right-1 bg-green-500 text-white rounded-full p-1"><Check size={10} weight="bold" /></div>
                                          )}
                                        </div>
                                        <div className="p-2">
                                          <div className="text-xs font-medium truncate">{s}</div>
                                          <div className="font-heading text-sm font-bold text-brand-primary mt-0.5">₹{markup(provider.price, 'vendor').toLocaleString('en-IN')}</div>
                                          <Button type="button" size="sm"
                                            className={`w-full mt-1.5 h-7 text-xs rounded-none ${sampleInCart ? 'bg-red-500 hover:bg-red-600' : 'bg-brand-primary hover:bg-[#A84A38]'} text-sand-50`}
                                            onClick={() => {
                                              if (sampleInCart) { removeFromCart(sampleCartId); } else {
                                                addToCart({ id: sampleCartId, name: `${provider.name} — ${s}`, price: provider.price, category: 'Birthday Decoration' });
                                              }
                                            }}
                                            data-testid={`bd-sample-theme-add-${provider.id}-${sIdx}`}>
                                            {sampleInCart ? <><Minus size={12} className="mr-1" /> Remove</> : <><Plus size={12} className="mr-1" /> Add</>}
                                          </Button>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </motion.div>
                            )}
                          </ProviderCardFrame>
                        );
                      })}
                    </>
                  );
                })()}

                {/* Caterer Service Providers */}
                {sidePanel.type === 'caterer_provider' && (() => {
                  const providers = sidePanel.data?.providers || [];
                  const tierLabel = sidePanel.data?.tierLabel;
                  const tierKey = sidePanel.data?.tierKey;
                  // Real registered caterers — filtered by event_city + pure_veg
                  // server-side, plus client-side filter by selected service style.
                  const allRealCaterers = sidePanel.data?.realCaterers || [];
                  // Helper: max menu item count across all of a caterer's meals.
                  // Used by the optional "min items" filter customers can type
                  // in the side panel header to narrow down which caterers
                  // actually publish elaborate menus.
                  const maxMenuItems = (c) => {
                    const meals = c?.meals || {};
                    let max = 0;
                    for (const m of Object.values(meals)) {
                      if (!m || typeof m !== 'object') continue;
                      const vegLen = Array.isArray(m.menuVeg) ? m.menuVeg.length : 0;
                      const nvLen  = Array.isArray(m.menuNV)  ? m.menuNV.length  : 0;
                      if (vegLen > max) max = vegLen;
                      if (nvLen  > max) max = nvLen;
                    }
                    return max;
                  };
                  const minItemsNum = parseInt(catererMinItems, 10);
                  const itemsFilterActive = !isNaN(minItemsNum) && minItemsNum > 0;
                  const realCaterers = (catererStyle === 'all'
                    ? allRealCaterers
                    : allRealCaterers.filter((c) => (c.service_styles || []).includes(catererStyle))
                  ).filter((c) => !itemsFilterActive || maxMenuItems(c) >= minItemsNum);
                  // Style filter — match against each provider's `service_styles` array.
                  const filteredCaterers = (catererStyle === 'all'
                    ? providers
                    : providers.filter((p) => (p.service_styles || []).includes(catererStyle))
                  ).filter((p) => !itemsFilterActive || maxMenuItems(p) >= minItemsNum);
                  return (
                    <>
                      <div className="mb-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-xs px-2 py-0.5 text-white font-bold uppercase tracking-wider ${tierKey === 'budget' ? 'bg-green-600' : tierKey === 'premium' ? 'bg-purple-600' : 'bg-amber-600'}`}>{tierLabel}</span>
                          <span className="text-xs text-text-secondary">{filteredCaterers.length} of {providers.length} caterers</span>
                          {/* Optional "min items" filter — narrows down to caterers
                              whose largest menu has at least N items. */}
                          <div className="ml-auto flex items-center gap-1.5" data-testid="caterer-min-items-filter">
                            <label className="text-[10px] uppercase tracking-wider font-bold text-text-secondary whitespace-nowrap">Min items</label>
                            <input
                              type="number"
                              min="1"
                              placeholder="any"
                              value={catererMinItems}
                              onChange={(e) => setCatererMinItems(e.target.value.replace(/\D/g, ''))}
                              className="w-16 h-7 text-xs text-center border border-[#E8E1D5] rounded-none bg-white"
                              data-testid="caterer-min-items-input"
                            />
                            {itemsFilterActive && (
                              <button
                                type="button"
                                onClick={() => setCatererMinItems('')}
                                className="text-[10px] uppercase tracking-wider font-bold text-text-secondary hover:text-brand-primary"
                                data-testid="caterer-min-items-clear"
                              >
                                Clear
                              </button>
                            )}
                          </div>
                        </div>
                        <p className="text-xs text-text-secondary mt-1">Price shown is per plate for {guestCount} guests</p>
                        {/* Service style filter — Traditional Serving vs Buffet */}
                        <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="caterer-style-filter">
                          <span className="text-[10px] uppercase font-bold tracking-wider text-text-secondary">Serving style:</span>
                          {[
                            { key: 'all', label: 'All' },
                            { key: 'traditional', label: 'Traditional Serving' },
                            { key: 'buffet', label: 'Buffet' },
                          ].map((opt) => (
                            <button key={opt.key} type="button"
                              onClick={() => setCatererStyle(opt.key)}
                              className={`px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider rounded-sm border transition-colors ${
                                catererStyle === opt.key ? 'bg-brand-primary text-sand-50 border-brand-primary' : 'bg-white text-brand-primary border-brand-primary/30 hover:bg-brand-primary/5'
                              }`}
                              data-testid={`caterer-style-${opt.key}`}>
                              {opt.label}
                            </button>
                          ))}
                        </div>
                        {sidePanel.data?.pureVegOnly && (
                          <div className="mt-2 p-2 bg-green-50 border border-green-300 rounded text-xs text-green-800 font-medium flex items-center gap-1" data-testid="pure-veg-notice">
                            <span className="inline-block w-3 h-3 bg-green-600 rounded-sm border border-green-700" /> Brahmin Wedding — Showing Pure Veg caterers only
                          </div>
                        )}
                      </div>
                      {filteredCaterers.length === 0 && realCaterers.length === 0 && (
                        <p className="text-sm text-text-secondary italic py-4 text-center">
                          No caterers offer this serving style in {tierLabel}. Try a different style.
                        </p>
                      )}

                      {filteredCaterers.map((provider) => {
                        const cartId = `caterer-${tierKey}-${provider.id}`;
                        const inCart = cart.find(c => c.id === cartId);
                        const isExpanded = expandedProviderId === provider.id;
                        const { total: configuredTotal, hasEnabled: anyMealEnabled } = computeCatererTotal(provider);
                        const fallbackTotal = provider.price * guestCount;
                        return (
                          <div key={provider.id}
                            className={`border-2 rounded-lg overflow-hidden transition-all mb-3 ${inCart ? 'border-green-500 ring-2 ring-green-200' : 'border-[#E8E1D5]'}`}
                            data-testid={`caterer-provider-${provider.id}`}>
                            <div className="p-4">
                              <div className="flex items-start gap-3">
                                <img src={CATERER_AVATARS[provider.avatar]} alt={provider.name}
                                  className="w-14 h-14 rounded-full object-cover border-2 border-[#E8E1D5] flex-shrink-0"
                                  onError={(e) => { e.target.style.display = 'none'; }} />
                                <div className="flex-1 min-w-0">
                                  <div className="font-medium text-sm truncate flex items-center gap-2 flex-wrap">
                                    {provider.isVerified && (
                                      <span className="inline-flex items-center text-[9px] tracking-wider uppercase font-bold bg-green-600 text-white px-1.5 py-0.5 rounded" data-testid={`verified-badge-${provider.id}`}>
                                        VERIFIED
                                      </span>
                                    )}
                                    {provider.name}
                                    {provider.pure_veg && (
                                      <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 bg-green-100 text-green-700 border border-green-300 font-bold rounded" data-testid={`pure-veg-badge-${provider.id}`}>
                                        <span className="inline-block w-2 h-2 bg-green-600 rounded-sm" /> Pure Veg
                                      </span>
                                    )}
                                    {(provider.service_styles || []).includes('buffet') && (
                                      <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-800 border border-amber-300 font-bold rounded" data-testid={`buffet-badge-${provider.id}`}>
                                        Buffet
                                      </span>
                                    )}
                                    {(provider.service_styles || []).includes('traditional') && (
                                      <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 bg-orange-100 text-orange-800 border border-orange-300 font-bold rounded" data-testid={`traditional-badge-${provider.id}`}>
                                        Traditional
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-1 mt-0.5">
                                    {Array.from({ length: 5 }).map((_, i) => (
                                      <Star key={i} size={12} weight={i < Math.floor(provider.rating) ? 'fill' : (i < provider.rating ? 'duotone' : 'regular')}
                                        className={i < provider.rating ? 'text-amber-500' : 'text-gray-300'} />
                                    ))}
                                    <span className="text-xs text-text-secondary ml-1">{provider.rating} ({provider.reviews})</span>
                                  </div>
                                  <div className="flex items-center gap-2 mt-1 text-xs text-text-secondary">
                                    <span className="flex items-center gap-0.5"><MapPin size={10} />{provider.location}</span>
                                    <span>{provider.experience}</span>
                                  </div>
                                  <div className="text-xs text-text-secondary mt-0.5">{provider.speciality}</div>
                                </div>
                                {inCart && <div className="bg-green-500 text-white rounded-full p-1 flex-shrink-0"><Check size={12} weight="bold" /></div>}
                              </div>

                              <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#E8E1D5]">
                                <div>
                                  <div className="font-heading text-xl font-bold text-brand-primary">₹{markup(provider.price, 'vendor')}/plate</div>
                                  {anyMealEnabled ? (
                                    <div className="text-xs text-text-secondary">Configured total = <span className="font-bold text-brand-primary">₹{markup(configuredTotal, 'vendor').toLocaleString('en-IN')}</span></div>
                                  ) : (
                                    <div className="text-xs text-amber-700 font-medium" data-testid={`caterer-needs-meal-${provider.id}`}>Open <span className="font-bold">Menu</span> & pick at least one meal to add</div>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  <Button type="button" size="sm" variant="outline"
                                    className={`rounded-none border-[#E8E1D5] text-text-secondary hover:text-brand-primary hover:border-brand-primary ${isExpanded ? 'bg-brand-primary/5 border-brand-primary text-brand-primary' : ''}`}
                                    onClick={() => setExpandedProviderId(isExpanded ? null : provider.id)}
                                    data-testid={`caterer-info-${provider.id}`}>
                                    <ForkKnife size={14} className="mr-1" /> Menu
                                  </Button>
                                  <Button type="button" size="sm"
                                    disabled={!inCart && !anyMealEnabled}
                                    className={`rounded-none ${inCart ? 'bg-red-500 hover:bg-red-600' : 'bg-brand-primary hover:bg-[#A84A38]'} text-sand-50 disabled:bg-gray-300 disabled:cursor-not-allowed disabled:hover:bg-gray-300`}
                                    onClick={() => {
                                      if (inCart) { removeFromCart(cartId); } else {
                                        if (!anyMealEnabled) return;
                                        // Build a friendly meal list label
                                        const cfg = catererMealConfig[provider.id] || {};
                                        const mealLabels = [];
                                        if (cfg.tiffin?.enabled) mealLabels.push('Tiffin');
                                        if (cfg.lunch?.enabled) mealLabels.push('Lunch');
                                        if (cfg.snacks?.enabled) mealLabels.push('Snacks');
                                        if (cfg.dinner?.enabled) mealLabels.push('Dinner');
                                        const finalPrice = configuredTotal || fallbackTotal;
                                        // Build full menu detail per meal for the slideshow + saved package detail view.
                                        // Each meal records: label, type (veg/non-veg), guests, per-plate price,
                                        // and the visible menu list (after the customer removed unwanted items).
                                        const MEAL_DEFS = [
                                          { key: 'tiffin', label: 'Tiffin' },
                                          { key: 'lunch', label: 'Lunch' },
                                          { key: 'snacks', label: 'Snacks & Refreshments' },
                                          { key: 'dinner', label: 'Dinner' },
                                        ];
                                        const menu = [];
                                        for (const m of MEAL_DEFS) {
                                          const mc = cfg[m.key];
                                          const md = provider.meals?.[m.key];
                                          if (!mc?.enabled || !md) continue;
                                          const isNV = mc.isNonVeg && md.priceNV;
                                          const perPlate = isNV ? md.priceNV : (md.priceVeg || 0);
                                          const full = isNV ? (md.menuNV || []) : (md.menuVeg || []);
                                          const removed = mc.removedItems || [];
                                          menu.push({
                                            meal: m.label,
                                            type: isNV ? 'Non-Veg' : 'Veg',
                                            guests: mc.guests || guestCount,
                                            price_per_plate: perPlate,
                                            items: full.filter(it => !removed.includes(it)),
                                          });
                                        }
                                        addToCart({
                                          id: cartId,
                                          name: `${provider.name} (${tierLabel} — ${mealLabels.join(', ')})`,
                                          price: finalPrice,
                                          category: 'Catering',
                                          image: provider.image || (Array.isArray(provider.photos) && provider.photos[0]) || null,
                                          details: { menu },
                                        });
                                      }
                                    }}
                                    data-testid={`caterer-add-${provider.id}`}>
                                    {inCart ? <><Minus size={14} className="mr-1" /> Remove</> : <><Plus size={14} className="mr-1" /> Add</>}
                                  </Button>
                                </div>
                              </div>
                            </div>

                            {/* Expanded Info — Interactive Meal Table */}
                            {isExpanded && (() => {
                              initCatererMealConfig(provider.id);
                              const cfg = catererMealConfig[provider.id] || {};
                              const MEAL_ROWS = [
                                { key: 'tiffin', label: 'Tiffin' },
                                { key: 'lunch', label: 'Lunch' },
                                { key: 'snacks', label: 'Snacks & Refreshments' },
                                { key: 'dinner', label: 'Dinner' },
                              ];
                              // Calculate total from enabled meals
                              let infoTotal = 0;
                              MEAL_ROWS.forEach(m => {
                                const mc = cfg[m.key];
                                const md = provider.meals?.[m.key];
                                if (mc?.enabled && md) {
                                  const price = mc.isNonVeg && md.priceNV ? md.priceNV : (md.priceVeg || 0);
                                  infoTotal += price * (mc.guests || 0);
                                }
                              });
                              return (
                                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
                                  className="bg-gray-50 border-t border-[#E8E1D5] px-3 py-3" data-testid={`caterer-info-panel-${provider.id}`}>
                                  {/* Meal Rows */}
                                  <div className="space-y-2">
                                    {MEAL_ROWS.map(meal => {
                                      const mealData = provider.meals?.[meal.key];
                                      if (!mealData) return null;
                                      const mc = cfg[meal.key] || { enabled: false, guests: guestCount, isNonVeg: false, removedItems: [] };
                                      const hasNV = !!mealData.priceNV && !provider.pure_veg;
                                      const currentPrice = mc.isNonVeg && mealData.priceNV ? mealData.priceNV : (mealData.priceVeg || 0);
                                      const currentMenu = mc.isNonVeg && mealData.menuNV ? mealData.menuNV : (mealData.menuVeg || []);
                                      const removedItems = mc.removedItems || [];
                                      const activeMenu = currentMenu.filter(item => !removedItems.includes(item));

                                      return (
                                        <div key={meal.key} className={`border rounded-lg overflow-hidden transition-all ${mc.enabled ? 'border-brand-primary/40 bg-white' : 'border-[#E8E1D5] bg-gray-100/50 opacity-60'}`}
                                          data-testid={`meal-row-${provider.id}-${meal.key}`}>
                                          {/* Meal Header */}
                                          <div className="flex items-center gap-2 p-2">
                                            {/* Add/Remove toggle */}
                                            <button type="button"
                                              className={`w-6 h-6 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${mc.enabled ? 'border-green-500 bg-green-500 text-white' : 'border-[#E8E1D5] text-transparent hover:border-brand-primary'}`}
                                              onClick={(e) => { e.stopPropagation(); toggleCatererMeal(provider.id, meal.key); }}
                                              data-testid={`meal-toggle-${provider.id}-${meal.key}`}>
                                              <Check size={12} weight="bold" />
                                            </button>
                                            {/* Meal label — clickable to open the banana-leaf popup. */}
                                            <button
                                              type="button"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setMealLeafPopup({
                                                  open: true,
                                                  providerId: provider.id,
                                                  mealKey: meal.key,
                                                  mealLabel: meal.label,
                                                  caterer: provider,
                                                  pureVeg: !!provider.pure_veg,
                                                });
                                              }}
                                              className="text-xs font-bold text-text-primary flex-1 text-left hover:text-brand-primary underline-offset-2 hover:underline cursor-pointer"
                                              data-testid={`meal-name-${provider.id}-${meal.key}`}
                                            >
                                              {meal.label}
                                            </button>
                                            {/* Price */}
                                            <span className="text-xs font-bold text-brand-primary">₹{currentPrice}/plate</span>
                                            {/* Guest count (editable) */}
                                            <input type="number" value={mc.guests}
                                              onChange={(e) => { e.stopPropagation(); updateCatererMealGuests(provider.id, meal.key, e.target.value); }}
                                              onClick={(e) => e.stopPropagation()}
                                              className="w-16 h-6 text-[10px] text-center border border-[#E8E1D5] rounded bg-white"
                                              disabled={!mc.enabled}
                                              data-testid={`meal-guests-${provider.id}-${meal.key}`} />
                                            {/* Veg/Non-Veg toggle */}
                                            {hasNV ? (
                                              <button type="button"
                                                className={`inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 font-bold rounded border cursor-pointer transition-colors ${mc.isNonVeg ? 'bg-red-100 text-red-700 border-red-300' : 'bg-green-100 text-green-700 border-green-300'}`}
                                                onClick={(e) => { e.stopPropagation(); toggleCatererMealType(provider.id, meal.key); }}
                                                data-testid={`meal-type-toggle-${provider.id}-${meal.key}`}>
                                                <span className={`inline-block w-2 h-2 rounded-sm ${mc.isNonVeg ? 'bg-red-600' : 'bg-green-600'}`} />
                                                {mc.isNonVeg ? 'Non-Veg' : 'Veg'}
                                              </button>
                                            ) : (
                                              <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 bg-green-100 text-green-700 border border-green-300 font-bold rounded">
                                                <span className="inline-block w-2 h-2 bg-green-600 rounded-sm" /> Veg
                                              </span>
                                            )}
                                          </div>
                                          {/* Expandable Menu (editable) */}
                                          {mc.enabled && (
                                            <div className={`px-2 pb-2 border-t border-[#E8E1D5]/50 ${mc.isNonVeg ? 'bg-red-50/30' : 'bg-green-50/30'}`}>
                                              <div className="flex flex-wrap gap-1 pt-1.5">
                                                {currentMenu.map((item, i) => {
                                                  const isActive = !removedItems.includes(item);
                                                  return (
                                                    <button key={i} type="button"
                                                      className={`text-[10px] px-1.5 py-0.5 rounded border transition-all ${isActive ? (mc.isNonVeg ? 'bg-white border-red-300 text-text-primary' : 'bg-white border-green-300 text-text-primary') : 'bg-gray-200 border-gray-300 text-gray-400 line-through'}`}
                                                      onClick={(e) => { e.stopPropagation(); toggleCatererMenuItem(provider.id, meal.key, item); }}
                                                      data-testid={`menu-item-${provider.id}-${meal.key}-${i}`}>
                                                      {item}
                                                    </button>
                                                  );
                                                })}
                                              </div>
                                              <p className="text-[9px] text-text-secondary mt-1">{activeMenu.length}/{currentMenu.length} items selected — click to remove/add</p>
                                              {activeMenu.length > 0 && (
                                                <button type="button"
                                                  className="mt-1.5 inline-flex items-center gap-1 text-[10px] px-2 py-0.5 bg-amber-50 hover:bg-amber-100 border border-amber-300 text-amber-800 font-bold rounded transition-colors"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    setBananaLeafPreview({
                                                      providerName: provider.name,
                                                      mealLabel: meal.label,
                                                      items: activeMenu,
                                                      isVeg: !mc.isNonVeg,
                                                    });
                                                  }}
                                                  data-testid={`banana-leaf-preview-${provider.id}-${meal.key}`}>
                                                  🍃 View on Banana Leaf
                                                </button>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>

                                  {/* Total Cost */}
                                  <div className="flex items-center justify-between mt-3 pt-3 border-t-2 border-brand-primary/30">
                                    <span className="text-xs font-bold text-text-secondary uppercase tracking-wider">Total Estimated Cost</span>
                                    <span className="font-heading text-lg font-bold text-brand-primary" data-testid={`caterer-total-${provider.id}`}>
                                      ₹{infoTotal.toLocaleString('en-IN')}
                                    </span>
                                  </div>
                                  <p className="text-[10px] text-text-secondary mt-1">* Only enabled meals are counted. Guest count per meal is editable.</p>
                                </motion.div>
                              );
                            })()}
                          </div>
                        );
                      })}
                    </>
                  );
                })()}

                {/* Catering Menu Items */}
                {sidePanel.type === 'catering' && (() => {
                  const menuItems = sidePanel.data?.menuItems || [];
                  const mealKey = sidePanel.data?.mealKey;
                  const groups = {};
                  menuItems.forEach(i => {
                    if (!groups[i.category]) groups[i.category] = [];
                    groups[i.category].push(i);
                  });
                  const selected = selectedMenuItems[mealKey] || [];
                  return (
                    <>
                      <p className="text-xs text-text-secondary mb-3">Add or remove dishes for this meal. Selected: {selected.length} items</p>
                      {Object.entries(groups).map(([groupName, groupItems]) => (
                        <div key={groupName}>
                          <div className="text-xs tracking-[0.15em] uppercase font-bold text-text-secondary mt-3 mb-2">{groupName}</div>
                          <div className="space-y-1">
                            {groupItems.map((item, iIdx) => {
                              const isSelected = selected.find(s => s.name === item.name);
                              return (
                                <button key={iIdx} type="button"
                                  className={`w-full flex items-center justify-between p-2.5 border rounded text-left transition-all ${isSelected ? 'border-green-500 bg-green-50/30' : 'border-[#E8E1D5] hover:border-brand-primary/30'}`}
                                  onClick={() => toggleMealMenuItem(mealKey, item)}
                                  data-testid={`menu-item-${groupName.toLowerCase().replace(/ /g, '-')}-${iIdx}`}>
                                  <div className="flex items-center gap-2 flex-1 min-w-0">
                                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? 'border-green-500 bg-green-500' : 'border-[#E8E1D5]'}`}>
                                      {isSelected && <Check size={10} weight="bold" className="text-white" />}
                                    </div>
                                    <span className="text-sm truncate">{item.name}</span>
                                  </div>
                                  <span className="text-brand-primary font-bold text-sm ml-2 flex-shrink-0">₹{Number(item.defaultPrice).toLocaleString('en-IN')}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </>
                  );
                })()}

                {/* DJ, Professional & Venue Provider Panels */}
                {(sidePanel.type === 'dj_provider' || sidePanel.type === 'pro_provider' || sidePanel.type === 'venue_provider') && (() => {
                  const providers = sidePanel.data?.providers || [];
                  const typeName = sidePanel.type === 'dj_provider' ? sidePanel.data?.djType?.name : sidePanel.type === 'venue_provider' ? sidePanel.data?.venueType?.name : sidePanel.data?.proType?.name;
                  const typePrice = sidePanel.type === 'dj_provider' ? sidePanel.data?.djType?.defaultPrice : sidePanel.type === 'venue_provider' ? sidePanel.data?.venueType?.defaultPrice : sidePanel.data?.proType?.defaultPrice;
                  const avatarSet = sidePanel.type === 'dj_provider' ? DJ_AVATARS : sidePanel.type === 'venue_provider' ? VENUE_AVATARS : PRO_AVATARS;
                  const cartPrefix = sidePanel.type === 'dj_provider' ? 'dj-prov' : sidePanel.type === 'venue_provider' ? 'ven-prov' : 'pro-prov';
                  const cartCategory = sidePanel.type === 'dj_provider' ? 'DJ & Entertainment' : sidePanel.type === 'venue_provider' ? 'Venue' : 'Professional';
                  const groupForSection = sidePanel.type === 'venue_provider' ? 'venue' : sidePanel.type === 'pro_provider' ? 'professional' : 'vendor';
                  const salaryPackages = sidePanel.data?.packages || [];
                  const isPerGuest = sidePanel.data?.perGuest;
                  const pureVegOnly = sidePanel.data?.pureVegOnly;
                  // Nearby-only filter (venue panel) — drops cross-city sample
                  // providers + restricts real venues to <=25 km. Activates a
                  // GPS detect if customer hasn't shared their location yet.
                  const showNearbyToggle = sidePanel.type === 'venue_provider';
                  const baseRealVenues = sidePanel.data?.realVenues || [];
                  const filteredRealVenues = (nearbyOnly && eventGeo?.lat)
                    ? baseRealVenues.filter((rv) => typeof rv.distance_km === 'number' && rv.distance_km <= 25)
                    : baseRealVenues;
                  const filteredProviders = (nearbyOnly && eventCity)
                    ? providers.filter((p) => (p.location || '').toLowerCase() === eventCity.toLowerCase())
                    : providers;
                  return (
                    <>
                      <div className="mb-3">
                        <div className="font-medium text-sm text-text-secondary">Base price: <span className="text-brand-primary font-bold">from ₹{markup(Number(typePrice), groupForSection).toLocaleString('en-IN')}</span></div>
                        <p className="text-xs text-text-secondary mt-1">{filteredProviders.length + filteredRealVenues.length} {sidePanel.type === 'venue_provider' ? 'venue' : 'service provider'}{(filteredProviders.length + filteredRealVenues.length) !== 1 ? 's' : ''} available</p>
                        {showNearbyToggle && (
                          <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="nearby-toggle-row">
                            <button type="button"
                              onClick={() => {
                                if (!nearbyOnly && !eventGeo?.lat) {
                                  // No GPS captured yet — kick off geolocation flow first.
                                  if (!('geolocation' in navigator)) {
                                    toast.error('Geolocation is not supported on this device');
                                    return;
                                  }
                                  navigator.geolocation.getCurrentPosition(async (pos) => {
                                    const { latitude, longitude } = pos.coords;
                                    try {
                                      const r = await fetch(`https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}&zoom=14&accept-language=en`);
                                      const dd = await r.json();
                                      const a = dd?.address || {};
                                      const detected = a.city || a.town || a.village || a.suburb;
                                      const area = a.suburb || a.neighbourhood || a.village || '';
                                      setEventGeo({ lat: latitude, lng: longitude, area, full_address: dd?.display_name || '', detected_city: detected });
                                      const matched = masterCities.find((c) => c.toLowerCase() === (detected || '').toLowerCase());
                                      if (matched && eventCity !== matched) setEventCity(matched);
                                      setNearbyOnly(true);
                                      toast.success(`Showing venues near ${matched || detected || 'you'}${area ? ' · ' + area : ''}`);
                                    } catch {
                                      toast.error('Could not detect — please pick city manually');
                                    }
                                  }, (err) => {
                                    toast.error(err.code === err.PERMISSION_DENIED ? 'Location permission denied' : 'Could not detect location');
                                  }, { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 });
                                } else {
                                  setNearbyOnly(!nearbyOnly);
                                }
                              }}
                              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded-sm border transition-colors ${
                                nearbyOnly ? 'bg-brand-primary text-sand-50 border-brand-primary' : 'bg-white text-brand-primary border-brand-primary hover:bg-brand-primary/5'
                              }`}
                              data-testid="nearby-toggle-btn">
                              <MapPin size={12} weight={nearbyOnly ? 'fill' : 'regular'} />
                              {nearbyOnly ? 'Showing Nearby Only ✓' : 'Show Nearby Only'}
                            </button>
                            {nearbyOnly && eventGeo?.area && (
                              <span className="text-[10px] text-text-secondary">within 25 km of {eventGeo.area || eventCity}</span>
                            )}
                          </div>
                        )}
                        {pureVegOnly && (
                          <div className="mt-2 p-2 bg-green-50 border border-green-300 rounded text-xs text-green-800 font-medium flex items-center gap-1" data-testid="pure-veg-notice-pro">
                            <span className="inline-block w-3 h-3 bg-green-600 rounded-sm border border-green-700" /> Brahmin Wedding — Showing Pure Veg providers only
                          </div>
                        )}
                      </div>

                      {/* Salary Packages — selectable food timing for Server / Chef / Assistant Chef */}
                      {salaryPackages.length > 0 && (
                        <div className="border border-brand-primary/20 rounded-lg p-3 mb-4 bg-brand-primary/3">
                          <p className="text-xs font-bold text-brand-primary uppercase tracking-wider mb-2">Select Food Timing</p>
                          <div className="space-y-1.5">
                            {salaryPackages.map((pkg, pkgIdx) => {
                              const isSelected = selectedFoodTiming[typeName] === pkg.name;
                              return (
                                <button key={pkgIdx} type="button"
                                  className={`w-full flex items-center justify-between p-2.5 border rounded text-left transition-all ${isSelected ? 'border-green-500 bg-green-100/50' : 'border-[#E8E1D5] hover:border-brand-primary/30'}`}
                                  onClick={() => setSelectedFoodTiming(prev => ({ ...prev, [typeName]: isSelected ? null : pkg.name }))}
                                  data-testid={`timing-${typeName.toLowerCase().replace(/ /g, '-')}-${pkgIdx}`}>
                                  <div className="flex items-center gap-2">
                                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? 'border-green-500 bg-green-500' : 'border-[#E8E1D5]'}`}>
                                      {isSelected && <Check size={8} weight="bold" className="text-white" />}
                                    </div>
                                    <span className="text-sm text-text-primary">{pkg.name}</span>
                                  </div>
                                  <span className="font-bold text-brand-primary">₹{Number(pkg.defaultPrice).toLocaleString('en-IN')}</span>
                                </button>
                              );
                            })}
                          </div>
                          {selectedFoodTiming[typeName] && (
                            <p className="text-[10px] text-green-700 mt-2 font-medium">
                              Selected: {selectedFoodTiming[typeName]} — this price will be used when adding providers
                            </p>
                          )}
                        </div>
                      )}

                      {/* Nearby Venues — real registered venue providers, sorted by
                          distance from the customer when geolocation is available. */}
                      {sidePanel.type === 'venue_provider' && filteredRealVenues.length > 0 && (
                        <div className="border border-green-300 rounded-lg p-3 mb-4 bg-green-50/40" data-testid="verified-venues-block">
                          <p className="text-xs font-bold text-green-700 uppercase tracking-wider mb-2">
                            {eventGeo?.lat ? 'Nearby Venues' : 'Verified Venues'} ({filteredRealVenues.length})
                          </p>
                          <div className="space-y-2">
                            {filteredRealVenues.map((rv) => {
                              const cartId = `ven-real-${typeName}-${rv.id}`;
                              const inCart = cart.find(c => c.id === cartId);
                              const matched = rv.matched;
                              const eventTypeUsed = (eventType === 'engagement' || eventType === 'reception' || eventType === 'wedding') ? 'marriage' : (eventType || 'marriage');
                              const eventLabelMap = { marriage: 'Marriage / Engagement / Reception', birthday: 'Birthday', baby_shower: 'Baby Shower', house_warming: 'House Warming', anniversary: 'Anniversary', corporate: 'Corporate', puberty_ceremony: 'Puberty Ceremony', retirement: 'Retirement', religious: 'Religious', other: 'Other' };
                              const finalPrice = markup(matched.effective_price, 'venue');
                              // Area label = vendor KYC area, else city, else legacy `location`
                              const areaLabel = rv.kyc_area || rv.kyc_base_city || rv.location || '';
                              const openVenueShowcase = () => {
                                const slotSuffix = isWeddingEvent(eventType) ? '' : ` • ${getSlotLabel(timeSlot)}`;
                                const venueImg = (matched.photos && matched.photos[0]) || null;
                                const venueSpecs = [];
                                if (rv.location) venueSpecs.push({ label: 'Location', value: rv.location });
                                if (matched.name) venueSpecs.push({ label: 'Venue type', value: matched.name });
                                if (matched.guest_capacity) venueSpecs.push({ label: 'Capacity', value: `${matched.guest_capacity} guests` });
                                if (matched.sqft) venueSpecs.push({ label: 'Area', value: `${matched.sqft} sqft` });
                                if (matched.has_ac) venueSpecs.push({ label: 'A/C', value: 'Yes' });
                                if (!isWeddingEvent(eventType)) venueSpecs.push({ label: 'Time slot', value: getSlotLabel(timeSlot) });
                                if (matched.hasFood) venueSpecs.push({ label: 'Food available', value: `Veg ₹${matched.foodPriceVeg}/plate` });
                                venueSpecs.push({ label: 'For', value: eventLabelMap[eventTypeUsed] || eventTypeUsed });
                                setShowcaseLightbox({
                                  open: true,
                                  provider: {
                                    ...rv,
                                    photos: matched.photos || rv.photos || [],
                                    rating: rv.rating,
                                    reviews: rv.reviews,
                                    area: rv.kyc_area || rv.kyc_base_city,
                                    verified: true,
                                  },
                                  cartId,
                                  addPayload: {
                                    id: cartId,
                                    name: `${rv.business_name || rv.name} (${matched.name})${slotSuffix}`,
                                    category: 'Venue',
                                    price: finalPrice,
                                    image: venueImg,
                                    details: { specs: venueSpecs },
                                  },
                                  priceText: `₹${finalPrice.toLocaleString('en-IN')}`,
                                  subPriceText: 'venue rental',
                                });
                              };
                              return (
                                <div key={rv.id} className={`border-2 rounded p-3 bg-white transition-all ${inCart ? 'border-green-500 ring-1 ring-green-200' : 'border-[#E8E1D5]'}`} data-testid={`real-venue-${rv.id}`}>
                                  <ProviderShowcaseHeader onOpen={openVenueShowcase}>
                                    {(matched.photos && matched.photos.length > 0) ? (
                                      <img src={matched.photos[0].startsWith('http') ? matched.photos[0] : `${BACKEND_URL}${matched.photos[0]}`} alt={rv.business_name || rv.name} className="w-14 h-14 rounded object-cover border border-[#E8E1D5] flex-shrink-0" onError={(e) => { e.target.style.display = 'none'; }} />
                                    ) : (
                                      <div className="w-14 h-14 rounded border border-[#E8E1D5] bg-sand-100 flex items-center justify-center flex-shrink-0">
                                        <MapPin size={20} className="text-text-secondary" />
                                      </div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-[9px] tracking-wider uppercase font-bold bg-green-600 text-white px-1.5 py-0.5 rounded">VENDOR'S OWN VENUE</span>
                                        {matched.is_marriage_fallback && (
                                          <span className="text-[9px] tracking-wider uppercase font-bold bg-amber-500 text-white px-1.5 py-0.5 rounded" title="Marriage price applies for this event">Marriage Price</span>
                                        )}
                                        {matched.has_ac && (<span className="text-[9px] uppercase font-bold bg-blue-100 text-blue-700 border border-blue-300 px-1.5 py-0.5 rounded">AC</span>)}
                                        {typeof rv.distance_km === 'number' && (
                                          <span className="text-[9px] uppercase font-bold bg-brand-primary/10 text-brand-primary border border-brand-primary/30 px-1.5 py-0.5 rounded" data-testid={`venue-distance-${rv.id}`}>
                                            {rv.distance_km < 1 ? '<1' : rv.distance_km} km away
                                          </span>
                                        )}
                                      </div>
                                      <div className="font-medium text-sm mt-1 truncate">{rv.business_name || rv.name}</div>
                                      {areaLabel && (
                                        <div className="flex items-center gap-1 mt-0.5 text-[11px] font-medium text-text-primary" data-testid={`venue-area-${rv.id}`}>
                                          <MapPin size={11} className="text-brand-primary" weight="fill" />
                                          {areaLabel}{rv.kyc_area && rv.kyc_base_city && rv.kyc_area !== rv.kyc_base_city ? `, ${rv.kyc_base_city}` : ''}
                                        </div>
                                      )}
                                      <div className="flex items-center gap-2 mt-0.5 text-[11px] text-text-secondary">
                                        {matched.guest_capacity && (<span>{matched.guest_capacity} guests</span>)}
                                        {matched.sqft && (<span>{matched.sqft} sqft</span>)}
                                      </div>
                                      <div className="text-[10px] text-text-secondary mt-1">For: <strong className="text-text-primary">{eventLabelMap[eventTypeUsed] || eventTypeUsed}</strong></div>
                                    </div>
                                  </ProviderShowcaseHeader>

                                  {/* Universal media library — venue's portfolio photos + videos */}
                                  {(rv.portfolio_items || []).length > 0 && (
                                    <div className="mt-2" data-testid={`venue-portfolio-strip-${rv.id}`}>
                                      <div className="text-[9px] uppercase tracking-wider font-bold text-text-secondary mb-1">
                                        Gallery · {rv.portfolio_items.length} item{rv.portfolio_items.length === 1 ? '' : 's'} · tap to view
                                      </div>
                                      <div className="flex gap-1 overflow-x-auto pb-1">
                                        {rv.portfolio_items.slice(0, 13).map((it, pi) => (
                                          <button key={it.id || pi} type="button"
                                            onClick={() => setPortfolioLightbox({
                                              open: true, providerId: rv.id,
                                              items: rv.portfolio_items, startIdx: pi,
                                              title: rv.business_name || rv.name,
                                            })}
                                            className="flex-shrink-0 w-16 h-16 border border-[#E8E1D5] bg-black hover:border-brand-primary overflow-hidden relative"
                                            data-testid={`venue-portfolio-thumb-${rv.id}-${pi}`}>
                                            {it.kind === 'video' ? (
                                              <>
                                                {it.poster ? (
                                                  <img src={it.poster.startsWith('http') ? it.poster : `${BACKEND_URL}${it.poster}`} alt="" className="w-full h-full object-cover" />
                                                ) : (
                                                  <video src={it.url.startsWith('http') ? it.url : `${BACKEND_URL}${it.url}`} className="w-full h-full object-cover" preload="metadata" muted />
                                                )}
                                                <span className="absolute inset-0 flex items-center justify-center text-sand-50 drop-shadow text-xs">▶</span>
                                              </>
                                            ) : (
                                              <img src={it.url.startsWith('http') ? it.url : `${BACKEND_URL}${it.url}`} alt="" className="w-full h-full object-cover" />
                                            )}
                                          </button>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#E8E1D5]">
                                    <div>
                                      <div className="font-heading text-lg font-bold text-brand-primary">₹{finalPrice.toLocaleString('en-IN')}</div>
                                      <div className="text-[10px] text-text-secondary">venue rental</div>
                                    </div>
                                    <Button type="button" size="sm"
                                      className={`rounded-none ${inCart ? 'bg-red-500 hover:bg-red-600' : 'bg-brand-primary hover:bg-[#A84A38]'} text-sand-50`}
                                      onClick={() => {
                                        if (inCart) {
                                          setCart(cart.filter(c => c.id !== cartId));
                                        } else {
                                          const slotSuffix = isWeddingEvent(eventType) ? '' : ` • ${getSlotLabel(timeSlot)}`;
                                          const venueImg = (matched.photos && matched.photos[0]) || null;
                                          const venueSpecs = [];
                                          if (rv.location) venueSpecs.push({ label: 'Location', value: rv.location });
                                          if (matched.name) venueSpecs.push({ label: 'Venue type', value: matched.name });
                                          if (matched.guest_capacity) venueSpecs.push({ label: 'Capacity', value: `${matched.guest_capacity} guests` });
                                          if (matched.sqft) venueSpecs.push({ label: 'Area', value: `${matched.sqft} sqft` });
                                          if (matched.has_ac) venueSpecs.push({ label: 'A/C', value: 'Yes' });
                                          if (!isWeddingEvent(eventType)) venueSpecs.push({ label: 'Time slot', value: getSlotLabel(timeSlot) });
                                          if (matched.hasFood) venueSpecs.push({ label: 'Food available', value: `Veg ₹${matched.foodPriceVeg}/plate` });
                                          venueSpecs.push({ label: 'For', value: eventLabelMap[eventTypeUsed] || eventTypeUsed });
                                          setCart([...cart, { id: cartId, name: `${rv.business_name || rv.name} (${matched.name})${slotSuffix}`, category: 'Venue', price: finalPrice, image: venueImg, details: { specs: venueSpecs } }]);
                                        }
                                      }}
                                      data-testid={`real-venue-add-${rv.id}`}>
                                      {inCart ? <><X size={14} className="mr-1" /> Remove</> : <><Plus size={14} className="mr-1" /> Add</>}
                                    </Button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          <p className="text-[10px] text-text-secondary mt-2 italic">
                            Sample providers below are for reference. Verified venues use real vendor pricing.
                          </p>
                        </div>
                      )}

                      {filteredProviders.map((provider) => {
                        const cartId = `${cartPrefix}-${typeName}-${provider.id}`;
                        const inCart = cart.find(c => c.id === cartId);
                        const isExpanded = expandedProviderId === provider.id;
                        // Catering staff pros use matrix/salary pricing — skip showcase lightbox per UX brief.
                        const isCateringStaff = ['Chef', 'Biryani Specialist', 'Assistant Chef', 'Server / Waiter', 'Catering Team'].includes(typeName);
                        const openShowcase = () => {
                          if (isCateringStaff) return;
                          let finalPrice;
                          let nameLabel;
                          let priceText;
                          let subPriceText = '';
                          if (isPerGuest) {
                            finalPrice = provider.price * guestCount;
                            nameLabel = `${provider.name} (${typeName} x${guestCount})`;
                            priceText = `₹${markup(provider.price, groupForSection).toLocaleString('en-IN')}/guest`;
                            subPriceText = `${guestCount} guests = ₹${markup(provider.price * guestCount, groupForSection).toLocaleString('en-IN')}`;
                          } else {
                            const timingPrice = salaryPackages.length > 0 && selectedFoodTiming[typeName]
                              ? getProviderPriceWithTiming(typeName, provider.price) : provider.price;
                            const timingLabel = selectedFoodTiming[typeName] ? ` - ${selectedFoodTiming[typeName]}` : '';
                            finalPrice = timingPrice;
                            nameLabel = `${provider.name} (${typeName}${timingLabel})`;
                            priceText = `₹${markup(timingPrice, groupForSection).toLocaleString('en-IN')}`;
                            if (salaryPackages.length > 0 && selectedFoodTiming[typeName]) {
                              subPriceText = selectedFoodTiming[typeName];
                            }
                          }
                          const specs = [];
                          if (provider.location && provider.location !== '—') specs.push({ label: 'Location', value: provider.location });
                          if (provider.experience && provider.experience !== '—') specs.push({ label: 'Experience', value: provider.experience });
                          if (provider.speciality) specs.push({ label: 'Highlights', value: provider.speciality });
                          if (provider.priest_title) specs.push({ label: 'Title', value: provider.priest_title });
                          if (provider.priest_tradition) specs.push({ label: 'Tradition', value: provider.priest_tradition.charAt(0).toUpperCase() + provider.priest_tradition.slice(1) });
                          if (provider.universal) specs.push({ label: 'Availability', value: 'All weddings & events' });
                          if (provider.verified) specs.push({ label: 'Status', value: 'Verified vendor' });
                          setShowcaseLightbox({
                            open: true,
                            provider,
                            cartId,
                            addPayload: {
                              id: cartId, name: nameLabel, price: finalPrice, category: cartCategory,
                              image: (Array.isArray(provider.photos) && provider.photos[0])
                                || (typeof provider.image === 'string' ? provider.image : null)
                                || null,
                              details: specs.length ? { specs } : null,
                            },
                            priceText,
                            subPriceText,
                          });
                        };
                        return (
                          <ProviderCardFrame key={provider.id}
                            inCart={!!inCart}
                            testId={`provider-${provider.id}`}>
                            <div className="p-4">
                              <ProviderShowcaseHeader
                                onOpen={openShowcase}
                                disabled={isCateringStaff}
                                headerTestId={`provider-card-header-${provider.id}`}
                              >
                                <img src={avatarSet[provider.avatar]} alt={provider.name}
                                  className="w-14 h-14 rounded-full object-cover border-2 border-[#E8E1D5] flex-shrink-0"
                                  onError={(e) => { e.target.style.display = 'none'; }} />
                                <div className="flex-1 min-w-0">
                                  <div className="font-medium text-sm truncate flex items-center gap-2">
                                    {provider.name}
                                    {provider.pure_veg && (
                                      <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 bg-green-100 text-green-700 border border-green-300 font-bold rounded flex-shrink-0" data-testid={`pure-veg-pro-${provider.id}`}>
                                        <span className="inline-block w-2 h-2 bg-green-600 rounded-sm" /> Pure Veg
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-1 mt-0.5">
                                    {Array.from({ length: 5 }).map((_, i) => (
                                      <Star key={i} size={12} weight={i < Math.floor(provider.rating) ? 'fill' : (i < provider.rating ? 'duotone' : 'regular')}
                                        className={i < provider.rating ? 'text-amber-500' : 'text-gray-300'} />
                                    ))}
                                    <span className="text-xs text-text-secondary ml-1">{provider.rating} ({provider.reviews})</span>
                                  </div>
                                  <div className="flex items-center gap-2 mt-1 text-xs text-text-secondary">
                                    <span className="flex items-center gap-0.5"><MapPin size={10} />{provider.area ? `${provider.area}, ${provider.location}` : provider.location}</span>
                                    <span>{provider.experience}</span>
                                  </div>
                                </div>
                                {inCart && (
                                  <div className="bg-green-500 text-white rounded-full p-1 flex-shrink-0">
                                    <Check size={12} weight="bold" />
                                  </div>
                                )}
                              </ProviderShowcaseHeader>

                              {/* Food availability for venues */}
                              {sidePanel.type === 'venue_provider' && provider.hasFood && (
                                <div className="mt-2 flex items-center gap-2 flex-wrap">
                                  <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 bg-green-100 text-green-700 border border-green-300 font-bold rounded" data-testid={`venue-food-badge-${provider.id}`}>
                                    <ForkKnife size={10} /> Food Available
                                  </span>
                                  <span className="text-[10px] text-text-secondary">
                                    Veg ₹{provider.foodPriceVeg}/plate
                                    {provider.foodPriceNV ? ` | Non-Veg ₹${provider.foodPriceNV}/plate` : ''}
                                  </span>
                                </div>
                              )}

                              {/* Decoration Team — clickable package thumbnails open lightbox slideshow. */}
                              {sidePanel.type === 'pro_provider'
                                && sidePanel.data?.proType?.name === 'Decoration Team'
                                && (provider.decor_packages || []).length > 0 && (
                                <div className="mt-3" data-testid={`decor-packages-strip-${provider.id}`}>
                                  <div className="text-[9px] uppercase tracking-wider font-bold text-text-secondary mb-1">
                                    {provider.decor_packages.length} Decor Package{provider.decor_packages.length === 1 ? '' : 's'} · tap to view
                                  </div>
                                  <div className="flex gap-1.5 overflow-x-auto pb-1">
                                    {provider.decor_packages.map((pkg, pi) => (
                                      <button key={pkg.id || pi} type="button"
                                        onClick={() => setDecorLightbox({ open: true, providerId: provider.id, packages: provider.decor_packages, startIdx: pi })}
                                        className="flex-shrink-0 w-20 h-20 border border-[#E8E1D5] bg-sand-100 hover:border-brand-primary overflow-hidden relative group"
                                        data-testid={`decor-package-thumb-${provider.id}-${pi}`}>
                                        {pkg.image ? (
                                          <img src={pkg.image.startsWith('http') ? pkg.image : `${BACKEND_URL}${pkg.image}`}
                                            alt={pkg.name} className="w-full h-full object-cover" />
                                        ) : (
                                          <div className="w-full h-full flex items-center justify-center text-text-secondary text-[9px]">No img</div>
                                        )}
                                        <div className="absolute inset-x-0 bottom-0 bg-black/70 text-sand-50 px-1 py-0.5 text-[9px] font-bold truncate">
                                          ₹{markup(Number(pkg.price) || 0, 'professional').toLocaleString('en-IN')}
                                        </div>
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Pro portfolio strip — images + ≤30s videos, opens unified slideshow. */}
                              {sidePanel.type === 'pro_provider' && (provider.portfolio_items || []).length > 0 && (
                                <div className="mt-3" data-testid={`portfolio-strip-${provider.id}`}>
                                  <div className="text-[9px] uppercase tracking-wider font-bold text-text-secondary mb-1">
                                    Gallery · {provider.portfolio_items.length} item{provider.portfolio_items.length === 1 ? '' : 's'} · tap to view
                                  </div>
                                  <div className="flex gap-1.5 overflow-x-auto pb-1">
                                    {provider.portfolio_items.slice(0, 13).map((it, pi) => (
                                      <button key={it.id || pi} type="button"
                                        onClick={() => setPortfolioLightbox({
                                          open: true, providerId: provider.id,
                                          items: provider.portfolio_items, startIdx: pi,
                                          title: provider.name,
                                        })}
                                        className="flex-shrink-0 w-20 h-20 border border-[#E8E1D5] bg-black hover:border-brand-primary overflow-hidden relative"
                                        data-testid={`portfolio-thumb-${provider.id}-${pi}`}>
                                        {it.kind === 'video' ? (
                                          <>
                                            {it.poster ? (
                                              <img src={it.poster.startsWith('http') ? it.poster : `${BACKEND_URL}${it.poster}`}
                                                alt="" className="w-full h-full object-cover" />
                                            ) : (
                                              <video src={it.url.startsWith('http') ? it.url : `${BACKEND_URL}${it.url}`}
                                                className="w-full h-full object-cover" preload="metadata" muted />
                                            )}
                                            <span className="absolute inset-0 flex items-center justify-center text-sand-50 drop-shadow">▶</span>
                                            <span className="absolute bottom-0 right-0 text-[8px] uppercase tracking-wider font-bold bg-black/70 text-sand-50 px-1">
                                              {it.duration ? `${Math.round(it.duration)}s` : 'vid'}
                                            </span>
                                          </>
                                        ) : (
                                          <img src={it.url.startsWith('http') ? it.url : `${BACKEND_URL}${it.url}`}
                                            alt="" className="w-full h-full object-cover" />
                                        )}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}

                              <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#E8E1D5]">
                                <div>
                                  <div className="font-heading text-xl font-bold text-brand-primary">
                                    {isPerGuest
                                      ? <>₹{markup(provider.price, groupForSection)}/guest</>
                                      : <>₹{markup((salaryPackages.length > 0 && selectedFoodTiming[typeName]
                                        ? getProviderPriceWithTiming(typeName, provider.price)
                                        : provider.price
                                      ), groupForSection).toLocaleString('en-IN')}</>
                                    }
                                  </div>
                                  {isPerGuest && (
                                    <div className="text-xs text-text-secondary">{guestCount} guests = <span className="font-bold text-brand-primary">₹{markup(provider.price * guestCount, groupForSection).toLocaleString('en-IN')}</span></div>
                                  )}
                                  {salaryPackages.length > 0 && selectedFoodTiming[typeName] && (
                                    <div className="text-[10px] text-green-600 font-medium">{selectedFoodTiming[typeName]}</div>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  <Button type="button" size="sm" variant="outline"
                                    className={`rounded-none border-[#E8E1D5] text-text-secondary hover:text-brand-primary hover:border-brand-primary ${isExpanded ? 'bg-brand-primary/5 border-brand-primary text-brand-primary' : ''}`}
                                    onClick={() => setExpandedProviderId(isExpanded ? null : provider.id)}
                                    data-testid={`${isPerGuest ? 'menu' : 'info'}-${provider.id}`}>
                                    {isPerGuest ? <><ForkKnife size={14} className="mr-1" /> Menu</> : <><Info size={14} className="mr-1" /> Info</>}
                                  </Button>
                                  <Button type="button" size="sm"
                                    className={`rounded-none ${inCart ? 'bg-red-500 hover:bg-red-600' : 'bg-brand-primary hover:bg-[#A84A38]'} text-sand-50`}
                                    onClick={() => {
                                      if (inCart) { removeFromCart(cartId); } else {
                                        let finalPrice;
                                        let nameLabel;
                                        if (isPerGuest) {
                                          finalPrice = provider.price * guestCount;
                                          nameLabel = `${provider.name} (${typeName} x${guestCount})`;
                                        } else {
                                          const timingPrice = salaryPackages.length > 0 && selectedFoodTiming[typeName]
                                            ? getProviderPriceWithTiming(typeName, provider.price) : provider.price;
                                          const timingLabel = selectedFoodTiming[typeName] ? ` - ${selectedFoodTiming[typeName]}` : '';
                                          finalPrice = timingPrice;
                                          nameLabel = `${provider.name} (${typeName}${timingLabel})`;
                                        }
                                        // Build a small detail panel for the slideshow. Whatever the
                                        // provider object exposes (location, experience, speciality,
                                        // priest tradition, "all-events" flag for music) gets surfaced.
                                        const specs = [];
                                        if (provider.location && provider.location !== '—') specs.push({ label: 'Location', value: provider.location });
                                        if (provider.experience && provider.experience !== '—') specs.push({ label: 'Experience', value: provider.experience });
                                        if (provider.speciality) specs.push({ label: 'Highlights', value: provider.speciality });
                                        if (provider.priest_title) specs.push({ label: 'Title', value: provider.priest_title });
                                        if (provider.priest_tradition) specs.push({ label: 'Tradition', value: provider.priest_tradition.charAt(0).toUpperCase() + provider.priest_tradition.slice(1) });
                                        if (provider.universal) specs.push({ label: 'Availability', value: 'All weddings & events' });
                                        if (provider.verified) specs.push({ label: 'Status', value: 'Verified vendor' });
                                        addToCart({
                                          id: cartId, name: nameLabel, price: finalPrice, category: cartCategory,
                                          image: (Array.isArray(provider.photos) && provider.photos[0])
                                            || (typeof provider.image === 'string' ? provider.image : null)
                                            || null,
                                          details: specs.length ? { specs } : null,
                                        });
                                      }
                                    }}
                                    data-testid={`add-${provider.id}`}>
                                    {inCart ? <><Minus size={14} className="mr-1" /> Remove</> : <><Plus size={14} className="mr-1" /> Add</>}
                                  </Button>
                                </div>
                              </div>
                            </div>

                            {isExpanded && (
                              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
                                className="bg-gray-50 border-t border-[#E8E1D5] px-4 py-3">
                                {/* Gift Packages for Return Gift providers */}
                                {isPerGuest && provider.giftPackages?.length > 0 ? (
                                  <div>
                                    <p className="text-xs font-bold text-text-secondary uppercase tracking-wider mb-2">Gift Packages — Choose One</p>
                                    <div className="space-y-2">
                                      {provider.giftPackages.map((pkg, pkgIdx) => {
                                        const isSelected = provider.price === pkg.price;
                                        const pkgCartId = `pro-prov-${typeName}-${provider.id}`;
                                        const pkgInCart = cart.find(c => c.id === pkgCartId && c.name?.includes(pkg.name));
                                        return (
                                          <div key={pkgIdx}
                                            className={`border rounded overflow-hidden transition-all cursor-pointer ${pkgInCart ? 'border-green-500 bg-green-50/30' : 'border-[#E8E1D5] hover:border-brand-primary/40 bg-white'}`}
                                            onClick={() => {
                                              const finalPrice = pkg.price * guestCount;
                                              const nameLabel = `${provider.name} — ${pkg.name} (x${guestCount})`;
                                              const existingIdx = cart.findIndex(c => c.id === pkgCartId);
                                              if (existingIdx >= 0) {
                                                if (pkgInCart) {
                                                  removeFromCart(pkgCartId);
                                                } else {
                                                  setCart(cart.map(c => c.id === pkgCartId ? { ...c, name: nameLabel, price: finalPrice } : c));
                                                }
                                              } else {
                                                addToCart({ id: pkgCartId, name: nameLabel, price: finalPrice, category: cartCategory });
                                              }
                                            }}
                                            data-testid={`gift-pkg-${provider.id}-${pkgIdx}`}>
                                            {/* Package Image */}
                                            {pkg.image && (
                                              <div className="w-full h-32 overflow-hidden bg-gray-100">
                                                <img src={pkg.image} alt={pkg.name} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                                              </div>
                                            )}
                                            <div className="p-3">
                                              <div className="flex items-center justify-between mb-1.5">
                                                <span className="font-medium text-sm">{pkg.name}</span>
                                                <div className="text-right">
                                                  <span className="text-brand-primary font-bold">₹{pkg.price}/guest</span>
                                                  <span className="text-[10px] text-text-secondary block">{guestCount} guests = ₹{(pkg.price * guestCount).toLocaleString('en-IN')}</span>
                                                </div>
                                              </div>
                                              <div className="flex flex-wrap gap-1">
                                                {pkg.items.map((item, iIdx) => (
                                                  <span key={iIdx} className="text-[10px] bg-sand-50 border border-[#E8E1D5] px-1.5 py-0.5 rounded">{item}</span>
                                                ))}
                                              </div>
                                              {pkgInCart && (
                                                <div className="mt-1.5 text-xs text-green-600 font-bold flex items-center gap-1"><Check size={12} weight="bold" /> Selected</div>
                                              )}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                ) : (
                                  <div>
                                    <p className="text-xs font-bold text-text-secondary uppercase tracking-wider mb-2">Speciality</p>
                                    <p className="text-sm text-text-primary">{provider.speciality}</p>
                                    <div className="flex flex-wrap gap-1.5 mt-2">
                                      {provider.speciality.split(', ').map((s, si) => (
                                        <span key={si} className="text-xs bg-white border border-[#E8E1D5] px-2 py-1 rounded font-medium">{s}</span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </motion.div>
                            )}
                          </ProviderCardFrame>
                        );
                      })}

                      {providers.length === 0 && (
                        <div className="text-center py-6 text-text-secondary">
                          <p className="text-sm">No providers available yet for {typeName}</p>
                          <p className="text-xs mt-1">Check back soon!</p>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>

              {/* Panel Footer */}
              <div className="sticky bottom-0 bg-white border-t border-[#E8E1D5] px-5 py-3">
                <Button onClick={closeSidePanel} className="w-full bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none" data-testid="side-panel-done">
                  Done
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Banana Leaf Menu Preview Modal */}
      <AnimatePresence>
        {bananaLeafPreview && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
            onClick={() => setBananaLeafPreview(null)}
            data-testid="banana-leaf-modal"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-amber-100 to-yellow-100 px-5 py-3 flex items-center justify-between border-b border-amber-200">
                <div>
                  <div className="text-xs tracking-[0.15em] uppercase font-bold text-amber-800">{bananaLeafPreview.mealLabel} Preview</div>
                  <div className="font-heading text-lg text-text-primary">{bananaLeafPreview.providerName}</div>
                </div>
                <button type="button"
                  className="w-8 h-8 rounded-full bg-white/60 hover:bg-white border border-amber-300 flex items-center justify-center"
                  onClick={() => setBananaLeafPreview(null)}
                  data-testid="banana-leaf-close">
                  <X size={16} />
                </button>
              </div>

              {/* Banana Leaf */}
              <div className="relative px-5 py-8 bg-amber-50/40 overflow-auto" style={{ minHeight: '380px', maxHeight: 'calc(90vh - 140px)' }}>
                {/* Wood-grain background hint */}
                <div className="absolute inset-0 pointer-events-none opacity-[0.03]"
                  style={{ backgroundImage: 'repeating-linear-gradient(90deg, #92400e 0px, #92400e 2px, transparent 2px, transparent 12px)' }} />
                {/* Banana Leaf SVG */}
                <div className="relative mx-auto" style={{ maxWidth: '600px' }}>
                  <svg viewBox="0 0 600 320" className="w-full h-auto drop-shadow-lg">
                    <defs>
                      <linearGradient id="leafGrad" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="#16a34a" />
                        <stop offset="50%" stopColor="#15803d" />
                        <stop offset="100%" stopColor="#166534" />
                      </linearGradient>
                      <linearGradient id="leafGradInner" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#bbf7d0" />
                        <stop offset="100%" stopColor="#86efac" />
                      </linearGradient>
                    </defs>
                    {/* Outer leaf shape (long oval with pointed ends) */}
                    <path d="M 30,160 Q 30,40 300,30 Q 570,40 570,160 Q 570,280 300,290 Q 30,280 30,160 Z" fill="url(#leafGrad)" />
                    {/* Inner pale area */}
                    <path d="M 50,160 Q 50,55 300,46 Q 550,55 550,160 Q 550,265 300,274 Q 50,265 50,160 Z" fill="url(#leafGradInner)" />
                    {/* Center vein */}
                    <line x1="50" y1="160" x2="550" y2="160" stroke="#15803d" strokeWidth="1.5" opacity="0.6" />
                    {/* Side veins */}
                    {Array.from({ length: 14 }).map((_, i) => {
                      const x = 80 + i * 35;
                      return (
                        <g key={i} opacity="0.35">
                          <line x1={x} y1="160" x2={x - 18} y2="70" stroke="#16a34a" strokeWidth="1" />
                          <line x1={x} y1="160" x2={x - 18} y2="250" stroke="#16a34a" strokeWidth="1" />
                        </g>
                      );
                    })}
                  </svg>

                  {/* Items overlay */}
                  <div className="absolute inset-0 flex flex-wrap items-center justify-center gap-2 px-12 py-8">
                    {bananaLeafPreview.items.map((item, idx) => (
                      <span key={idx}
                        className="inline-flex items-center gap-1 bg-white/95 backdrop-blur-sm border-2 border-amber-200 text-text-primary text-xs sm:text-sm font-medium px-3 py-1.5 rounded-full shadow-sm"
                        data-testid={`banana-leaf-item-${idx}`}>
                        <span className="text-base">{getFoodEmoji(item)}</span>
                        {item}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="text-center mt-4 text-xs text-text-secondary">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded font-bold ${bananaLeafPreview.isVeg ? 'bg-green-100 text-green-700 border border-green-300' : 'bg-red-100 text-red-700 border border-red-300'}`}>
                    <span className={`inline-block w-2 h-2 rounded-sm ${bananaLeafPreview.isVeg ? 'bg-green-600' : 'bg-red-600'}`} />
                    {bananaLeafPreview.isVeg ? 'Veg' : 'Non-Veg'}
                  </span>
                  <span className="ml-2">{bananaLeafPreview.items.length} item{bananaLeafPreview.items.length !== 1 ? 's' : ''} • served traditional style</span>
                </div>
              </div>

              {/* Footer */}
              <div className="px-5 py-3 border-t border-[#E8E1D5] bg-sand-50">
                <Button onClick={() => setBananaLeafPreview(null)}
                  className="w-full bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none"
                  data-testid="banana-leaf-done">
                  Looks delicious! Close
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Lightbox for Custom Wedding Package galleries */}
      <Lightbox
        open={lightbox.open}
        photos={lightbox.photos}
        initialIndex={lightbox.initialIndex}
        title={lightbox.title}
        onClose={() => setLightbox({ open: false, photos: [], initialIndex: 0, title: '' })}
      />

      {/* Decoration Team — pro decor packages slideshow */}
      <DecorPackagesLightbox
        open={decorLightbox.open}
        packages={decorLightbox.packages}
        startIdx={decorLightbox.startIdx}
        onClose={() => setDecorLightbox({ open: false, providerId: null, packages: [], startIdx: 0 })}
        formatPrice={(price) => `₹${markup(Number(price) || 0, 'professional').toLocaleString('en-IN')}`}
      />

      {/* Pro / entertainment-pro portfolio gallery (images + ≤30s videos) */}
      <PortfolioLightbox
        open={portfolioLightbox.open}
        items={portfolioLightbox.items}
        startIdx={portfolioLightbox.startIdx}
        title={portfolioLightbox.title}
        onClose={() => setPortfolioLightbox({ open: false, providerId: null, items: [], startIdx: 0, title: '' })}
      />

      {/* Full-screen provider showcase — opens when customer taps a vendor / pro / venue card */}
      <CatererMealLeafPopup
        open={mealLeafPopup.open}
        caterer={mealLeafPopup.caterer}
        mealKey={mealLeafPopup.mealKey}
        mealLabel={mealLeafPopup.mealLabel}
        pureVeg={mealLeafPopup.pureVeg}
        initialGuests={
          mealLeafPopup.open
            ? (catererMealConfig[mealLeafPopup.providerId]?.[mealLeafPopup.mealKey]?.guests || guestCount)
            : guestCount
        }
        initialIsNonVeg={
          mealLeafPopup.open
            ? !!catererMealConfig[mealLeafPopup.providerId]?.[mealLeafPopup.mealKey]?.isNonVeg
            : false
        }
        initialRemovedItems={
          mealLeafPopup.open
            ? (catererMealConfig[mealLeafPopup.providerId]?.[mealLeafPopup.mealKey]?.removedItems || [])
            : []
        }
        onClose={() => setMealLeafPopup({ open: false, providerId: null, mealKey: null, mealLabel: '', caterer: null, pureVeg: false })}
        onAdd={({ guests, isNonVeg, removedItems }) => {
          const pid = mealLeafPopup.providerId;
          const mk = mealLeafPopup.mealKey;
          if (!pid || !mk) return;
          // Patch the existing catererMealConfig for this meal — enable it,
          // set guest count, veg/nv, and persist menu picks. The "Add" button
          // on the parent caterer card already handles the actual addToCart()
          // call, so here we just configure the meal state.
          setCatererMealConfig((prev) => {
            const next = { ...prev };
            const provCfg = { ...(next[pid] || {}) };
            provCfg[mk] = {
              ...(provCfg[mk] || {}),
              enabled: true,
              guests: parseInt(guests || guestCount, 10) || guestCount,
              isNonVeg: !!isNonVeg,
              removedItems: Array.isArray(removedItems) ? removedItems : [],
            };
            next[pid] = provCfg;
            return next;
          });
          toast.success(`${mealLeafPopup.mealLabel} added for ${guests} guests`);
          setMealLeafPopup({ open: false, providerId: null, mealKey: null, mealLabel: '', caterer: null, pureVeg: false });
        }}
      />

      {/* Full-screen product popup — flowers / pooja / return-gift / muhurtham */}
      {productPopup && (
        <div className="fixed inset-0 z-[80] bg-black/85 flex items-end sm:items-center justify-center p-0 sm:p-6"
          onClick={() => setProductPopup(null)}
          data-testid="product-popup">
          <div className="bg-white max-w-md w-full sm:rounded-lg overflow-hidden flex flex-col max-h-[95vh] sm:max-h-[85vh]"
            onClick={(e) => e.stopPropagation()}>
            <div className="relative bg-stone-100 aspect-square">
              <img
                src={productPopup.item.image?.startsWith('http') ? productPopup.item.image : `${BACKEND_URL}${productPopup.item.image}`}
                alt={productPopup.item.name}
                className="w-full h-full object-contain"
                data-testid="product-popup-image"
              />
              <button onClick={() => setProductPopup(null)}
                className="absolute top-3 right-3 bg-white/90 hover:bg-white p-2 rounded-full shadow"
                aria-label="Close"
                data-testid="product-popup-close">
                <X size={18} />
              </button>
            </div>
            <div className="p-4 space-y-2 overflow-y-auto">
              <div className="text-xs uppercase tracking-wider font-bold text-text-secondary">
                {productPopup.shop.business_name} · {productPopup.shop.city}
              </div>
              <h3 className="font-heading text-2xl" data-testid="product-popup-name">{productPopup.item.name}</h3>
              {productPopup.item.category_tag && (
                <span className={`inline-block text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${productPopup.accent === 'pink' ? 'bg-pink-100 text-pink-700' : 'bg-emerald-100 text-emerald-700'}`}>
                  {productPopup.item.category_tag}
                </span>
              )}
              <div className="text-2xl font-bold text-brand-primary" data-testid="product-popup-price">
                ₹{Number(productPopup.finalPrice).toLocaleString('en-IN')}
                {productPopup.item.unit && (
                  <span className="text-xs font-normal text-text-secondary ml-2">({productPopup.item.unit})</span>
                )}
              </div>
              {productPopup.item.description && (
                <p className="text-sm text-text-secondary leading-relaxed">{productPopup.item.description}</p>
              )}
              {(() => {
                const inCart = cart.find(c => c.id === productPopup.cartId);
                if (inCart) {
                  return (
                    <Button onClick={() => removeFromCart(productPopup.cartId)}
                      variant="outline"
                      className="w-full rounded-none border-emerald-500 text-emerald-700"
                      data-testid="product-popup-remove">
                      ✓ Added — Remove from estimate
                    </Button>
                  );
                }
                return (
                  <Button
                    onClick={() => {
                      addToCart({
                        id: productPopup.cartId,
                        name: productPopup.item.name,
                        price: productPopup.finalPrice,
                        category: productPopup.category,
                        qty: 1,
                        shop_id: productPopup.shop.id,
                        vendor_id: productPopup.shop.id,
                      });
                      setProductPopup(null);
                    }}
                    className="w-full rounded-none bg-brand-primary hover:bg-[#A84A38] text-sand-50"
                    data-testid="product-popup-add">
                    <Plus size={14} className="mr-1" /> Add to estimate
                  </Button>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      <ProviderShowcaseLightbox
        open={showcaseLightbox.open}
        provider={showcaseLightbox.provider}
        priceText={showcaseLightbox.priceText}
        subPriceText={showcaseLightbox.subPriceText}
        inCart={!!(showcaseLightbox.cartId && cart.find(c => c.id === showcaseLightbox.cartId))}
        onToggleCart={() => {
          const sl = showcaseLightbox;
          if (!sl.cartId) return;
          const exists = cart.find(c => c.id === sl.cartId);
          if (exists) {
            if (typeof sl.onRemove === 'function') {
              sl.onRemove();
            } else if (typeof removeFromCart === 'function') {
              removeFromCart(sl.cartId);
            } else {
              setCart(cart.filter(c => c.id !== sl.cartId));
            }
          } else if (typeof sl.onAdd === 'function') {
            sl.onAdd();
          } else if (sl.addPayload && typeof addToCart === 'function') {
            addToCart(sl.addPayload);
          } else if (sl.addPayload) {
            setCart([...cart, sl.addPayload]);
          }
        }}
        onClose={() => setShowcaseLightbox({ open: false, provider: null, cartId: null, addPayload: null, priceText: '', subPriceText: '' })}
      />

      {/* Floating Segment Menu — only on results page */}
      {step === 2 && pricingData && (() => {
        const isMarriage = ['marriage', 'wedding', 'engagement'].includes(eventType);
        const isBirthday = eventType === 'birthday';
        const segs = [];
        // Catering
        if (pricingData.caterer && isSegmentEnabled('caterer')) segs.push({ id: 'pricing-caterer', label: t('pricing.catering') || 'Catering', icon: SEGMENT_ICONS.caterer });
        // Decoration (general)
        if (pricingData.decorator && isSegmentEnabled('decorator') && !isBirthday) segs.push({ id: 'pricing-decorator', label: t('pricing.decoration') || 'Decoration', icon: SEGMENT_ICONS.decorator });
        // Custom Decor (marriage only)
        if (isMarriage && isSegmentEnabled('custom_decor_packages') && customDecorPackages.length > 0) {
          segs.push({ id: 'pricing-custom-decor', label: 'Simple Wedding Decoration', icon: SEGMENT_ICONS.custom_decor });
        }
        // Birthday Decoration
        if (isBirthday && isSegmentEnabled('birthday_decoration')) segs.push({ id: 'pricing-birthday-deco', label: 'Birthday Decoration', icon: SEGMENT_ICONS.birthday_deco });
        if (isBirthday && isSegmentEnabled('cake')) segs.push({ id: 'pricing-cake', label: 'Cakes', icon: SEGMENT_ICONS.cake });
        // Flowers
        if (pricingData.flowers && isSegmentEnabled('flowers')) segs.push({ id: 'pricing-flowers', label: t('pricing.flowers') || 'Flowers', icon: SEGMENT_ICONS.flowers });
        // Photography
        if (pricingData.photographer && isSegmentEnabled('photographer')) segs.push({ id: 'pricing-photographer', label: t('pricing.photography') || 'Photography', icon: SEGMENT_ICONS.photographer });
        // Venue (marriage)
        if (pricingData.venue && isSegmentEnabled('venue') && isMarriage) segs.push({ id: 'pricing-venue', label: t('pricing.venue') || 'Venue', icon: SEGMENT_ICONS.venue });
        // Professionals
        if (pricingData.professional && isSegmentEnabled('professional')) segs.push({ id: 'pricing-professional', label: 'Professionals', icon: SEGMENT_ICONS.professional });
        // Entertainment Pros
        if (isSegmentEnabled('entertainment_pro')) segs.push({ id: 'pricing-entertainment-pro', label: 'Entertainment', icon: SEGMENT_ICONS.entertainment_pro });
        // DJ / Music
        if (pricingData.dj && isSegmentEnabled('dj')) segs.push({ id: 'pricing-dj', label: isMarriage ? 'Celebrate With Music' : 'DJ & Music', icon: SEGMENT_ICONS.dj });
        // Equipment
        if (pricingData.equipment && isSegmentEnabled('equipment')) segs.push({ id: 'pricing-equipment', label: 'Equipment', icon: SEGMENT_ICONS.equipment });
        // Cart (always last so user can jump to total)
        segs.push({ id: 'pricing-cart', label: 'Your Estimate', icon: SEGMENT_ICONS.cart });
        return <FloatingSegmentMenu segments={segs} testId="pricing-menu" />;
      })()}
    </div>
  );
};

export default InstantPricingPage;
